import QuotationExclusionEditor, { type QuotationExclusionOption } from './QuotationExclusionEditor';
import QuotationCcInput from './QuotationCcInput';
import QuotationScopeItemInput, { type QuotationScopeItemOption } from './QuotationScopeItemInput';
import { ADDITIONAL_SCOPE_SECTION_KEY, withQuotationAdditionalScope } from './quotationAdditionalScope';
import { buildQuotationCcEmails, parseEmailListInput } from './quotationEmailRecipients';
import React, { useEffect, useRef, useState } from 'react';
import { GripVertical, Trash2, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/Auth/AuthContext';
import { outlookQuotationOwnerIdentity, type OutlookQuotationDraft } from '@/services/outlookQuotationDraftService';
import ClientEmailAutocompleteInput from '@/components/ClientEmailAutocompleteInput';
import GoogleAddressInput, { type GoogleAddressSuggestion } from '@/components/maps/GoogleAddressInput';
import { getCompanyPaymentProfile } from '@/constants/companyProfiles';
import { resolveQuotationSignatoryCompany } from '@/constants/quotationSignatories';
import {
  DEFAULT_QUOTATION_HOURLY_RATE,
  DEFAULT_QUOTATION_INSPECTION_FEE,
} from '@/constants/quotationDefaults';
import {
  DEFAULT_QUOTATION_EXCLUSION_OPTIONS,
  dedupeQuotationExclusions,
  normalizeQuotationExclusionLabel,
  orderQuotationExclusions,
} from '@/constants/quotationExclusions';
import {
  DEFAULT_QUOTATION_TEMPLATE_DEFINITION,
  QUOTATION_PROJECT_TYPE_OPTIONS,
  getQuotationScopeCatalogueSelectionFromTemplate,
  type QuotationScopeCatalogueSection,
  type QuotationTemplateDefinition,
} from '@/constants/quotationOptions';
import {
  searchSuburbOptions,
  searchSuburbOptionsByPostCode,
  type SuburbLookupRow,
} from '@/services/suburbLookup';
import { formatPhoneForDisplay, normalizePhoneForStorage } from '@/utils/phone';
import { userEmailToUserInitial } from '@/utils/internalOwnerLabel';
import { getQuotationDisplayAmount, getQuotationStructuralFeeTotal } from '@/utils/quotationFees';
import { formatUserFacingError } from '@/utils/userFacingErrors';
import { useIsNarrowScreen } from '@/hooks/useMediaQuery';
import type { QuotationPdfData, QuotationPaymentStage } from '@/templates/pdf/QuotationTemplate';
import { canRequestQuotationOtpApproval, resolveWorkflowQuotationIdentity, type WorkflowQuotationIdentity } from './quotationWorkflowIdentity';
import './workflowDataGridFocus.css';

type WorkflowResult = {
  success?: boolean;
  [key: string]: any;
} | null;

type QuotationWorkflowPrefillState = {
  prefill?: Partial<{
    quoteDate: string;
    quoteValidityDays: number;
    quotationNumber: string;
    entity: string;
    clientName: string;
    clientCompany: string;
    clientAddress: string;
    clientMobile: string;
    clientEmail: string;
    projectAddress: string;
    suburb: string;
    postCode: string;
    council: string;
    projectScope: string;
    projectType: string;
    projectDescription: string;
    successMessage: string;
    includeStructuralScope: boolean;
    includeCivilScope: boolean;
  }>;
  outlookQuotationDraft?: { ownerIdentity: string; source: OutlookQuotationDraft['source'] };
  workflowQuotationDraft?: { ownerIdentity: string; draftId: string };
  amendQuotation?: {
    quotationId?: number | string | null;
    quotationNumber?: string | null;
  };
};

type WorkflowUiNotice = {
  message: string;
  code?: string;
  correlationId?: string;
};

type InvoiceWorkflowPrefillItem = {
  categoryId?: string;
  categoryName?: string;
  description: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
};

type InvoiceWorkflowPrefillState = {
  mode: 'create';
  prefill: {
    invoiceNumber?: string;
    projectId?: string;
    projectNumber?: string;
    entity?: string;
    clientName?: string;
    clientCompany?: string;
    clientAddress?: string;
    clientMobile?: string;
    clientEmail?: string;
    projectAddress?: string;
    notes?: string;
    items?: InvoiceWorkflowPrefillItem[];
    successMessage?: string;
  };
};

type PendingQuotationCandidate = {
  QuotationId?: number | string | null;
  QuotationNumber?: string | null;
  Date?: string | null;
  IssueDate?: string | null;
  Address?: string | null;
  Suburb?: string | null;
  PostCode?: number | string | null;
  Council?: string | null;
  Entity?: string | null;
  StructuralFee?: number | string | null;
  CivilFee?: number | string | null;
  SoilReportFee?: number | string | null;
  HourlyRate?: number | string | null;
  InspectionFee?: number | string | null;
  QuoteValidityDays?: number | string | null;
  QuotationAmount?: number | string | null;
  ProjectType?: string | null;
  ProjectDescription?: string | null;
  ProjectScope?: string | null;
  Acceptance?: string | null;
  Status?: string | null;
  Client?: string | null;
  ClientName?: string | null;
  ClientCompany?: string | null;
  CompanyName?: string | null;
  ABN?: string | null;
  ClientAddress?: string | null;
  ClientMobile?: string | null;
  ClientEmail?: string | null;
  ScopeFootnoteS?: string | null;
  ScopeFootnoteC?: string | null;
  ExclusionsJson?: string[] | string | null;
  SenderDisplayName?: string | null;
  SenderAddress?: string | null;
  SenderMobile?: string | null;
  SenderEmail?: string | null;
  SenderPayId?: string | null;
  SenderBsb?: string | null;
  SenderAccountNumber?: string | null;
  SenderAccountName?: string | null;
  BroughtBy?: string | null;
  CertifiedBy?: string | null;
  StructuralPaymentScheduleJson?: unknown;
  StructuralPaymentMode?: string | null;
  CivilPaymentScheduleJson?: unknown;
  CivilPaymentMode?: string | null;
  StructuralScopeGroupKey?: string | null;
  StructuralScopeGroupLabel?: string | null;
  StructuralScopeItemsJson?: unknown;
  CivilScopeGroupKey?: string | null;
  CivilScopeGroupLabel?: string | null;
  CivilScopeItemsJson?: unknown;
  GeneralScopeItemsJson?: unknown;
  IncludeGeneralScope?: boolean | number | string | null;
};

type ClientLookupCandidate = {
  ClientId?: number | string | null;
  Name?: string | null;
  Company?: string | null;
  Email?: string | null;
  Phone?: string | null;
  Address?: string | null;
  Abn?: string | null;
};

type QuotationNumberStatus = {
  requestedQuotationNumber?: string | null;
  isDuplicate?: boolean;
  isAvailable?: boolean;
  recommendedQuotationNumber?: string | null;
  nextAvailableQuotationNumber?: string | null;
  existingQuotationFolder?: {
    folderName: string;
    folderPath: string;
    driveId?: string;
    itemId?: string;
  } | null;
  existingFolderLookupStatus?: 'configured' | 'not_configured' | 'error' | null;
  message?: string;
};

type LoadedPendingQuotationDraft = {
  quotationId: string;
  quotationNumber: string;
};

type QuotationSigningLinkResult = {
  sessionId: number;
  quotationId: number;
  quotationNumber: string | null;
  status: 'active';
  signUrl: string;
  issuedAt: string;
  expiresAt: string;
};

type QuotationEmailPreviewResult = {
  quotationId?: number | string | null;
  quotationNumber?: string | null;
  recipientEmail?: string | null;
  recipientEmails?: string[] | null;
  ccEmails?: string[] | null;
  senderEmail?: string | null;
  subject?: string | null;
  html?: string | null;
  signUrl?: string | null;
  pdfFileName?: string | null;
  filedSignedPdf?: FiledSignedQuotationPdf | null;
};

type QuotationEmailComposerDraft = {
  subject: string;
  html: string;
};

type DocumentApprovalState = {
  approvalId: string;
  status: string;
  approverEmail?: string | null;
  expiresAtLabel?: string | null;
  approvedAt?: string | null;
  pdfBase64: string;
  pdfFileName: string;
};

type FiledSignedQuotationPdf = {
  fileId: string;
  fileName?: string | null;
  contentType?: string | null;
  contentPath?: string | null;
};

type WorkflowEngineerOption = {
  userId?: number | null;
  email: string;
  displayName?: string | null;
  company?: string | null;
  role?: string | null;
  jobTitle?: string | null;
  sourceTable?: string | null;
};

type GooglePlaceAddressDetails = {
  placeId?: string | null;
  formattedAddress?: string | null;
  streetAddress?: string | null;
  suburb?: string | null;
  postCode?: string | null;
  state?: string | null;
  country?: string | null;
};

type ScopeDisciplineKey = 'structural' | 'civil';

type ScopeDetailLibraryItem = {
  ScopeDetailItemId?: number | null;
  StructuralCivil?: string | null;
  DetailGroupKey: string;
  DetailGroupLabel: string;
  ScopeDetailItem: string;
  SortOrder?: number | null;
  IsActive?: boolean;
  IsSystem?: boolean;
  Source?: string;
};

type ScopeDetailGroup = {
  discipline: ScopeDisciplineKey;
  groupKey: string;
  groupLabel: string;
  items: ScopeDetailLibraryItem[];
};

type ScopeDetailLibrary = {
  groups: ScopeDetailGroup[];
  source?: string;
};

type PaymentStageReferenceOption = {
  QuotationPaymentStageRefOptionId?: number | null;
  Discipline: 'Structural' | 'Civil';
  OptionLabel: string;
  SortOrder?: number | null;
};

type PaymentStageReferenceLibrary = {
  disciplines: Array<{
    discipline: 'Structural' | 'Civil';
    options: PaymentStageReferenceOption[];
  }>;
  source?: string;
};

const DEFAULT_PAYMENT_STAGE_REFERENCE_LIBRARY: PaymentStageReferenceLibrary = {
  source: 'fallback',
  disciplines: [
    {
      discipline: 'Structural',
      options: [
        'before commencement of structural engineering service',
        'before submission of concept design',
        'before submission of preliminary structural design',
        'before submission of detailed structural design',
        'before submission of final package for building permit',
      ].map((OptionLabel, index) => ({
        QuotationPaymentStageRefOptionId: null,
        Discipline: 'Structural' as const,
        OptionLabel,
        SortOrder: (index + 1) * 10,
      })),
    },
    {
      discipline: 'Civil',
      options: [
        'before commencement of civil drainage design',
        'before council submission of first civil drainage design',
        'before submission of preliminary civil drainage design',
        'before submission of detailed civil drainage design',
        'before submission of final civil endorsement',
      ].map((OptionLabel, index) => ({
        QuotationPaymentStageRefOptionId: null,
        Discipline: 'Civil' as const,
        OptionLabel,
        SortOrder: (index + 1) * 10,
      })),
    },
  ],
};

type PaymentStageKey = 'deposit' | 'stage2' | 'stage3' | 'final';

type PaymentScheduleStage = {
  key: string;
  stageKey?: PaymentStageKey;
  componentKey?: 'structural' | 'civil';
  componentLabel?: string;
  label: string;
  requestedPercentage?: number;
  percentage: number;
  amount: number;
  reference: string;
  description?: string;
  triggerOptionId?: number | null;
  triggerLabel?: string;
};

type PaymentComponentSchedule = {
  key: 'structural' | 'civil';
  label: string;
  fee: number;
  depositPercentage: number;
  stage2Percentage: number;
  stages: PaymentScheduleStage[];
  manualOverride?: boolean;
};

type PaymentStageDraft = {
  stageKey: PaymentStageKey;
  label: string;
  amount: string;
  triggerOptionId?: number | null;
  triggerLabel?: string;
};

type GeneralScopeItemDraft = {
  key: string;
  sectionKey: string;
  sectionTitle: string;
  text: string;
  included: boolean;
};

type ScopeItemDragState = {
  discipline: ScopeDisciplineKey;
  fromIndex: number;
  overIndex: number | null;
} | null;

type PaymentSchedule = {
  quotationNumber: string;
  fees: {
    structuralFee: number;
    civilFee: number;
    soilReportFee: number;
  };
  total: number;
  scheduledTotal?: number;
  componentSchedules?: PaymentComponentSchedule[];
  stages: PaymentScheduleStage[];
  splitRule?: string;
};

const panelStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(247,250,253,0.99) 100%)',
  borderRadius: 20,
  border: '1px solid rgba(144,163,195,0.2)',
  boxShadow: '0 18px 48px rgba(15,23,42,0.08)',
  padding: 18,
  marginBottom: 18,
  boxSizing: 'border-box',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  minWidth: 0,
  padding: '10px 12px',
  minHeight: 44,
  borderRadius: 10,
  border: '1px solid rgba(144,163,195,0.3)',
  fontSize: 14,
  lineHeight: 1.4,
  boxSizing: 'border-box',
  background: '#ffffff',
  color: '#10243f',
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: '#34579e',
  marginBottom: 6,
  display: 'block',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
};

const clientDetailsGridStyle: React.CSSProperties = {
  ...gridStyle,
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
};

const sectionStyle: React.CSSProperties = {
  border: '1px solid rgba(144,163,195,0.22)',
  borderRadius: 16,
  padding: 16,
  background: 'linear-gradient(180deg, rgba(244,248,253,0.98) 0%, rgba(255,255,255,0.98) 100%)',
  boxSizing: 'border-box',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 15,
  fontWeight: 700,
  color: '#10243f',
};

const sectionCaptionStyle: React.CSSProperties = {
  marginTop: 4,
  marginBottom: 14,
  fontSize: 12,
  color: '#5d6d84',
};

const currencyFieldWrapStyle: React.CSSProperties = {
  display: 'grid',
  gap: 0,
};

const currencyPrefixStyle: React.CSSProperties = {
  flex: '0 0 auto',
  padding: '0 0 0 12px',
  color: '#5d6d84',
  fontSize: 14,
  fontWeight: 700,
};

const currencyInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  alignSelf: 'stretch',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  padding: '10px 12px 10px 10px',
  fontSize: 14,
  lineHeight: 1.4,
  boxSizing: 'border-box',
};

const currencyInputShellStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minHeight: 44,
  borderRadius: 10,
  border: '1px solid rgba(144,163,195,0.3)',
  background: '#ffffff',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  });
}

function openQuotationPdfBlob(blob: Blob, previewWindow?: Window | null, title = 'Quotation PDF Review') {
  const objectUrl = URL.createObjectURL(blob);
  const targetWindow = previewWindow && !previewWindow.closed ? previewWindow : window.open('', '_blank');

  if (targetWindow && !targetWindow.closed) {
    try {
      targetWindow.document.title = title;
      targetWindow.document.body.innerHTML = '';
      targetWindow.document.body.style.margin = '0';
      const frame = targetWindow.document.createElement('iframe');
      frame.src = objectUrl;
      frame.title = title;
      frame.style.width = '100vw';
      frame.style.height = '100vh';
      frame.style.border = '0';
      targetWindow.document.body.appendChild(frame);
    } catch {
      targetWindow.location.href = objectUrl;
    }
  } else {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 300_000);
}

function openQuotationReviewWindow(message = 'Opening signed quotation...') {
  if (typeof window === 'undefined') return null;
  const previewWindow = window.open('', '_blank');
  if (previewWindow && !previewWindow.closed) {
    try {
      previewWindow.document.title = 'Preparing Signed Quotation Review';
      previewWindow.document.body.innerHTML = `
        <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f8fafc;color:#334155;font:600 16px Segoe UI, Arial, sans-serif;">
          ${message}
        </div>
      `;
      previewWindow.document.body.style.margin = '0';
    } catch {
      // Ignore document access issues and let the final open call fall back.
    }
  }
  return previewWindow;
}

type EditableEmailHtmlProps = {
  value: string;
  onChange: (nextValue: string) => void;
  minHeight?: number;
};

const EditableEmailHtml: React.FC<EditableEmailHtmlProps> = ({ value, onChange, minHeight = 180 }) => {
  const editorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }
    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  return (
    <div
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      onInput={(event) => onChange(event.currentTarget.innerHTML)}
      style={{
        minHeight,
        outline: 'none',
        lineHeight: 1.6,
        color: '#1f2937',
      }}
    />
  );
};

const paymentDisciplinePanelStyle: React.CSSProperties = {
  border: '1px solid #d9e2ec',
  borderRadius: 12,
  padding: 14,
  background: '#ffffff',
};

const paymentDisciplineHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  alignItems: 'center',
  flexWrap: 'wrap',
  marginBottom: 12,
};

const paymentStageGridStyle: React.CSSProperties = {
  marginTop: 12,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
  gap: 12,
};

const responsiveWorkflowTripleFieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
  gap: 12,
  alignItems: 'start',
};

const responsiveWorkflowWideFieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
  gap: 12,
  alignItems: 'start',
};

const responsiveAcceptWorkflowGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
  gap: 16,
  alignItems: 'start',
};

const scopePanelStyle: React.CSSProperties = {
  border: '1px solid #dbe7f0',
  borderRadius: 12,
  padding: 14,
  background: '#ffffff',
};

const scopeListStyle: React.CSSProperties = {
  margin: '10px 0 0 18px',
  padding: 0,
  color: '#243b53',
  fontSize: 13,
  lineHeight: 1.6,
};

const scopeEditorListStyle: React.CSSProperties = {
  display: 'grid',
  gap: 8,
  marginTop: 12,
};

const scopeEditorRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '36px minmax(0, 1fr) minmax(42px, auto)',
  gap: 8,
  alignItems: 'center',
};

const scopeDragHandleStyle: React.CSSProperties = {
  border: '1px solid #cbd5e1',
  background: '#f8fafc',
  color: '#52657a',
  borderRadius: 8,
  padding: 0,
  cursor: 'grab',
  width: 36,
  minHeight: 38,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  touchAction: 'none',
};

const scopeDeleteButtonStyle: React.CSSProperties = {
  border: '1px solid #cbd5e1',
  background: '#fff',
  borderRadius: 8,
  padding: 0,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  width: 40,
  minHeight: 38,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const stageCardStyle: React.CSSProperties = {
  border: '1px solid #d9e2ec',
  borderRadius: 12,
  padding: 14,
  background: '#ffffff',
  minWidth: 0,
};

const paymentStageRefTextareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 68,
  resize: 'vertical',
  lineHeight: 1.45,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

const paymentStageRefTextStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 12,
  color: '#334e68',
  lineHeight: 1.45,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

const normalizeSuburb = (value: string) => value.trim().toLowerCase();
const normalizeLookupValue = (value: unknown) => String(value ?? '').trim().toLowerCase();
const normalizeScopeDetailText = (value: unknown) => String(value ?? '').replace(/\s+/g, ' ').trim();
const normalizeScopeGroupKey = (value: unknown) => String(value ?? '').trim().toLowerCase();
const isBooleanFlagEnabled = (value: unknown, fallback = true) => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) {
    return fallback;
  }

  if (['false', '0', 'no', 'off'].includes(normalized)) {
    return false;
  }
  if (['true', '1', 'yes', 'on'].includes(normalized)) {
    return true;
  }
  return fallback;
};

const normalizeScopeDiscipline = (value: unknown): ScopeDisciplineKey | null => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'structural' || normalized === 'structure') return 'structural';
  if (normalized === 'civil') return 'civil';
  return null;
};

const buildFallbackScopeDetailLibrary = (
  template: Pick<QuotationTemplateDefinition, 'scopeSections'>
): ScopeDetailLibrary => ({
  groups: (template.scopeSections || [])
    .filter((section) => section.tint === 'structural' || section.tint === 'civil')
    .map((section) => ({
      discipline: section.tint,
      groupKey: section.key,
      groupLabel: section.title,
      items: (section.bullets || []).map((bullet, index) => ({
        ScopeDetailItemId: null,
        StructuralCivil: section.tint === 'structural' ? 'Structural' : 'Civil',
        DetailGroupKey: section.key,
        DetailGroupLabel: section.title,
        ScopeDetailItem: bullet,
        SortOrder: (index + 1) * 10,
        IsActive: true,
        IsSystem: true,
        Source: 'fallback',
      })),
    })),
  source: 'fallback',
});

const normalizeScopeDetailLibraryResponse = (
  response: any,
  fallbackTemplate: Pick<QuotationTemplateDefinition, 'scopeSections'>
): ScopeDetailLibrary => {
  if (Array.isArray(response?.groups)) {
    return {
      groups: response.groups,
      source: response?.source || 'db',
    };
  }

  if (Array.isArray(response?.disciplines)) {
    const groups = response.disciplines.flatMap((disciplineGroup: any) => {
      const discipline = normalizeScopeDiscipline(disciplineGroup?.discipline);
      if (!discipline) return [];
      return (disciplineGroup?.detailGroups || []).map((detailGroup: any) => ({
        discipline,
        groupKey: String(detailGroup?.detailGroupKey || '').trim(),
        groupLabel: String(detailGroup?.detailGroupLabel || detailGroup?.detailGroupKey || '').trim(),
        items: (detailGroup?.items || []).map((item: any) => ({
          ScopeDetailItemId: item?.QuotationScopeDetailItemId ?? null,
          StructuralCivil: item?.Discipline ?? discipline,
          DetailGroupKey: String(item?.DetailGroupKey || detailGroup?.detailGroupKey || '').trim(),
          DetailGroupLabel: String(item?.DetailGroupLabel || detailGroup?.detailGroupLabel || detailGroup?.detailGroupKey || '').trim(),
          ScopeDetailItem: String(item?.ItemText || item?.ScopeDetailItem || '').trim(),
          SortOrder: item?.SortOrder ?? null,
          IsActive: item?.IsActive ?? true,
          IsSystem: item?.IsSystem ?? false,
          Source: response?.source || 'db',
        })).filter((item: ScopeDetailLibraryItem) => Boolean(item.ScopeDetailItem)),
      })).filter((group: ScopeDetailGroup) => Boolean(group.groupKey));
    });

    return {
      groups: groups.length > 0 ? groups : buildFallbackScopeDetailLibrary(fallbackTemplate).groups,
      source: response?.source || 'db',
    };
  }

  return buildFallbackScopeDetailLibrary(fallbackTemplate);
};

const dedupeScopeItems = (items: string[]) => {
  const seen = new Set<string>();
  return items
    .map((item) => normalizeScopeDetailText(item))
    .filter((item) => {
      const key = item.toLowerCase();
      if (!item || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const buildScopeSectionFromSelection = (
  discipline: ScopeDisciplineKey,
  groupKey: string,
  groupLabel: string,
  items: string[],
): QuotationScopeCatalogueSection | null => {
  const bullets = dedupeScopeItems(items);
  if (bullets.length === 0) {
    return null;
  }

  const normalizedKey = normalizeScopeGroupKey(groupKey) || normalizeScopeGroupKey(groupLabel) || `${discipline}-custom`;
  const title = groupLabel || groupKey || (discipline === 'structural' ? 'Detailed Structural Scope' : 'Detailed Civil Scope');

  return {
    key: `selected-${discipline}-${normalizedKey}`,
    title,
    tint: discipline,
    bullets,
  };
};

type PartitionedScopeSections = ReturnType<typeof partitionScopeSections>;

const buildDefaultGeneralScopeDrafts = (
  sections: QuotationScopeCatalogueSection[],
): GeneralScopeItemDraft[] => (
  sections.flatMap((section) => (
    (section.bullets || []).map((bullet, index) => ({
      key: `${section.key}-${index}`,
      sectionKey: section.key,
      sectionTitle: section.title,
      text: bullet,
      included: section.key !== ADDITIONAL_SCOPE_SECTION_KEY,
    }))
  ))
);

const hydrateGeneralScopeDrafts = (
  rawDrafts: GeneralScopeItemDraft[],
  fallbackSections: QuotationScopeCatalogueSection[],
): GeneralScopeItemDraft[] => {
  const fallbackDrafts = buildDefaultGeneralScopeDrafts(fallbackSections);
  const fallbackByKey = new Map(fallbackDrafts.map((draft) => [draft.key, draft]));

  if (rawDrafts.length === 0) {
    return fallbackDrafts;
  }

  const hydrated = rawDrafts.map((draft, index) => {
    const fallbackDraft = fallbackByKey.get(draft.key) || null;
    return {
      key: draft.key || fallbackDraft?.key || `general-${index + 1}`,
      sectionKey: draft.sectionKey || fallbackDraft?.sectionKey || 'general',
      sectionTitle: draft.sectionTitle || fallbackDraft?.sectionTitle || 'Other Scope Details',
      text: draft.text ?? fallbackDraft?.text ?? '',
      included: draft.included !== false,
    };
  });
  // Preserve reviewed/custom rows. Add missing optional choices without selecting them.
  return [...hydrated, ...fallbackDrafts.filter(draft => draft.sectionKey === ADDITIONAL_SCOPE_SECTION_KEY
    && !hydrated.some(current => current.key === draft.key
      || normalizeScopeDetailText(current.text).toLowerCase() === normalizeScopeDetailText(draft.text).toLowerCase()))];
};

const buildGeneralScopeSectionsFromDrafts = (
  drafts: GeneralScopeItemDraft[],
  fallbackSections: QuotationScopeCatalogueSection[],
): QuotationScopeCatalogueSection[] => {
  const fallbackByKey = new Map(fallbackSections.map((section) => [section.key, section]));
  const grouped = new Map<string, QuotationScopeCatalogueSection>();

  drafts.forEach((draft, index) => {
    const text = normalizeScopeDetailText(draft.text);
    if (!draft.included || !text) {
      return;
    }

    const fallbackSection = fallbackByKey.get(draft.sectionKey) || null;
    const key = draft.sectionKey || fallbackSection?.key || `general-${index + 1}`;
    const title = draft.sectionTitle || fallbackSection?.title || 'Other Scope Details';
    const existing = grouped.get(key);

    if (existing) {
      existing.bullets.push(text);
      return;
    }

    grouped.set(key, {
      key,
      title,
      tint: 'general',
      bullets: [text],
    });
  });

  const orderedFallbackSections = fallbackSections
    .map((section) => {
      const group = grouped.get(section.key);
      return group
        ? {
            ...group,
            bullets: dedupeScopeItems(group.bullets),
          }
        : null;
    })
    .filter((section): section is QuotationScopeCatalogueSection => Boolean(section) && section.bullets.length > 0);

  const customSections = [...grouped.entries()]
    .filter(([key]) => !fallbackByKey.has(key))
    .map(([, section]) => ({
      ...section,
      bullets: dedupeScopeItems(section.bullets),
    }))
    .filter((section) => section.bullets.length > 0);

  return [...orderedFallbackSections, ...customSections];
};

const buildStoredScopeSections = (
  quotation: PendingQuotationCandidate,
  fallbackSections: PartitionedScopeSections,
  includeGeneralScope: boolean,
) => {
  const storedStructuralSection = buildScopeSectionFromSelection(
    'structural',
    toTrimmedString(quotation.StructuralScopeGroupKey),
    toTrimmedString(quotation.StructuralScopeGroupLabel),
    parseStringArrayValue(quotation.StructuralScopeItemsJson),
  );
  const storedCivilSection = buildScopeSectionFromSelection(
    'civil',
    toTrimmedString(quotation.CivilScopeGroupKey),
    toTrimmedString(quotation.CivilScopeGroupLabel),
    parseStringArrayValue(quotation.CivilScopeItemsJson),
  );
  const storedGeneralScopeDrafts = parseGeneralScopeItemDrafts(quotation.GeneralScopeItemsJson);

  return [
    ...(storedStructuralSection ? [storedStructuralSection] : fallbackSections.structural),
    ...(storedCivilSection ? [storedCivilSection] : fallbackSections.civil),
    ...(includeGeneralScope
      ? (
          storedGeneralScopeDrafts.length > 0
            ? buildGeneralScopeSectionsFromDrafts(storedGeneralScopeDrafts, fallbackSections.general)
            : fallbackSections.general
        )
      : []),
  ].filter((section): section is QuotationScopeCatalogueSection => Boolean(section));
};

const buildPaymentStageDraftsFromStoredStages = (stages: QuotationPaymentStage[]): PaymentStageDraft[] => (
  normalizeStageDrafts(
    stages.map((stage) => {
      const stageKey = (stage.stageKey || 'final') as PaymentStageKey;
      return {
        stageKey,
        label: stage.label || getPaymentStageLabel(stageKey),
        amount: stageKey === 'final' ? '' : String(roundCurrency(stage.amount)),
        triggerOptionId: null,
        triggerLabel: stage.triggerLabel || '',
      };
    })
  )
);

const normalizePostCode = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(Math.trunc(value)) : '';
  }
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? String(Math.trunc(numeric)) : trimmed;
};

const formatCurrency = (value: unknown) => {
  const numeric = typeof value === 'number' ? value : Number(String(value ?? '').trim());
  const safeValue = Number.isFinite(numeric) ? numeric : 0;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(safeValue);
};

const formatCurrencyOrDash = (value: unknown) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return '-';
  }
  return formatCurrency(value);
};

const formatPercentage = (value: unknown) => {
  const numeric = typeof value === 'number' ? value : Number(String(value ?? '').trim());
  if (!Number.isFinite(numeric)) {
    return '0%';
  }
  return `${Math.round(numeric * 100)}%`;
};

const toFeeNumber = (value: unknown) => {
  const numeric = typeof value === 'number' ? value : Number(String(value ?? '').trim());
  return Number.isFinite(numeric) ? numeric : 0;
};

const toPercentageNumber = (value: unknown, fallback: number) => {
  const numeric = typeof value === 'number' ? value : Number(String(value ?? '').trim());
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  return Math.max(0, numeric);
};

const roundCurrency = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const roundUpToIncrement = (value: number, increment: number) => {
  if (value <= 0 || increment <= 0) return 0;
  return Math.ceil(value / increment) * increment;
};

const clampCurrency = (value: number, minValue: number, maxValue: number) => (
  roundCurrency(Math.min(Math.max(value, minValue), maxValue))
);

const PAYMENT_STAGE_ORDER: PaymentStageKey[] = ['deposit', 'stage2', 'stage3', 'final'];

const getPaymentStageLabel = (stageKey: PaymentStageKey) => {
  switch (stageKey) {
    case 'deposit':
      return 'Deposit';
    case 'stage2':
      return 'Stage 2 Payment';
    case 'stage3':
      return 'Stage 3 Payment';
    case 'final':
      return 'Final Payment';
    default:
      return 'Payment Stage';
  }
};

const getPaymentStageReferenceCode = (
  quotationNumber: string,
  componentKey: 'structural' | 'civil',
  stageIndex: number,
) => `${quotationNumber}-${componentKey === 'structural' ? 'S' : 'C'}${stageIndex + 1}`;

const normalizeStageDrafts = (drafts: PaymentStageDraft[]) => (
  [...drafts].sort((left, right) => PAYMENT_STAGE_ORDER.indexOf(left.stageKey) - PAYMENT_STAGE_ORDER.indexOf(right.stageKey))
);

const normalizeManualPaymentStageAmounts = (
  drafts: PaymentStageDraft[],
  fee: number,
) => {
  const safeFee = roundCurrency(Math.max(0, fee));
  let remaining = safeFee;

  return normalizeStageDrafts(drafts).map((draft) => {
    if (draft.stageKey === 'final') {
      return {
        ...draft,
        amount: '',
      };
    }

    const rawAmount = String(draft.amount ?? '').trim();
    const amount = clampCurrency(Math.max(0, toFeeNumber(rawAmount)), 0, remaining);
    remaining = clampCurrency(remaining - amount, 0, safeFee);

    return {
      ...draft,
      amount: rawAmount ? String(roundCurrency(amount)) : '',
    };
  });
};

const arePaymentStageDraftsEqual = (
  left: PaymentStageDraft[],
  right: PaymentStageDraft[],
) => left.length === right.length && left.every((item, index) => {
  const other = right[index];
  return other
    && item.stageKey === other.stageKey
    && item.label === other.label
    && item.amount === other.amount
    && item.triggerOptionId === other.triggerOptionId
    && item.triggerLabel === other.triggerLabel;
});

const createStageDraftsFromSchedule = (
  schedule: PaymentComponentSchedule,
  referenceOptions: PaymentStageReferenceOption[] = [],
): PaymentStageDraft[] => (
  normalizeStageDrafts(
    schedule.stages.map((stage, index) => {
      const stageKey = (stage.stageKey || 'final') as PaymentStageKey;
      const defaultOption = getPaymentStageReferenceOptionForStage(referenceOptions, stageKey, index);
      const selectedOption = referenceOptions.find((option) => option.QuotationPaymentStageRefOptionId !== null
        && option.QuotationPaymentStageRefOptionId === stage.triggerOptionId)
        || referenceOptions.find((option) => option.OptionLabel === stage.triggerLabel)
        || defaultOption;

      return {
        stageKey,
        label: stage.label || getPaymentStageLabel(stageKey),
        amount: stage.stageKey === 'final' ? '' : String(roundCurrency(stage.amount)),
        triggerOptionId: selectedOption?.QuotationPaymentStageRefOptionId ?? stage.triggerOptionId ?? null,
        triggerLabel: selectedOption?.OptionLabel || stage.triggerLabel || '',
      };
    })
  )
);

const getPaymentStageReferenceOptions = (
  library: PaymentStageReferenceLibrary,
  discipline: 'Structural' | 'Civil',
) => library.disciplines.find((item) => item.discipline === discipline)?.options || [];

const getDefaultPaymentStageReferenceOption = (
  options: PaymentStageReferenceOption[],
  stageIndex: number,
) => {
  if (options.length === 0) {
    return null;
  }

  const safeIndex = Math.max(0, Math.min(stageIndex, options.length - 1));
  return options[safeIndex] || options[options.length - 1] || null;
};

const getPaymentStageReferenceOptionForStage = (
  options: PaymentStageReferenceOption[],
  stageKey: PaymentStageKey,
  stageIndex: number,
) => {
  if (options.length === 0) {
    return null;
  }

  if (stageKey === 'final') {
    return options[options.length - 1] || null;
  }

  if (stageKey === 'stage3') {
    const lastMiddleIndex = Math.max(0, options.length - 2);
    return getDefaultPaymentStageReferenceOption(options, Math.min(2, lastMiddleIndex));
  }

  return getDefaultPaymentStageReferenceOption(options, stageIndex);
};

const stageDraftMatchesReferenceOption = (
  draft: PaymentStageDraft,
  option: PaymentStageReferenceOption | null,
) => {
  if (!option) {
    return false;
  }

  if (option.QuotationPaymentStageRefOptionId !== null) {
    return draft.triggerOptionId === option.QuotationPaymentStageRefOptionId;
  }

  return Boolean(draft.triggerLabel) && draft.triggerLabel === option.OptionLabel;
};

const decoratePaymentComponentScheduleWithReferenceLabels = (
  schedule: PaymentComponentSchedule,
  library: PaymentStageReferenceLibrary,
  quotationNumber: string,
): PaymentComponentSchedule => {
  const discipline = schedule.key === 'structural' ? 'Structural' : 'Civil';
  const options = getPaymentStageReferenceOptions(library, discipline);

  return {
    ...schedule,
    stages: schedule.stages.map((stage, index) => {
      const stageKey = (stage.stageKey || 'final') as PaymentStageKey;
      const defaultOption = getPaymentStageReferenceOptionForStage(options, stageKey, index);
      const selectedOption = options.find((option) => option.QuotationPaymentStageRefOptionId !== null
        && option.QuotationPaymentStageRefOptionId === stage.triggerOptionId)
        || options.find((option) => option.OptionLabel === stage.triggerLabel)
        || defaultOption;

      return {
        ...stage,
        reference: getPaymentStageReferenceCode(quotationNumber, schedule.key, index),
        triggerOptionId: selectedOption?.QuotationPaymentStageRefOptionId ?? stage.triggerOptionId ?? null,
        triggerLabel: selectedOption?.OptionLabel || stage.triggerLabel || '',
      };
    }),
  };
};

const decoratePaymentScheduleWithReferenceLabels = (
  schedule: PaymentSchedule,
  library: PaymentStageReferenceLibrary,
): PaymentSchedule => {
  const quotationNumber = schedule.quotationNumber || 'DRAFT';
  const componentSchedules = (schedule.componentSchedules || []).map((componentSchedule) => (
    decoratePaymentComponentScheduleWithReferenceLabels(componentSchedule, library, quotationNumber)
  ));

  return {
    ...schedule,
    componentSchedules,
    stages: componentSchedules.flatMap((componentSchedule) => componentSchedule.stages),
  };
};

const mergeStageTriggers = (
  schedule: PaymentComponentSchedule,
  drafts: PaymentStageDraft[],
): PaymentComponentSchedule => {
  const draftMap = new Map(drafts.map((draft) => [draft.stageKey, draft]));
  return {
    ...schedule,
    stages: schedule.stages.map((stage) => {
      const draft = draftMap.get((stage.stageKey || 'final') as PaymentStageKey);
      return draft
        ? {
            ...stage,
            triggerOptionId: draft.triggerOptionId ?? null,
            triggerLabel: draft.triggerLabel || '',
          }
        : stage;
    }),
  };
};

const buildManualPaymentComponentSchedule = (
  componentKey: 'structural' | 'civil',
  label: string,
  fee: number,
  quotationNumber: string,
  drafts: PaymentStageDraft[],
): PaymentComponentSchedule => {
  const safeFee = roundCurrency(Math.max(0, fee));
  const normalizedDrafts = normalizeStageDrafts(
    drafts.length > 0
      ? drafts
      : [
          { stageKey: 'deposit', label: getPaymentStageLabel('deposit'), amount: '', triggerOptionId: null, triggerLabel: '' },
          { stageKey: 'stage2', label: getPaymentStageLabel('stage2'), amount: '', triggerOptionId: null, triggerLabel: '' },
          { stageKey: 'final', label: getPaymentStageLabel('final'), amount: '', triggerOptionId: null, triggerLabel: '' },
        ]
  );
  let remaining = safeFee;

  const stages = normalizedDrafts.map((draft, index) => {
    const isFinal = draft.stageKey === 'final';
    const requestedAmount = isFinal ? remaining : Math.max(0, toFeeNumber(draft.amount));
    const amount = clampCurrency(requestedAmount, 0, remaining);
    remaining = clampCurrency(remaining - amount, 0, safeFee);
    const percentage = safeFee > 0 ? amount / safeFee : 0;

    return {
      key: `${componentKey}-${draft.stageKey}`,
      stageKey: draft.stageKey,
      componentKey,
      componentLabel: label,
      label: draft.label || getPaymentStageLabel(draft.stageKey),
      requestedPercentage: percentage,
      percentage,
      amount,
      reference: getPaymentStageReferenceCode(quotationNumber, componentKey, index),
      description: isFinal ? 'Remaining balance after the earlier manual stages.' : 'Manual schedule override.',
      triggerOptionId: draft.triggerOptionId ?? null,
      triggerLabel: draft.triggerLabel || '',
    } satisfies PaymentScheduleStage;
  });

  return {
    key: componentKey,
    label,
    fee: safeFee,
    depositPercentage: stages[0]?.percentage ?? 0,
    stage2Percentage: stages.find((stage) => stage.stageKey === 'stage2')?.percentage ?? 0,
    stages,
  };
};

const buildFallbackPaymentComponentSchedule = (
  componentKey: 'structural' | 'civil',
  label: string,
  fee: number,
  quotationNumber: string,
  depositPercentageInput: unknown,
  stage2PercentageInput: unknown,
): PaymentComponentSchedule => {
  const safeFee = roundCurrency(Math.max(0, fee));

  if (componentKey === 'civil') {
    let depositAmount = 0;
    let depositDescription = 'No civil deposit is required for fees up to $3,000.';

    if (safeFee > 5000) {
      depositAmount = clampCurrency(safeFee * 0.2 * 1.1, 0, safeFee);
      depositDescription = 'Civil deposit uses 20% of the civil fee plus GST.';
    } else if (safeFee > 3000) {
      depositAmount = clampCurrency(500, 0, safeFee);
      depositDescription = 'Civil deposit is fixed at $500 when the civil fee exceeds $3,000.';
    }

    const remainingAfterDeposit = clampCurrency(safeFee - depositAmount, 0, safeFee);

    let stage2Amount = 0;
    let stage2Description = 'Stage 2 collects the remaining civil balance for fees up to $5,000.';

    if (safeFee > 10000) {
      stage2Amount = remainingAfterDeposit;
      stage2Description = 'Stage 2 collects the remaining civil balance once the civil fee exceeds $10,000.';
    } else if (safeFee > 5000) {
      stage2Amount = clampCurrency(safeFee * 0.6, 0, remainingAfterDeposit);
      stage2Description = 'Stage 2 uses 60% of the civil fee for civil fees above $5,000 and up to $10,000.';
    } else {
      stage2Amount = remainingAfterDeposit;
    }

    const finalAmount = clampCurrency(safeFee - depositAmount - stage2Amount, 0, safeFee);

    return {
      key: componentKey,
      label,
      fee: safeFee,
      depositPercentage: safeFee > 0 ? depositAmount / safeFee : 0,
      stage2Percentage: safeFee > 0 ? stage2Amount / safeFee : 0,
      stages: [
        {
          key: `${componentKey}-deposit`,
          stageKey: 'deposit',
          componentKey,
          componentLabel: label,
          label: 'Deposit',
          requestedPercentage: safeFee > 0 ? depositAmount / safeFee : 0,
          percentage: safeFee > 0 ? depositAmount / safeFee : 0,
          amount: depositAmount,
          reference: `${quotationNumber}-C1`,
          description: depositDescription,
        },
        {
          key: `${componentKey}-stage2`,
          stageKey: 'stage2',
          componentKey,
          componentLabel: label,
          label: 'Stage 2 Payment',
          requestedPercentage: safeFee > 0 ? stage2Amount / safeFee : 0,
          percentage: safeFee > 0 ? stage2Amount / safeFee : 0,
          amount: stage2Amount,
          reference: `${quotationNumber}-C2`,
          description: stage2Description,
        },
        {
          key: `${componentKey}-final`,
          stageKey: 'final',
          componentKey,
          componentLabel: label,
          label: 'Final Payment',
          requestedPercentage: safeFee > 0 ? finalAmount / safeFee : 0,
          percentage: safeFee > 0 ? finalAmount / safeFee : 0,
          amount: finalAmount,
          reference: `${quotationNumber}-C3`,
          description: 'Remaining civil balance after deposit and stage 2.',
        },
      ],
    };
  }

  const depositPercentage = toPercentageNumber(depositPercentageInput, 10);
  const stage2Percentage = toPercentageNumber(stage2PercentageInput, 70);
  const referencePrefix = componentKey === 'structural' ? 'S' : 'C';
  const depositAmount = clampCurrency(roundUpToIncrement(safeFee * (depositPercentage / 100), 500), 0, safeFee);
  const remainingAfterDeposit = clampCurrency(safeFee - depositAmount, 0, safeFee);
  const stage2Amount = clampCurrency(roundUpToIncrement(safeFee * (stage2Percentage / 100), 500), 0, remainingAfterDeposit);
  const finalAmount = clampCurrency(safeFee - depositAmount - stage2Amount, 0, safeFee);

  return {
    key: componentKey,
    label,
    fee: safeFee,
    depositPercentage: depositPercentage / 100,
    stage2Percentage: stage2Percentage / 100,
    stages: [
      {
        key: `${componentKey}-deposit`,
        stageKey: 'deposit',
        componentKey,
        componentLabel: label,
        label: 'Deposit',
        requestedPercentage: depositPercentage / 100,
        percentage: safeFee > 0 ? depositAmount / safeFee : 0,
        amount: depositAmount,
        reference: `${quotationNumber}-${referencePrefix}1`,
        description: 'Initial payment rounded up to the next $500.',
      },
      {
        key: `${componentKey}-stage2`,
        stageKey: 'stage2',
        componentKey,
        componentLabel: label,
        label: 'Stage 2 Payment',
        requestedPercentage: stage2Percentage / 100,
        percentage: safeFee > 0 ? stage2Amount / safeFee : 0,
        amount: stage2Amount,
        reference: `${quotationNumber}-${referencePrefix}2`,
        description: 'Progress payment rounded up to the next $500 without exceeding the remaining balance.',
      },
      {
        key: `${componentKey}-final`,
        stageKey: 'final',
        componentKey,
        componentLabel: label,
        label: 'Final Payment',
        requestedPercentage: safeFee > 0 ? finalAmount / safeFee : 0,
        percentage: safeFee > 0 ? finalAmount / safeFee : 0,
        amount: finalAmount,
        reference: `${quotationNumber}-${referencePrefix}3`,
        description: 'Remaining balance after deposit and stage 2 payment.',
      },
    ],
  };
};

const partitionScopeSections = (sections: QuotationScopeCatalogueSection[]) => ({
  structural: sections.filter((section) => section.tint === 'structural'),
  civil: sections.filter((section) => section.tint === 'civil'),
  general: sections.filter((section) => section.tint === 'general'),
});

const buildPendingQuotationAddress = (row: PendingQuotationCandidate | null) => {
  if (!row) return '';
  return [
    row.Address || '',
    row.Suburb || '',
    normalizePostCode(row.PostCode),
  ].filter(Boolean).join(', ');
};

const buildClientAddressFromProjectFields = (form: { projectAddress?: unknown; suburb?: unknown; postCode?: unknown }) => (
  [
    String(form.projectAddress ?? '').trim(),
    String(form.suburb ?? '').trim(),
    normalizePostCode(form.postCode),
  ].filter(Boolean).join(', ')
);

const fetchGooglePlaceAddressDetails = async (placeId: string): Promise<GooglePlaceAddressDetails | null> => {
  const normalizedPlaceId = String(placeId || '').trim();
  if (!normalizedPlaceId) {
    return null;
  }

  const response = await fetch(`/api/v1/public/google-places/details?placeId=${encodeURIComponent(normalizedPlaceId)}`);
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(body?.error?.message || 'Address details are unavailable.');
  }

  return body?.data?.place || null;
};

const buildPendingQuotationProjectTitle = (row: PendingQuotationCandidate | null) => {
  if (!row) return '';
  const address = row.Address || 'Untitled Site';
  const suffix = row.ProjectDescription || row.ProjectType || row.ProjectScope || row.Council || '';
  return suffix ? `${address} - ${suffix}` : String(address);
};

const isSamePendingQuotation = (
  left: PendingQuotationCandidate | null | undefined,
  right: PendingQuotationCandidate | null | undefined,
) => {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return normalizeLookupValue(left.QuotationId) === normalizeLookupValue(right.QuotationId)
    && normalizeLookupValue(left.QuotationNumber) === normalizeLookupValue(right.QuotationNumber);
};

const matchesPendingQuotationExactly = (row: PendingQuotationCandidate, value: string) => {
  const normalized = normalizeLookupValue(value);
  if (!normalized) return false;
  return normalizeLookupValue(row.QuotationNumber) === normalized
    || normalizeLookupValue(row.QuotationId) === normalized;
};

const buildPendingQuotationOptionLabel = (row: PendingQuotationCandidate) => {
  const number = String(row.QuotationNumber || row.QuotationId || '').trim();
  const location = buildPendingQuotationAddress(row);
  return [number, location].filter(Boolean).join(' - ');
};

const toTrimmedString = (value: unknown) => String(value ?? '').trim();

const toFileNameTitleCase = (value: string) => (
  value.toLowerCase().replace(/[a-z]+(?:'[a-z]+)?/g, (word) => word.charAt(0).toUpperCase() + word.slice(1))
);

const sanitizeQuotationFileNamePart = (value: unknown) => (
  toFileNameTitleCase(toTrimmedString(value)
    .replace(/[<>:"/\\|?*\x00-\x1F]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[. ]+$/g, '')
    .trim())
);

const buildFiledQuotationPdfFileName = (input: {
  quotationNumber?: unknown;
  address?: unknown;
  suburb?: unknown;
  fallbackQuotationId?: unknown;
}) => {
  const quotationNumber = toTrimmedString(input.quotationNumber) || toTrimmedString(input.fallbackQuotationId) || 'Quotation';
  const suburb = sanitizeQuotationFileNamePart(input.suburb);
  let address = sanitizeQuotationFileNamePart(String(input.address ?? '').split(',').map((part) => part.trim()).filter(Boolean)[0] || input.address);
  if (address && suburb) {
    const suburbPattern = new RegExp(`(?:,?\\s*)${suburb.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\s+\\d{4})?$`, 'i');
    address = address.replace(suburbPattern, '').replace(/\b\d{4}\b$/g, '').replace(/[,\s]+$/g, '').trim();
  }

  const location = [address, suburb].filter(Boolean).join(' ');
  const baseName = location ? `Quotation ${quotationNumber} - ${location}` : `Quotation ${quotationNumber}`;
  return `${sanitizeQuotationFileNamePart(baseName) || 'Quotation'}.pdf`;
};

const WD_QUOTATION_CC_EMAIL = 'wd@linfinityeng.com';

const normalizeCompanyName = (value: unknown) => toTrimmedString(value).toLowerCase();

const asPositiveInteger = (value: unknown): number | null => {
  const numeric = Number(toTrimmedString(value));
  return Number.isFinite(numeric) && numeric > 0 ? Math.trunc(numeric) : null;
};

const isActiveSigningLink = (value: QuotationSigningLinkResult | null | undefined, quotationId: number | null): value is QuotationSigningLinkResult => {
  if (!value || !quotationId) return false;
  if (value.quotationId !== quotationId) return false;
  return new Date(value.expiresAt).getTime() > Date.now();
};

const toInitialsLabel = (value: string) => {
  const normalized = toTrimmedString(value);
  if (!normalized) return '';
  if (/^[A-Z]{2,4}$/.test(normalized)) return normalized;
  return normalized
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
};

const getWorkflowEngineerShortLabel = (engineer: Pick<WorkflowEngineerOption, 'email' | 'displayName'>) => {
  return userEmailToUserInitial(engineer.email)
    || toInitialsLabel(engineer.displayName || '')
    || toInitialsLabel(engineer.email.split('@')[0] || '')
    || toTrimmedString(engineer.displayName || engineer.email).slice(0, 2).toUpperCase();
};

const quotationInvolvesWd = (values: Array<unknown>) => values
  .map((value) => toTrimmedString(value).toLowerCase())
  .filter(Boolean)
  .some((value) => (
    value === WD_QUOTATION_CC_EMAIL
    || value === 'wdu@eqeng.com.au'
    || value.includes('wilson du')
    || /\bwd\b/.test(value)
    || /\bwdu\b/.test(value)
  ));

type QuotationSignatoryProfile = {
  email: string;
  displayName: string;
  company: string;
  position: string;
};

const FALLBACK_SIGNATORY_BY_ENTITY: Record<string, QuotationSignatoryProfile> = {
  'equilibrium engineering pty ltd': {
    email: 'wdu@eqeng.com.au',
    displayName: 'Wilson Du',
    company: 'Equilibrium Engineering Pty Ltd',
    position: 'Director',
  },
  'linfinity engineering pty ltd': {
    email: 'yl@linfinityeng.com',
    displayName: 'Yuan Lin',
    company: 'Linfinity Engineering Pty Ltd',
    position: 'Director',
  },
};

const buildFallbackSignatoryProfile = (entity?: string | null): QuotationSignatoryProfile => {
  const normalizedEntity = normalizeCompanyName(entity);
  if (normalizedEntity && FALLBACK_SIGNATORY_BY_ENTITY[normalizedEntity]) {
    return FALLBACK_SIGNATORY_BY_ENTITY[normalizedEntity];
  }

  return {
    email: '',
    displayName: '',
    company: toTrimmedString(entity) || 'Linfinity Engineering Pty Ltd',
    position: 'Director',
  };
};

const resolveQuotationSignatoryProfile = (args: {
  entity?: string | null;
  preferredEmail?: string | null;
  currentUserEmail?: string | null;
  options?: WorkflowEngineerOption[];
}): QuotationSignatoryProfile => {
  const normalizedEntity = normalizeCompanyName(args.entity);
  const normalizedPreferredEmail = toTrimmedString(args.preferredEmail).toLowerCase();
  const normalizedCurrentUserEmail = toTrimmedString(args.currentUserEmail).toLowerCase();
  const options = Array.isArray(args.options) ? args.options : [];
  const matchingEntityOptions = options.filter((option) => normalizeCompanyName(option.company) === normalizedEntity);
  const allCandidates = matchingEntityOptions.length > 0 ? matchingEntityOptions : options;
  const selected = allCandidates.find((option) => option.email === normalizedPreferredEmail)
    || allCandidates.find((option) => option.email === normalizedCurrentUserEmail)
    || matchingEntityOptions[0]
    || options.find((option) => option.email === normalizedPreferredEmail)
    || options.find((option) => option.email === normalizedCurrentUserEmail)
    || null;

  const fallback = buildFallbackSignatoryProfile(args.entity);

  return {
    email: selected?.email || normalizedPreferredEmail || normalizedCurrentUserEmail || fallback.email,
    displayName: toTrimmedString(selected?.displayName) || fallback.displayName,
    company: resolveQuotationSignatoryCompany({
      name: toTrimmedString(selected?.displayName) || fallback.displayName,
      email: selected?.email || normalizedPreferredEmail || normalizedCurrentUserEmail || fallback.email,
      company: toTrimmedString(selected?.company) || fallback.company,
      entity: args.entity || undefined,
    }),
    position: toTrimmedString(selected?.jobTitle || selected?.role) || fallback.position,
  };
};

const formatWorkflowUiNotice = (error: any, fallbackMessage: string): WorkflowUiNotice => {
  const details = error?.details;
  const errorBlock = details?.error && typeof details.error === 'object' ? details.error : null;
  const message = formatUserFacingError(
    error,
    fallbackMessage,
    'Try again from this workflow. If the message repeats, check the API connection before continuing.'
  );

  return {
    message,
    code: toTrimmedString(errorBlock?.code || details?.code) || undefined,
    correlationId: toTrimmedString(errorBlock?.correlationId || details?.correlationId) || undefined,
  };
};

const parseStringArrayValue = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => toTrimmedString(item)).filter(Boolean);
  }

  try {
    const parsed = JSON.parse(String(value));
    return Array.isArray(parsed)
      ? parsed.map((item) => toTrimmedString(item)).filter(Boolean)
      : [];
  } catch {
    return [];
  }
};

const parseGeneralScopeItemDrafts = (value: unknown): GeneralScopeItemDraft[] => {
  if (!value) return [];

  let rawItems: unknown[] = [];
  if (Array.isArray(value)) {
    rawItems = value;
  } else if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      rawItems = Array.isArray(parsed) ? parsed : [];
    } catch {
      rawItems = [];
    }
  }

  return rawItems.flatMap((item, index) => {
    if (!item || typeof item !== 'object') {
      return [];
    }

    const record = item as Record<string, unknown>;
    const sectionKey = toTrimmedString(record.sectionKey ?? record.SectionKey);
    const sectionTitle = toTrimmedString(record.sectionTitle ?? record.SectionTitle);

    return [{
      key: toTrimmedString(record.key ?? record.Key) || `${sectionKey || 'general'}-${index}`,
      sectionKey: sectionKey || 'general',
      sectionTitle: sectionTitle || 'Other Scope Details',
      text: String(record.text ?? record.Text ?? ''),
      included: record.included !== false && record.Included !== false,
    }];
  });
};

const normalizeQuotationPdfDate = (value: unknown) => {
  const trimmed = toTrimmedString(value);
  if (!trimmed) {
    return new Date().toISOString().slice(0, 10);
  }

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return trimmed.slice(0, 10);
  }

  return date.toISOString().slice(0, 10);
};

const parseQuotationPaymentStages = (value: unknown): QuotationPaymentStage[] => {
  if (!value) return [];

  let parsed: any = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }
  }

  const rawStages = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.stages)
      ? parsed.stages
      : [];

  return rawStages
    .map((stage: any, index: number) => {
      const amount = Number(stage?.amount ?? stage?.Amount ?? 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        return null;
      }

      return {
        key: toTrimmedString(stage?.key ?? stage?.Key ?? `payment-stage-${index + 1}`),
        stageKey: toTrimmedString(stage?.stageKey ?? stage?.StageKey ?? 'final') || 'final',
        componentKey: toTrimmedString(stage?.componentKey ?? stage?.ComponentKey ?? 'quotation') || 'quotation',
        componentLabel: toTrimmedString(stage?.componentLabel ?? stage?.ComponentLabel ?? 'Payment') || 'Payment',
        label: toTrimmedString(stage?.label ?? stage?.Label ?? 'Payment') || 'Payment',
        requestedPercentage: Number(stage?.requestedPercentage ?? stage?.RequestedPercentage ?? stage?.percentage ?? stage?.Percentage ?? 0) || 0,
        percentage: Number(stage?.percentage ?? stage?.Percentage ?? 0) || 0,
        amount,
        reference: toTrimmedString(stage?.reference ?? stage?.Reference ?? ''),
        description: toTrimmedString(stage?.description ?? stage?.Description ?? '') || undefined,
        triggerLabel: toTrimmedString(stage?.triggerLabel ?? stage?.TriggerLabel ?? '') || undefined,
      } satisfies QuotationPaymentStage;
    })
    .filter((stage): stage is QuotationPaymentStage => Boolean(stage));
};

const buildStructuralInvoicePrefillState = (
  quotation: PendingQuotationCandidate | null,
  projectId: string,
  projectNumber: string,
): InvoiceWorkflowPrefillState | null => {
  if (!quotation) return null;
  const normalizedProjectNumber = toTrimmedString(projectNumber);
  if (!normalizedProjectNumber) return null;

  const structuralStages = parseQuotationPaymentStages(quotation.StructuralPaymentScheduleJson)
    .filter((stage) => {
      const componentKey = normalizeLookupValue(stage.componentKey);
      const componentLabel = normalizeLookupValue(stage.componentLabel);
      return !componentKey || componentKey === 'structural' || componentLabel === 'structural';
    });
  const firstStructuralStage = structuralStages.find((stage) => toFeeNumber(stage.amount) > 0) || null;
  const structuralAmount = firstStructuralStage
    ? toFeeNumber(firstStructuralStage.amount)
    : getQuotationStructuralFeeTotal(quotation);

  if (structuralAmount <= 0) {
    return null;
  }

  const stageLabel = toTrimmedString(firstStructuralStage?.label) || 'Stage 1 Payment';
  const componentLabel = toTrimmedString(firstStructuralStage?.componentLabel) || 'Structural';
  const lineDescription = stageLabel.toLowerCase().includes(componentLabel.toLowerCase())
    ? stageLabel
    : `${componentLabel} ${stageLabel}`;

  return {
    mode: 'create',
    prefill: {
      invoiceNumber: `${normalizedProjectNumber}-1`,
      projectId: toTrimmedString(projectId) || undefined,
      projectNumber: normalizedProjectNumber,
      entity: toTrimmedString(quotation.Entity) || undefined,
      clientName: toTrimmedString(quotation.Client || quotation.ClientName) || undefined,
      clientCompany: toTrimmedString(quotation.ClientCompany || quotation.CompanyName) || undefined,
      clientAddress: toTrimmedString(quotation.ClientAddress) || undefined,
      clientMobile: toTrimmedString(quotation.ClientMobile) || undefined,
      clientEmail: toTrimmedString(quotation.ClientEmail) || undefined,
      projectAddress: buildPendingQuotationAddress(quotation) || undefined,
      notes: `Initial structural invoice from quotation ${toTrimmedString(quotation.QuotationNumber || quotation.QuotationId) || normalizedProjectNumber}`,
      items: [{
        categoryName: 'Structural',
        description: lineDescription,
        quantity: '1',
        unitPrice: String(roundCurrency(structuralAmount)),
        taxRate: '0.1',
      }],
      successMessage: `Prepared ${lineDescription} for project ${normalizedProjectNumber}.`,
    },
  };
};

function buildQuotationExclusionOptions(rows: Array<Partial<QuotationExclusionOption> | string>): QuotationExclusionOption[] {
  const seen = new Set<string>();
  const options: QuotationExclusionOption[] = [];

  rows.forEach((row, index) => {
    const label = normalizeQuotationExclusionLabel(typeof row === 'string' ? row : row?.Label);
    const key = label.toLowerCase();
    if (!label || seen.has(key)) {
      return;
    }
    seen.add(key);
    options.push({
      QuotationExclusionOptionId: typeof row === 'string' ? null : row.QuotationExclusionOptionId ?? null,
      Label: label,
      SortOrder: typeof row === 'string' ? (index + 1) * 10 : row.SortOrder ?? (index + 1) * 10,
      IsActive: typeof row === 'string' ? true : row.IsActive ?? true,
      IsSystem: typeof row === 'string' ? true : row.IsSystem ?? false,
      Source: typeof row === 'string' ? 'default' : row.Source,
    });
  });

  return options.sort((a, b) => {
    const sortDelta = Number(a.SortOrder ?? 0) - Number(b.SortOrder ?? 0);
    if (sortDelta !== 0) {
      return sortDelta;
    }
    return a.Label.localeCompare(b.Label);
  });
}

function reorderArray<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  if (
    fromIndex === toIndex
    || fromIndex < 0
    || toIndex < 0
    || fromIndex >= items.length
    || toIndex >= items.length
  ) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
}

const QuotationWorkflowPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const routeState = (location.state || {}) as QuotationWorkflowPrefillState;
  const isOutlookDraft = Boolean(routeState.outlookQuotationDraft && !routeState.amendQuotation
    && routeState.outlookQuotationDraft.ownerIdentity === outlookQuotationOwnerIdentity(user));
  const isWorkflowDraft = Boolean(routeState.workflowQuotationDraft && !routeState.amendQuotation
    && routeState.workflowQuotationDraft.ownerIdentity === outlookQuotationOwnerIdentity(user));
  const isImportedDraft = isOutlookDraft || isWorkflowDraft;
  const isPhone = useIsNarrowScreen(720);
  const currentUserEmail = toTrimmedString(user?.userDetails).toLowerCase();
  const [busy, setBusy] = useState<'create' | 'accept' | 'preview' | 'filePdf' | 'fileApproval' | 'fileApprovalVerify' | 'emailPreview' | 'emailPdf' | 'reviewSignedPdf' | 'acceptReview' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [prefillNotice, setPrefillNotice] = useState<string | null>(null);
  const [result, setResult] = useState<WorkflowResult>(null);
  const [suburbOptions, setSuburbOptions] = useState<SuburbLookupRow[]>([]);
  const [suburbLookupMessage, setSuburbLookupMessage] = useState('');
  const [quotationTemplateDefinition, setQuotationTemplateDefinition] = useState(DEFAULT_QUOTATION_TEMPLATE_DEFINITION);
  const [quotationExclusionOptions, setQuotationExclusionOptions] = useState<QuotationExclusionOption[]>([]);
  const [quotationExclusionMessage, setQuotationExclusionMessage] = useState('');
  const [adHocExclusion, setAdHocExclusion] = useState('');
  const [saveAdHocExclusion, setSaveAdHocExclusion] = useState(true);
  const [exclusionActionBusy, setExclusionActionBusy] = useState(false);
  const [exclusionDeleteBusyLabel, setExclusionDeleteBusyLabel] = useState<string | null>(null);
  const [quotationLookupBusy, setQuotationLookupBusy] = useState(false);
  const [quotationLookupMessage, setQuotationLookupMessage] = useState('');
  const [paymentScheduleBusy, setPaymentScheduleBusy] = useState(false);
  const [paymentScheduleMessage, setPaymentScheduleMessage] = useState('');
  const [quotationNumberStatusBusy, setQuotationNumberStatusBusy] = useState(false);
  const [quotationNumberStatus, setQuotationNumberStatus] = useState<QuotationNumberStatus | null>(null);
  const [selectedExistingQuotationFolderPath, setSelectedExistingQuotationFolderPath] = useState('');
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentSchedule | null>(null);
  const [paymentStageRefLibrary, setPaymentStageRefLibrary] = useState<PaymentStageReferenceLibrary>({ disciplines: [], source: 'fallback' });
  const [paymentStageRefMessage, setPaymentStageRefMessage] = useState('');
  const [scopeDetailLibrary, setScopeDetailLibrary] = useState<ScopeDetailLibrary>(buildFallbackScopeDetailLibrary(DEFAULT_QUOTATION_TEMPLATE_DEFINITION));
  const [scopeDetailMessage, setScopeDetailMessage] = useState('');
  const [scopeDetailActionBusy, setScopeDetailActionBusy] = useState<'structural' | 'civil' | null>(null);
  const [exclusionOrder, setExclusionOrder] = useState<string[]>([]);
  const [scopeItemDrag, setScopeItemDrag] = useState<ScopeItemDragState>(null);
  const [structuralExtraScopeRow, setStructuralExtraScopeRow] = useState('');
  const [civilExtraScopeRow, setCivilExtraScopeRow] = useState('');
  const [saveStructuralExtraScopeRow, setSaveStructuralExtraScopeRow] = useState(true);
  const [saveCivilExtraScopeRow, setSaveCivilExtraScopeRow] = useState(true);
  const [pendingQuotationSuggestions, setPendingQuotationSuggestions] = useState<PendingQuotationCandidate[]>([]);
  const [selectedPendingQuotation, setSelectedPendingQuotation] = useState<PendingQuotationCandidate | null>(null);
  const [acceptReviewOpen, setAcceptReviewOpen] = useState(false);
  const [acceptReviewPdfUrl, setAcceptReviewPdfUrl] = useState<string | null>(null);
  const [acceptReviewQuotation, setAcceptReviewQuotation] = useState<PendingQuotationCandidate | null>(null);
  const [acceptedQuotationForActions, setAcceptedQuotationForActions] = useState<PendingQuotationCandidate | null>(null);
  const [acceptReviewNotice, setAcceptReviewNotice] = useState<WorkflowUiNotice | null>(null);
  const [availableEngineerOptions, setAvailableEngineerOptions] = useState<WorkflowEngineerOption[]>([]);
  const [engineerOptionsBusy, setEngineerOptionsBusy] = useState(false);
  const [engineerOptionsMessage, setEngineerOptionsMessage] = useState('');
  const [clientLookupBusy, setClientLookupBusy] = useState(false);
  const [clientLookupMessage, setClientLookupMessage] = useState('');
  const [clientMatches, setClientMatches] = useState<ClientLookupCandidate[]>([]);
  const [dismissedClientLookupQuery, setDismissedClientLookupQuery] = useState('');
  const exclusionSelectionInitializedRef = useRef(false);
  const suppressClientLookupRef = useRef(false);
  const dismissedClientLookupQueryRef = useRef('');
  const quotationNumberAutofillRef = useRef(false);
  const createDraftPanelRef = useRef<HTMLDivElement | null>(null);
  const [loadedPendingQuotationDraft, setLoadedPendingQuotationDraft] = useState<LoadedPendingQuotationDraft | null>(null);
  const [outputSigningLink, setOutputSigningLink] = useState<QuotationSigningLinkResult | null>(null);
  const [quotationEmailCcText, setQuotationEmailCcText] = useState('');
  const [quotationEmailPreview, setQuotationEmailPreview] = useState<QuotationEmailPreviewResult | null>(null);
  const [quotationEmailComposerDraft, setQuotationEmailComposerDraft] = useState<QuotationEmailComposerDraft | null>(null);
  const [quotationEmailSentConfirmation, setQuotationEmailSentConfirmation] = useState<string | null>(null);
  const [quotationFileApproval, setQuotationFileApproval] = useState<DocumentApprovalState | null>(null);
  const [quotationFileApprovalCode, setQuotationFileApprovalCode] = useState('');
  const [filedSignedQuotationPdf, setFiledSignedQuotationPdf] = useState<FiledSignedQuotationPdf | null>(null);

  const resetQuotationOutputArtifacts = () => {
    setOutputSigningLink(null);
    setQuotationEmailPreview(null);
    setQuotationEmailComposerDraft(null);
    setQuotationEmailSentConfirmation(null);
    setQuotationFileApproval(null);
    setQuotationFileApprovalCode('');
    setFiledSignedQuotationPdf(null);
  };

  const [createForm, setCreateForm] = useState({
    quotationNumber: '',
    quoteDate: new Date().toISOString().slice(0, 10),
    entity: 'Linfinity Engineering Pty Ltd',
    signatoryEmail: '',
    clientName: '',
    clientCompany: '',
    clientAbn: '',
    clientAddress: '',
    clientMobile: '',
    clientEmail: '',
    projectAddress: '',
    suburb: '',
    postCode: '',
    council: '',
    projectScope: '',
    projectDescription: '',
    projectType: '',
    selectedExclusions: [] as string[],
    structuralFee: isImportedDraft ? '' : '0',
    civilFee: isImportedDraft ? '' : '0',
    soilReportFee: isImportedDraft ? '' : '0',
    hourlyRate: String(DEFAULT_QUOTATION_HOURLY_RATE),
    inspectionFee: String(DEFAULT_QUOTATION_INSPECTION_FEE),
    quoteValidityDays: '30',
    structuralDepositPercentage: '10',
    structuralStage2Percentage: '70',
    civilDepositPercentage: '10',
    civilStage2Percentage: '70',
    structuralPaymentMode: 'auto',
    structuralPaymentStages: [] as PaymentStageDraft[],
    civilPaymentMode: 'auto',
    civilPaymentStages: [] as PaymentStageDraft[],
    includeStructuralScope: isImportedDraft ? routeState.prefill?.includeStructuralScope === true : true,
    structuralScopeGroupKey: '',
    structuralScopeGroupLabel: '',
    structuralScopeItems: [] as string[],
    structuralScopeFootnote: '',
    includeCivilScope: isImportedDraft ? routeState.prefill?.includeCivilScope === true : false,
    civilScopeGroupKey: '',
    civilScopeGroupLabel: '',
    civilScopeItems: [] as string[],
    civilScopeFootnote: '',
    includeGeneralScope: false,
    generalScopeItems: [] as GeneralScopeItemDraft[],
  });
  const [useProjectAddressForClient, setUseProjectAddressForClient] = useState(false);

  const [acceptForm, setAcceptForm] = useState({
    quotationLookup: '',
    quotationId: '',
    quotationNumber: '',
    projectNumber: '',
    projectTitle: '',
    assignedEngineerEmails: [] as string[],
    createSharePointFolders: true,
  });

  const rememberSavedQuotationIdentity = (identity: WorkflowQuotationIdentity | null) => {
    if (!identity?.quotationId) {
      return;
    }

    setLoadedPendingQuotationDraft(identity);
    setAcceptForm((prev) => ({
      ...prev,
      quotationLookup: identity.quotationNumber || identity.quotationId || prev.quotationLookup,
      quotationId: identity.quotationId,
      quotationNumber: identity.quotationNumber,
    }));
  };

  const setCreate = (key: string, value: any) => {
    if (key === 'quotationNumber' && !String(value ?? '').trim()) {
      quotationNumberAutofillRef.current = false;
    }
    setCreateForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'civilFee') {
        const previousCivilFee = toFeeNumber(prev.civilFee);
        const nextCivilFee = toFeeNumber(value);
        if (nextCivilFee <= 0) {
          next.includeCivilScope = false;
        } else if (previousCivilFee <= 0) {
          next.includeCivilScope = true;
        }
      }
      return next;
    });
  };

  const handleProjectAddressSelected = async (place: GoogleAddressSuggestion) => {
    const fallbackStreetAddress = toTrimmedString(place.mainText) || toTrimmedString(place.description);

    try {
      const details = await fetchGooglePlaceAddressDetails(place.placeId);
      const streetAddress = toTrimmedString(details?.streetAddress) || fallbackStreetAddress;
      const suburb = toTrimmedString(details?.suburb);
      const postCode = normalizePostCode(details?.postCode);

      setCreateForm((prev) => ({
        ...prev,
        projectAddress: streetAddress || prev.projectAddress,
        suburb: suburb || prev.suburb,
        postCode: postCode || prev.postCode,
      }));
      setSuburbLookupMessage(
        suburb
          ? `Street address${postCode ? ', suburb, and postcode' : ' and suburb'} filled from Google address result.`
          : 'Street address filled from Google address result. Check the suburb before creating the quotation.'
      );
    } catch {
      if (fallbackStreetAddress) {
        setCreateForm((prev) => ({
          ...prev,
          projectAddress: fallbackStreetAddress,
        }));
      }
      setSuburbLookupMessage('Address selected; Google details are unavailable. Check the suburb before creating the quotation.');
    }
  };

  const handleUseProjectAddressForClientChange = (checked: boolean) => {
    setUseProjectAddressForClient(checked);
    if (!checked) {
      return;
    }

    setCreateForm((prev) => ({
      ...prev,
      clientAddress: buildClientAddressFromProjectFields(prev),
    }));
  };

  const currentCreateQuotationNumber = createForm.quotationNumber.trim();
  const isEditingCurrentPendingQuotationNumber = Boolean(
    loadedPendingQuotationDraft?.quotationNumber
    && normalizeLookupValue(loadedPendingQuotationDraft.quotationNumber) === normalizeLookupValue(currentCreateQuotationNumber)
  );
  const isReusingExistingQuotationFolder = Boolean(
    quotationNumberStatus?.isDuplicate
    && quotationNumberStatus.existingQuotationFolder?.folderPath
    && normalizeLookupValue(selectedExistingQuotationFolderPath)
      === normalizeLookupValue(quotationNumberStatus.existingQuotationFolder.folderPath)
  );
  const hasBlockingQuotationNumberDuplicate = Boolean(
    quotationNumberStatus?.isDuplicate
    && !isEditingCurrentPendingQuotationNumber
    && !isReusingExistingQuotationFolder
  );
  const isEditingLoadedPendingQuotation = Boolean(loadedPendingQuotationDraft?.quotationId);
  const quotationNumberHelpMessage = quotationNumberStatusBusy
    ? 'Checking quotation number...'
    : isReusingExistingQuotationFolder
      ? `New quotation scope will use SharePoint folder ${quotationNumberStatus?.existingQuotationFolder?.folderName}.`
    : isEditingCurrentPendingQuotationNumber
      ? `Editing quotation ${loadedPendingQuotationDraft?.quotationNumber || currentCreateQuotationNumber}. Saving will update the existing row.`
      : quotationNumberStatus?.message;

  const formatCreatePhoneField = (key: 'clientMobile') => {
    setCreateForm((prev) => ({
      ...prev,
      [key]: normalizePhoneForStorage(prev[key]) || '',
    }));
  };

  const setAccept = (key: string, value: any) => {
    setAcceptForm((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (!useProjectAddressForClient) {
      return;
    }

    setCreateForm((prev) => {
      const nextClientAddress = buildClientAddressFromProjectFields(prev);
      return nextClientAddress === prev.clientAddress
        ? prev
        : { ...prev, clientAddress: nextClientAddress };
    });
  }, [
    useProjectAddressForClient,
    createForm.projectAddress,
    createForm.suburb,
    createForm.postCode,
  ]);

  useEffect(() => {
    const state = (location.state || {}) as QuotationWorkflowPrefillState;
    if (!state.prefill || state.amendQuotation || state.workflowQuotationDraft && !isWorkflowDraft) {
      return;
    }

    const nextEntity = toTrimmedString(state.prefill.entity);
    const nextQuotationNumber = toTrimmedString(state.prefill.quotationNumber);
    const nextClientName = toTrimmedString(state.prefill.clientName);
    const nextClientCompany = toTrimmedString(state.prefill.clientCompany);
    const nextClientAddress = toTrimmedString(state.prefill.clientAddress);
    const nextClientMobile = toTrimmedString(state.prefill.clientMobile);
    const nextClientEmail = toTrimmedString(state.prefill.clientEmail);
    const nextProjectAddress = toTrimmedString(state.prefill.projectAddress);
    const nextSuburb = toTrimmedString(state.prefill.suburb);
    const nextPostCode = normalizePostCode(state.prefill.postCode);
    const nextCouncil = toTrimmedString(state.prefill.council);
    const nextProjectScope = toTrimmedString(state.prefill.projectScope);
    const nextProjectType = toTrimmedString(state.prefill.projectType);
    const nextProjectDescription = toTrimmedString(state.prefill.projectDescription);

    if (nextQuotationNumber && !isOutlookDraft) {
      quotationNumberAutofillRef.current = true;
    }

    setCreateForm((prev) => ({
      ...prev,
      ...(isWorkflowDraft ? { quoteDate: toTrimmedString(state.prefill.quoteDate) || prev.quoteDate,
        quoteValidityDays: String(state.prefill.quoteValidityDays || prev.quoteValidityDays) } : {}),
      quotationNumber: isOutlookDraft ? prev.quotationNumber : nextQuotationNumber || prev.quotationNumber,
      entity: nextEntity || prev.entity,
      clientName: nextClientName || prev.clientName,
      clientCompany: nextClientCompany || prev.clientCompany,
      clientAddress: nextClientAddress || prev.clientAddress,
      clientMobile: nextClientMobile || prev.clientMobile,
      clientEmail: nextClientEmail || prev.clientEmail,
      projectAddress: nextProjectAddress || prev.projectAddress,
      suburb: nextSuburb || prev.suburb,
      postCode: nextPostCode || prev.postCode,
      council: nextCouncil || prev.council,
      projectScope: isOutlookDraft ? '' : nextProjectScope || prev.projectScope,
      projectType: isOutlookDraft ? '' : nextProjectType || prev.projectType,
      projectDescription: nextProjectDescription || prev.projectDescription,
    }));
    setPrefillNotice(
      (isOutlookDraft ? 'Unverified Outlook draft. Review client, scope, all fees and standard rates before creating. Nothing has been created or sent. Hourly and inspection rates are existing configured defaults, not email-derived.' : toTrimmedString(state.prefill.successMessage))
      || 'Loaded a quotation handoff draft. Review and complete the remaining quotation fields before creating anything.'
    );
    setError(null);

    window.setTimeout(() => {
      createDraftPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (acceptReviewPdfUrl) {
        URL.revokeObjectURL(acceptReviewPdfUrl);
      }
    };
  }, [acceptReviewPdfUrl]);

  const loadQuotationTemplateDefinition = async (activeGuard?: { current: boolean }) => {
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.getQuotationTemplateDefinition();
      if (activeGuard && !activeGuard.current) return;

      const templateDefinition = response?.definition || DEFAULT_QUOTATION_TEMPLATE_DEFINITION;
      setQuotationTemplateDefinition(templateDefinition);

      const dbBackedExclusions = response?.source === 'db';
      const options = buildQuotationExclusionOptions(
        templateDefinition.exclusionOptions?.length > 0
          ? templateDefinition.exclusionOptions.map((label: string) => ({
              Label: label,
              Source: dbBackedExclusions ? 'template' : 'fallback',
              IsSystem: !dbBackedExclusions,
            }))
          : [...DEFAULT_QUOTATION_EXCLUSION_OPTIONS]
      );
      setQuotationExclusionOptions((prev) => buildQuotationExclusionOptions([
        ...options,
        ...prev.filter((option) => option.Source === 'local'),
      ]));
      setQuotationExclusionMessage(
        response?.source === 'fallback'
          ? 'Using built-in quotation template defaults right now.'
          : ''
      );

      if (!exclusionSelectionInitializedRef.current) {
        exclusionSelectionInitializedRef.current = true;
        setCreate('selectedExclusions', options.map((option) => option.Label));
      }
    } catch {
      if (activeGuard && !activeGuard.current) return;

      const fallbackOptions = buildQuotationExclusionOptions([...DEFAULT_QUOTATION_EXCLUSION_OPTIONS]);
      setQuotationTemplateDefinition(DEFAULT_QUOTATION_TEMPLATE_DEFINITION);
      setQuotationExclusionOptions((prev) => buildQuotationExclusionOptions([
        ...fallbackOptions,
        ...prev.filter((option) => option.Source === 'local'),
      ]));
      setQuotationExclusionMessage('Using built-in quotation template defaults right now. DB template fields are unavailable.');

      if (!exclusionSelectionInitializedRef.current) {
        exclusionSelectionInitializedRef.current = true;
        setCreate('selectedExclusions', fallbackOptions.map((option) => option.Label));
      }
    }
  };

  const loadScopeDetailLibrary = async (activeGuard?: { current: boolean }) => {
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.getQuotationScopeDetailLibrary();
      if (activeGuard && !activeGuard.current) return;

      const normalizedLibrary = normalizeScopeDetailLibraryResponse(response, quotationTemplateDefinition);
      setScopeDetailLibrary(normalizedLibrary);
      setScopeDetailMessage(
        normalizedLibrary.source === 'fallback'
          ? 'Using built-in detailed scope groups right now.'
          : ''
      );
    } catch {
      if (activeGuard && !activeGuard.current) return;
      setScopeDetailLibrary(buildFallbackScopeDetailLibrary(quotationTemplateDefinition));
      setScopeDetailMessage('Using built-in detailed scope groups right now. DB scope detail items are unavailable.');
    }
  };

  const loadEngineerOptions = async (activeGuard?: { current: boolean }) => {
    try {
      setEngineerOptionsBusy(true);
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.getWorkflowEngineerOptions();
      if (activeGuard && !activeGuard.current) return;

      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      const normalized = rows
        .map((row: any) => {
          const email = String(row?.email || row?.Email || '').trim().toLowerCase();
          if (!email) return null;
          return {
            userId: Number.isFinite(Number(row?.userId ?? row?.UserId)) ? Number(row?.userId ?? row?.UserId) : null,
            email,
            displayName: String(row?.displayName || row?.DisplayName || email).trim() || email,
            company: String(row?.company || row?.Company || '').trim() || null,
            role: String(row?.role || row?.Role || '').trim() || null,
            jobTitle: String(row?.jobTitle || row?.JobTitle || '').trim() || null,
            sourceTable: String(row?.sourceTable || row?.SourceTable || '').trim() || null,
          } as WorkflowEngineerOption;
        })
        .filter((row: WorkflowEngineerOption | null): row is WorkflowEngineerOption => Boolean(row));

      setAvailableEngineerOptions(normalized);
      setEngineerOptionsMessage(normalized.length > 0 ? '' : 'No active company engineers were found in Users/UserList.');
    } catch {
      if (activeGuard && !activeGuard.current) return;
      setAvailableEngineerOptions([]);
      setEngineerOptionsMessage('Company engineer options are unavailable right now.');
    } finally {
      if (!activeGuard || activeGuard.current) {
        setEngineerOptionsBusy(false);
      }
    }
  };

  const loadPaymentStageRefLibrary = async (activeGuard?: { current: boolean }) => {
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.getQuotationPaymentStageRefLibrary();
      if (activeGuard && !activeGuard.current) return;

      const disciplines = Array.isArray(response?.disciplines)
        ? response.disciplines
        : Array.isArray(response?.data?.disciplines)
          ? response.data.disciplines
          : [];
      setPaymentStageRefLibrary({
        source: response?.source || response?.data?.source || 'db',
        disciplines,
      });
      setPaymentStageRefMessage(
        (response?.source || response?.data?.source) === 'fallback'
          ? 'Using built-in payment reference options right now.'
          : ''
      );
    } catch {
      if (activeGuard && !activeGuard.current) return;
      setPaymentStageRefLibrary(DEFAULT_PAYMENT_STAGE_REFERENCE_LIBRARY);
      setPaymentStageRefMessage('Using built-in payment reference options right now. DB payment reference options are unavailable.');
    }
  };

  useEffect(() => {
    const activeGuard = { current: true };
    void loadQuotationTemplateDefinition(activeGuard);
    void loadEngineerOptions(activeGuard);
    void loadPaymentStageRefLibrary(activeGuard);
    return () => {
      activeGuard.current = false;
    };
  }, []);

  useEffect(() => {
    const activeGuard = { current: true };
    void loadScopeDetailLibrary(activeGuard);
    return () => {
      activeGuard.current = false;
    };
  }, [quotationTemplateDefinition]);

  useEffect(() => {
    setCreateForm((prev) => {
      const nextProfile = resolveQuotationSignatoryProfile({
        entity: prev.entity,
        preferredEmail: prev.signatoryEmail,
        currentUserEmail,
        options: availableEngineerOptions,
      });

      return nextProfile.email && nextProfile.email !== prev.signatoryEmail
        ? { ...prev, signatoryEmail: nextProfile.email }
        : prev;
    });
  }, [availableEngineerOptions, currentUserEmail, createForm.entity]);

  useEffect(() => {
    const suburb = createForm.suburb.trim();
    if (suburb.length < 2) {
      setSuburbOptions([]);
      return;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        const matches = await searchSuburbOptions(suburb, 8);
        if (!active) return;
        setSuburbOptions(matches);

        const exactMatch = matches.find((row: SuburbLookupRow) => normalizeSuburb(row.Suburb) === normalizeSuburb(suburb));
        if (exactMatch) {
          setCreateForm((prev) => ({
            ...prev,
            suburb: exactMatch.Suburb || prev.suburb,
            postCode: exactMatch.PostCode !== null && exactMatch.PostCode !== undefined
              ? String(exactMatch.PostCode)
              : prev.postCode,
            council: exactMatch.Council || prev.council,
          }));
          setSuburbLookupMessage(`Postcode${exactMatch.Council ? ' and council' : ''} filled from suburb lookup.`);
        }
      } catch {
        if (!active) return;
        setSuburbOptions([]);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [createForm.suburb]);

  useEffect(() => {
    let active = true;
    setPaymentScheduleBusy(true);

    const timer = window.setTimeout(async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const schedule = await azureSqlService.getQuotationPaymentSchedule({
          quotationNumber: createForm.quotationNumber.trim(),
          structuralFee: createForm.structuralFee,
          civilFee: createForm.civilFee,
          soilReportFee: createForm.soilReportFee,
          structuralDepositPercentage: createForm.structuralDepositPercentage,
          structuralStage2Percentage: createForm.structuralStage2Percentage,
          civilDepositPercentage: createForm.civilDepositPercentage,
          civilStage2Percentage: createForm.civilStage2Percentage,
        });
        if (!active) return;

        setPaymentSchedule(decoratePaymentScheduleWithReferenceLabels(schedule, paymentStageRefLibrary));
        setPaymentScheduleMessage(
          schedule?.splitRule
            ? `API payment split rule: ${schedule.splitRule}.`
            : 'Payment split loaded from API.'
        );
      } catch {
        if (!active) return;
        setPaymentSchedule(null);
        setPaymentScheduleMessage('Payment split API is unavailable right now.');
      } finally {
        if (active) {
          setPaymentScheduleBusy(false);
        }
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [
    createForm.quotationNumber,
    createForm.structuralFee,
    createForm.civilFee,
    createForm.soilReportFee,
    createForm.structuralDepositPercentage,
    createForm.structuralStage2Percentage,
    createForm.civilDepositPercentage,
    createForm.civilStage2Percentage,
    paymentStageRefLibrary,
  ]);

  useEffect(() => {
    if (!paymentSchedule?.componentSchedules?.length) {
      return;
    }

    const structuralAuto = paymentSchedule.componentSchedules.find((schedule) => schedule.key === 'structural');
    const civilAuto = paymentSchedule.componentSchedules.find((schedule) => schedule.key === 'civil');

    setCreateForm((prev) => {
      let changed = false;
      const next = { ...prev };

      if (structuralAuto && (prev.structuralPaymentMode === 'auto' || prev.structuralPaymentStages.length === 0)) {
        next.structuralPaymentStages = createStageDraftsFromSchedule(
          structuralAuto,
          getPaymentStageReferenceOptionsForComponent('structural'),
        );
        changed = true;
      }

      if (civilAuto && (prev.civilPaymentMode === 'auto' || prev.civilPaymentStages.length === 0)) {
        next.civilPaymentStages = createStageDraftsFromSchedule(
          civilAuto,
          getPaymentStageReferenceOptionsForComponent('civil'),
        );
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [paymentSchedule]);

  useEffect(() => {
    setCreateForm((prev) => {
      let changed = false;
      const next = { ...prev };

      if (prev.structuralPaymentMode === 'manual' && prev.structuralPaymentStages.length > 0) {
        const normalizedStructuralStages = normalizeManualPaymentStageAmounts(
          prev.structuralPaymentStages,
          toFeeNumber(prev.structuralFee),
        );
        if (!arePaymentStageDraftsEqual(prev.structuralPaymentStages, normalizedStructuralStages)) {
          next.structuralPaymentStages = normalizedStructuralStages;
          changed = true;
        }
      }

      if (prev.civilPaymentMode === 'manual' && prev.civilPaymentStages.length > 0) {
        const normalizedCivilStages = normalizeManualPaymentStageAmounts(
          prev.civilPaymentStages,
          toFeeNumber(prev.civilFee),
        );
        if (!arePaymentStageDraftsEqual(prev.civilPaymentStages, normalizedCivilStages)) {
          next.civilPaymentStages = normalizedCivilStages;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [
    createForm.structuralFee,
    createForm.civilFee,
    createForm.structuralPaymentMode,
    createForm.civilPaymentMode,
  ]);

  useEffect(() => {
    let active = true;
    setQuotationNumberStatusBusy(true);
    setQuotationNumberStatus(null);
    setSelectedExistingQuotationFolderPath('');

    const timer = window.setTimeout(async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const status = await azureSqlService.getWorkflowQuotationNumberStatus(createForm.quotationNumber.trim());
        if (!active) return;

        setQuotationNumberStatus(status || null);

        const recommendedNumber = String(
          status?.recommendedQuotationNumber
          || status?.nextAvailableQuotationNumber
          || ''
        ).trim();

        if (!createForm.quotationNumber.trim() && recommendedNumber && !quotationNumberAutofillRef.current) {
          quotationNumberAutofillRef.current = true;
          setCreateForm((prev) => (
            prev.quotationNumber.trim()
              ? prev
              : { ...prev, quotationNumber: recommendedNumber }
          ));
        }
      } catch {
        if (!active) return;
        setQuotationNumberStatus({
          message: 'Quotation number check is unavailable right now.',
        });
      } finally {
        if (active) {
          setQuotationNumberStatusBusy(false);
        }
      }
    }, createForm.quotationNumber.trim() ? 250 : 0);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [createForm.quotationNumber]);

  useEffect(() => {
    const lookup = acceptForm.quotationLookup.trim();
    if (lookup.length < 2) {
      setQuotationLookupBusy(false);
      setPendingQuotationSuggestions([]);
      if (!lookup) {
        setSelectedPendingQuotation(null);
        setQuotationLookupMessage('');
        setAcceptForm((prev) => (
          prev.quotationId || prev.quotationNumber
            ? { ...prev, quotationId: '', quotationNumber: '' }
            : prev
        ));
      }
      return;
    }

    let active = true;
    setQuotationLookupBusy(true);

    const timer = window.setTimeout(async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const matches = await azureSqlService.searchPendingQuotations(lookup, 8);
        if (!active) return;

        setPendingQuotationSuggestions(matches);
        const exactMatch = matches.find((row: PendingQuotationCandidate) => matchesPendingQuotationExactly(row, lookup)) ?? null;

        if (exactMatch) {
          setSelectedPendingQuotation((prev) => (isSamePendingQuotation(prev, exactMatch) ? prev : exactMatch));
          setAcceptForm((prev) => {
            const nextQuotationId = exactMatch.QuotationId !== null && exactMatch.QuotationId !== undefined
              ? String(exactMatch.QuotationId)
              : '';
            const nextQuotationNumber = exactMatch.QuotationNumber ? String(exactMatch.QuotationNumber) : '';
            const nextProjectTitle = prev.projectTitle.trim() ? prev.projectTitle : buildPendingQuotationProjectTitle(exactMatch);

            if (
              prev.quotationId === nextQuotationId
              && prev.quotationNumber === nextQuotationNumber
              && prev.projectTitle === nextProjectTitle
            ) {
              return prev;
            }

            return {
              ...prev,
              quotationId: nextQuotationId,
              quotationNumber: nextQuotationNumber,
              projectTitle: nextProjectTitle,
            };
          });
          setQuotationLookupMessage('Pending quotation matched. Review the quotation details before accepting.');
        } else {
          setSelectedPendingQuotation(null);
          setAcceptForm((prev) => (
            prev.quotationId || prev.quotationNumber
              ? { ...prev, quotationId: '', quotationNumber: '' }
              : prev
          ));
          setQuotationLookupMessage(
            matches.length > 0
              ? 'Choose one of the pending quotation suggestions to continue.'
              : 'No pending quotation matched that entry yet.'
          );
        }
      } catch {
        if (!active) return;
        setQuotationLookupMessage('Pending quotation lookup is unavailable right now.');
        setPendingQuotationSuggestions([]);
        setSelectedPendingQuotation(null);
      } finally {
        if (active) {
          setQuotationLookupBusy(false);
        }
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [acceptForm.quotationLookup]);

  useEffect(() => {
    const lookup = createForm.clientName.trim();
    const lookupKey = normalizeLookupValue(lookup);
    if (suppressClientLookupRef.current) {
      suppressClientLookupRef.current = false;
      setClientLookupBusy(false);
      return;
    }

    if (lookupKey && lookupKey === dismissedClientLookupQueryRef.current) {
      setClientLookupBusy(false);
      setClientMatches([]);
      setClientLookupMessage('');
      return;
    }

    if (lookup.length < 2) {
      setClientLookupBusy(false);
      setClientMatches([]);
      if (!lookup) {
        setClientLookupMessage('');
        dismissedClientLookupQueryRef.current = '';
        setDismissedClientLookupQuery('');
      }
      return;
    }

    let active = true;
    setClientLookupBusy(true);

    const timer = window.setTimeout(async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const matches = await azureSqlService.searchClients(lookup, 8);
        if (!active) return;
        if (lookupKey && lookupKey === dismissedClientLookupQueryRef.current) {
          setClientMatches([]);
          setClientLookupMessage('');
          return;
        }

        const normalized = Array.isArray(matches)
          ? matches.map((row: any) => ({
              ClientId: row?.ClientId ?? null,
              Name: String(row?.Name || '').trim() || null,
              Company: String(row?.Company || row?.ClientCompany || row?.CompanyName || '').trim() || null,
              Email: String(row?.Email || row?.ClientEmail || '').trim() || null,
              Phone: formatPhoneForDisplay(row?.Phone || row?.ClientMobile || row?.Mobile || '') || null,
              Address: String(row?.Address || row?.ClientAddress || '').trim() || null,
              Abn: String(row?.Abn || row?.ABN || row?.ClientAbn || '').trim() || null,
            })).filter((row: ClientLookupCandidate) => Boolean(row.Name || row.Company || row.Email || row.Phone))
          : [];

        setClientMatches(normalized);
        setClientLookupMessage(
          normalized.length > 0
            ? `Found ${normalized.length} matching client${normalized.length === 1 ? '' : 's'}. Pick one to fill the details.`
            : 'No matching clients found yet.'
        );
      } catch {
        if (!active) return;
        setClientMatches([]);
        setClientLookupMessage('Client lookup is unavailable right now.');
      } finally {
        if (active) {
          setClientLookupBusy(false);
        }
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [createForm.clientName, dismissedClientLookupQuery]);

  const handleQuotationLookupChange = (value: string) => {
    setAcceptForm((prev) => ({
      ...prev,
      quotationLookup: value,
      quotationId: '',
      quotationNumber: '',
    }));
    setSelectedPendingQuotation(null);
    setQuotationLookupMessage('');
  };

  const applyClientMatch = (match: ClientLookupCandidate) => {
    suppressClientLookupRef.current = true;
    dismissedClientLookupQueryRef.current = '';
    setDismissedClientLookupQuery('');
    setUseProjectAddressForClient(false);
    setCreateForm((prev) => ({
      ...prev,
      clientName: match.Name || prev.clientName,
      clientCompany: match.Company || prev.clientCompany,
      clientEmail: match.Email || prev.clientEmail,
      clientMobile: formatPhoneForDisplay(match.Phone) || prev.clientMobile,
      clientAddress: match.Address || prev.clientAddress,
      clientAbn: match.Abn || prev.clientAbn,
    }));
    setClientMatches([]);
    setClientLookupBusy(false);
    setClientLookupMessage(`Picked ${match.Name || 'the client'} from the client list.`);
  };

  const dismissClientMatches = () => {
    const lookupKey = normalizeLookupValue(createForm.clientName);
    dismissedClientLookupQueryRef.current = lookupKey;
    setDismissedClientLookupQuery(lookupKey);
    setClientMatches([]);
    setClientLookupBusy(false);
    setClientLookupMessage('');
  };

  const hydrateSuburbLookup = async (rawSuburb: string) => {
    const suburb = rawSuburb.trim();
    if (suburb.length < 2) {
      setSuburbLookupMessage('');
      return;
    }

    try {
      const matches = await searchSuburbOptions(suburb, 8);
      setSuburbOptions(matches);

      const exactMatch = matches.find((row: SuburbLookupRow) => normalizeSuburb(row.Suburb) === normalizeSuburb(suburb));
      if (!exactMatch) {
        setSuburbLookupMessage('No exact suburb match found yet. Keep typing or pick from the list.');
        return;
      }

      setCreateForm((prev) => ({
        ...prev,
        suburb: exactMatch.Suburb || prev.suburb,
        postCode: exactMatch.PostCode !== null && exactMatch.PostCode !== undefined
          ? String(exactMatch.PostCode)
          : prev.postCode,
        council: exactMatch.Council || prev.council,
      }));
      setSuburbLookupMessage(`Postcode${exactMatch.Council ? ' and council' : ''} filled from suburb lookup.`);
    } catch {
      setSuburbLookupMessage('Suburb lookup is unavailable right now.');
    }
  };

  const hydratePostCodeLookup = async (rawPostCode: string) => {
    const postCode = normalizePostCode(rawPostCode);
    if (postCode.length < 3) {
      setSuburbLookupMessage('');
      return;
    }

    try {
      const matches = await searchSuburbOptionsByPostCode(postCode, 20);
      setSuburbOptions(matches);

      if (matches.length === 0) {
        setSuburbLookupMessage('No suburb match found for that postcode yet.');
        return;
      }

      const exactSuburbMatch = matches.find(
        (row: SuburbLookupRow) => normalizeSuburb(row.Suburb) === normalizeSuburb(createForm.suburb)
      );
      const selectedMatch = exactSuburbMatch || (matches.length === 1 ? matches[0] : null);

      if (!selectedMatch) {
        setCreateForm((prev) => ({
          ...prev,
          postCode,
        }));
        setSuburbLookupMessage('Multiple suburbs match that postcode. Pick the suburb from the list.');
        return;
      }

      setCreateForm((prev) => ({
        ...prev,
        suburb: selectedMatch.Suburb || prev.suburb,
        postCode: selectedMatch.PostCode !== null && selectedMatch.PostCode !== undefined
          ? String(selectedMatch.PostCode)
          : postCode,
        council: selectedMatch.Council || prev.council,
      }));
      setSuburbLookupMessage(`Suburb${selectedMatch.Council ? ', postcode, and council' : ' and postcode'} filled from lookup.`);
    } catch {
      setSuburbLookupMessage('Suburb lookup is unavailable right now.');
    }
  };

  const getSelectedExclusionsForOutput = () => {
    const selected = new Set(dedupeQuotationExclusions(createForm.selectedExclusions).map((label) => label.toLowerCase()));
    return orderQuotationExclusions([
      ...quotationExclusionOptions.map((option) => option.Label),
      ...createForm.selectedExclusions,
    ], exclusionOrder).filter((label) => selected.has(label.toLowerCase()));
  };

  const toggleExclusionSelection = (label: string) => {
    const normalized = normalizeQuotationExclusionLabel(label);
    const targetKey = normalized.toLowerCase();

    setCreateForm((prev) => {
      const current = dedupeQuotationExclusions(prev.selectedExclusions || []);
      const exists = current.some((item) => item.toLowerCase() === targetKey);
      return {
        ...prev,
        selectedExclusions: exists
          ? current.filter((item) => item.toLowerCase() !== targetKey)
          : dedupeQuotationExclusions([...current, normalized]),
      };
    });
  };

  const selectAllExclusions = () => {
    setCreate('selectedExclusions', dedupeQuotationExclusions(quotationExclusionOptions.map((option) => option.Label)));
  };

  const clearAllExclusions = () => {
    setCreate('selectedExclusions', []);
  };

  const canDeleteQuotationExclusionOption = (option: QuotationExclusionOption) => (
    option.Source === 'template'
    || option.Source === 'db'
    || option.Source === 'local-db'
    || (option.Source === undefined && option.IsSystem === false)
  );

  const handleDeleteQuotationExclusionOption = async (option: QuotationExclusionOption) => {
    const normalized = normalizeQuotationExclusionLabel(option.Label);
    if (!normalized) {
      return;
    }

    const confirmed = window.confirm(`Delete this reusable exclusion from the database?\n\n${normalized}`);
    if (!confirmed) {
      return;
    }

    setExclusionDeleteBusyLabel(normalized);
    setQuotationExclusionMessage('');

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.deleteQuotationExclusionOption(normalized);
      const targetKey = normalized.toLowerCase();

      setQuotationExclusionOptions((prev) => buildQuotationExclusionOptions(
        prev.filter((item) => normalizeQuotationExclusionLabel(item.Label).toLowerCase() !== targetKey)
      ));
      setCreateForm((prev) => ({
        ...prev,
        selectedExclusions: dedupeQuotationExclusions(
          (prev.selectedExclusions || []).filter((item: string) => normalizeQuotationExclusionLabel(item).toLowerCase() !== targetKey)
        ),
      }));
      setQuotationExclusionMessage(
        response?.deleted === false
          ? `Removed "${normalized}" from this list. It was already absent from the database template.`
          : `Deleted reusable exclusion "${normalized}" from the database.`
      );
    } catch (err: any) {
      setQuotationExclusionMessage(formatUserFacingError(err, 'Reusable exclusion could not be deleted.'));
    } finally {
      setExclusionDeleteBusyLabel(null);
    }
  };

  const handleAddAdHocExclusion = async () => {
    const normalized = normalizeQuotationExclusionLabel(adHocExclusion);
    if (!normalized) {
      setQuotationExclusionMessage('Enter an exclusion line before adding it.');
      return;
    }

    setExclusionActionBusy(true);
    try {
      let createdOption: QuotationExclusionOption | null = null;

      if (saveAdHocExclusion) {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const response = await azureSqlService.createQuotationExclusionOption(normalized);
        createdOption = {
          QuotationExclusionOptionId: response?.QuotationExclusionOptionId ?? response?.quotationExclusionOptionId ?? null,
          Label: normalizeQuotationExclusionLabel(response?.Label ?? normalized),
          SortOrder: response?.SortOrder ?? quotationExclusionOptions.length * 10 + 10,
          IsActive: response?.IsActive ?? true,
          IsSystem: response?.IsSystem ?? false,
          Source: response?.Source ?? 'template',
        };
        await loadQuotationTemplateDefinition();
      }

      setQuotationExclusionOptions((prev) => buildQuotationExclusionOptions([
        ...prev,
        createdOption ?? {
          Label: normalized,
          SortOrder: prev.length * 10 + 10,
          IsActive: true,
          IsSystem: false,
          Source: 'local',
        },
      ]));

      setCreateForm((prev) => ({
        ...prev,
        selectedExclusions: dedupeQuotationExclusions([...(prev.selectedExclusions || []), normalized]),
      }));
      setAdHocExclusion('');
      setQuotationExclusionMessage(
        saveAdHocExclusion
          ? 'Ad-hoc exclusion added and saved for future reuse.'
          : 'Ad-hoc exclusion added to this quotation.'
      );
    } catch (err: any) {
      setQuotationExclusionMessage(formatUserFacingError(err, 'Ad-hoc exclusion could not be saved.'));
    } finally {
      setExclusionActionBusy(false);
    }
  };

  const getPaymentStageReferenceOptionsForComponent = (componentKey: 'structural' | 'civil') => (
    getPaymentStageReferenceOptions(paymentStageRefLibrary, componentKey === 'structural' ? 'Structural' : 'Civil')
  );

  const getSeedPaymentStageDraftsForComponent = (componentKey: 'structural' | 'civil') => {
    const autoSchedule = paymentSchedule?.componentSchedules?.find((schedule) => schedule.key === componentKey);
    if (autoSchedule) {
      return createStageDraftsFromSchedule(autoSchedule, getPaymentStageReferenceOptionsForComponent(componentKey));
    }

    const fee = componentKey === 'structural'
      ? toFeeNumber(createForm.structuralFee)
      : toFeeNumber(createForm.civilFee);
    const label = componentKey === 'structural' ? 'Structural' : 'Civil';
    const fallbackSchedule = buildFallbackPaymentComponentSchedule(
      componentKey,
      label,
      fee,
      createForm.quotationNumber.trim() || 'DRAFT',
      componentKey === 'structural' ? createForm.structuralDepositPercentage : createForm.civilDepositPercentage,
      componentKey === 'structural' ? createForm.structuralStage2Percentage : createForm.civilStage2Percentage,
    );
    const decorated = decoratePaymentComponentScheduleWithReferenceLabels(
      fallbackSchedule,
      paymentStageRefLibrary,
      createForm.quotationNumber.trim() || 'DRAFT',
    );
    return createStageDraftsFromSchedule(decorated, getPaymentStageReferenceOptionsForComponent(componentKey));
  };

  const updatePaymentStageDrafts = (
    componentKey: 'structural' | 'civil',
    updater: (drafts: PaymentStageDraft[]) => PaymentStageDraft[],
  ) => {
    const fieldKey = componentKey === 'structural' ? 'structuralPaymentStages' : 'civilPaymentStages';
    setCreateForm((prev) => {
      const nextDrafts = normalizeStageDrafts(updater([...prev[fieldKey]]));
      return {
        ...prev,
        [fieldKey]: nextDrafts,
      };
    });
  };

  const setPaymentScheduleMode = (componentKey: 'structural' | 'civil', mode: 'auto' | 'manual') => {
    const modeFieldKey = componentKey === 'structural' ? 'structuralPaymentMode' : 'civilPaymentMode';
    const stagesFieldKey = componentKey === 'structural' ? 'structuralPaymentStages' : 'civilPaymentStages';

    setCreateForm((prev) => {
      const next = { ...prev };
      next[modeFieldKey] = mode;

      if (mode === 'manual' && prev[stagesFieldKey].length === 0) {
        next[stagesFieldKey] = getSeedPaymentStageDraftsForComponent(componentKey);
      } else if (mode === 'auto') {
        next[stagesFieldKey] = getSeedPaymentStageDraftsForComponent(componentKey);
      }

      return next;
    });
  };

  const addManualPaymentStage = (componentKey: 'structural' | 'civil') => {
    updatePaymentStageDrafts(componentKey, (drafts) => {
      if (drafts.some((draft) => draft.stageKey === 'stage3')) {
        return drafts;
      }

      const options = getPaymentStageReferenceOptionsForComponent(componentKey);
      const defaultOption = getDefaultPaymentStageReferenceOption(options, 2) || getDefaultPaymentStageReferenceOption(options, 1) || getDefaultPaymentStageReferenceOption(options, 0);
      const finalIndex = drafts.findIndex((draft) => draft.stageKey === 'final');
      const stage3Draft: PaymentStageDraft = {
        stageKey: 'stage3',
        label: getPaymentStageLabel('stage3'),
        amount: '',
        triggerOptionId: defaultOption?.QuotationPaymentStageRefOptionId ?? null,
        triggerLabel: defaultOption?.OptionLabel || '',
      };

      if (finalIndex >= 0) {
        const next = [...drafts];
        next.splice(finalIndex, 0, stage3Draft);
        return next;
      }

      return [...drafts, stage3Draft];
    });
  };

  const removeManualPaymentStage = (componentKey: 'structural' | 'civil') => {
    updatePaymentStageDrafts(componentKey, (drafts) => drafts.filter((draft) => draft.stageKey !== 'stage3'));
  };

  const setManualPaymentStageAmount = (
    componentKey: 'structural' | 'civil',
    stageKey: PaymentStageKey,
    amount: string,
  ) => {
    updatePaymentStageDrafts(componentKey, (drafts) => (
      drafts.map((draft) => (draft.stageKey === stageKey ? { ...draft, amount } : draft))
    ));
  };

  const setManualPaymentStageReference = (
    componentKey: 'structural' | 'civil',
    stageKey: PaymentStageKey,
    triggerOptionIdValue: string,
  ) => {
    const options = getPaymentStageReferenceOptionsForComponent(componentKey);
    const selectedOption = options.find((option) => {
      const optionValue = option.QuotationPaymentStageRefOptionId !== null
        ? String(option.QuotationPaymentStageRefOptionId)
        : `label:${option.OptionLabel}`;
      return optionValue === triggerOptionIdValue;
    }) || null;

    updatePaymentStageDrafts(componentKey, (drafts) => (
      drafts.map((draft) => (draft.stageKey === stageKey ? {
        ...draft,
        triggerOptionId: selectedOption?.QuotationPaymentStageRefOptionId ?? null,
        triggerLabel: selectedOption?.OptionLabel || '',
      } : draft))
    ));
  };

  const inferredDetailedScopeSections = getQuotationScopeCatalogueSelectionFromTemplate(quotationTemplateDefinition, {
    projectScope: createForm.projectScope || '',
    projectType: createForm.projectType || '',
    projectDescription: createForm.projectDescription || '',
  });
  const groupedScopeSections = partitionScopeSections(inferredDetailedScopeSections);
  groupedScopeSections.general = withQuotationAdditionalScope(groupedScopeSections.general, quotationTemplateDefinition);
  const generalScopeTemplateSignature = groupedScopeSections.general
    .map((section) => `${section.key}:${section.title}:${section.bullets.join('|')}`)
    .join('||');
  const fallbackScopeDetailGroups = buildFallbackScopeDetailLibrary(quotationTemplateDefinition).groups;
  const combinedScopeDetailGroups = [...scopeDetailLibrary.groups, ...fallbackScopeDetailGroups];
  const libraryGroupsByKey = new Map(
    [...fallbackScopeDetailGroups, ...scopeDetailLibrary.groups].map((group) => [group.groupKey, group]),
  );
  const projectTypeOptions = Array.from(new Set([
    ...quotationTemplateDefinition.projectTypeOptions,
    ...QUOTATION_PROJECT_TYPE_OPTIONS,
  ].filter(Boolean)));

  const buildDetailGroupOptions = (
    discipline: ScopeDisciplineKey,
    sections: QuotationScopeCatalogueSection[],
  ): ScopeDetailGroup[] => {
    const inferredGroups = sections.map((section) => (
      libraryGroupsByKey.get(section.key) || {
        discipline,
        groupKey: section.key,
        groupLabel: section.title,
        items: (section.bullets || []).map((bullet, index) => ({
          ScopeDetailItemId: null,
          StructuralCivil: discipline === 'structural' ? 'Structural' : 'Civil',
          DetailGroupKey: section.key,
          DetailGroupLabel: section.title,
          ScopeDetailItem: bullet,
          SortOrder: (index + 1) * 10,
          IsActive: true,
          IsSystem: true,
          Source: 'fallback',
        })),
      }
    ));
    const libraryGroups = combinedScopeDetailGroups.filter((group) => group.discipline === discipline);
    const seen = new Set<string>();

    return [...inferredGroups, ...libraryGroups].filter((group) => {
      const key = normalizeScopeGroupKey(group.groupKey) || normalizeScopeGroupKey(group.groupLabel);
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  useEffect(() => {
    setCreateForm((prev) => {
      const availableSections = isImportedDraft && !isEditingLoadedPendingQuotation
        ? groupedScopeSections.general.filter(section => section.key === ADDITIONAL_SCOPE_SECTION_KEY)
        : groupedScopeSections.general;
      const nextGeneralScopeItems = hydrateGeneralScopeDrafts(prev.generalScopeItems, availableSections);
      const isUnchanged = nextGeneralScopeItems.length === prev.generalScopeItems.length
        && nextGeneralScopeItems.every((item, index) => {
          const current = prev.generalScopeItems[index];
          return current
            && current.key === item.key
            && current.sectionKey === item.sectionKey
            && current.sectionTitle === item.sectionTitle
            && current.text === item.text
            && current.included === item.included;
        });

      if (isUnchanged) {
        return prev;
      }

      return {
        ...prev,
        generalScopeItems: nextGeneralScopeItems,
      };
    });
  }, [generalScopeTemplateSignature]);

  const structuralScopeGroupOptions = buildDetailGroupOptions('structural', groupedScopeSections.structural);

  const civilScopeGroupOptions = buildDetailGroupOptions('civil', groupedScopeSections.civil);

  if (isEditingLoadedPendingQuotation) {
    for (const discipline of ['structural', 'civil'] as const) {
      const groups = discipline === 'structural' ? structuralScopeGroupOptions : civilScopeGroupOptions;
      const groupKey = createForm[`${discipline}ScopeGroupKey`];
      if (groupKey && !groups.some(group => group.groupKey === groupKey)) {
        groups.push({ discipline, groupKey, groupLabel: createForm[`${discipline}ScopeGroupLabel`] || groupKey,
          items: createForm[`${discipline}ScopeItems`].map(text => ({ DetailGroupKey: groupKey,
            DetailGroupLabel: createForm[`${discipline}ScopeGroupLabel`] || groupKey, ScopeDetailItem: text })) });
      }
    }
  }

  const scopeItemOptions = (discipline: ScopeDisciplineKey): QuotationScopeItemOption[] => {
    const seen = new Set<string>();
    return combinedScopeDetailGroups.filter(group => group.discipline === discipline).flatMap(group => (
      group.items.filter(item => isBooleanFlagEnabled(item.IsActive, true)).map(item => ({
        text: item.ScopeDetailItem, groupLabel: group.groupLabel,
      }))
    )).filter(option => {
      const key = normalizeScopeDetailText(option.text).toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key); return true;
    });
  };
  const structuralScopeItemOptions = scopeItemOptions('structural');
  const civilScopeItemOptions = scopeItemOptions('civil');

  useEffect(() => {
    if (isImportedDraft && !isEditingLoadedPendingQuotation) return;
    const nextGroup = structuralScopeGroupOptions.find((group) => group.groupKey === createForm.structuralScopeGroupKey)
      || structuralScopeGroupOptions[0]
      || null;

    setCreateForm((prev) => {
      const currentKey = prev.structuralScopeGroupKey || '';
      const currentItems = Array.isArray(prev.structuralScopeItems) ? prev.structuralScopeItems : [];

      if (!nextGroup) {
        if (isEditingLoadedPendingQuotation && currentItems.length > 0) {
          return prev;
        }
        if (!currentKey && currentItems.length === 0 && !prev.structuralScopeGroupLabel) {
          return prev;
        }
        return {
          ...prev,
          structuralScopeGroupKey: '',
          structuralScopeGroupLabel: '',
          structuralScopeItems: [],
        };
      }

      if (isEditingLoadedPendingQuotation && currentItems.length > 0) {
        return prev;
      }

      if (currentKey === nextGroup.groupKey && currentItems.length > 0 && prev.structuralScopeGroupLabel === nextGroup.groupLabel) {
        return prev;
      }

      return {
        ...prev,
        structuralScopeGroupKey: nextGroup.groupKey,
        structuralScopeGroupLabel: nextGroup.groupLabel,
        structuralScopeItems: dedupeScopeItems(nextGroup.items.map((item) => item.ScopeDetailItem)),
      };
    });
  }, [structuralScopeGroupOptions, createForm.structuralScopeGroupKey, isEditingLoadedPendingQuotation]);

  useEffect(() => {
    if (isImportedDraft && !isEditingLoadedPendingQuotation) return;
    const nextGroup = civilScopeGroupOptions.find((group) => group.groupKey === createForm.civilScopeGroupKey)
      || civilScopeGroupOptions[0]
      || null;

    setCreateForm((prev) => {
      const currentKey = prev.civilScopeGroupKey || '';
      const currentItems = Array.isArray(prev.civilScopeItems) ? prev.civilScopeItems : [];

      if (!nextGroup) {
        if (isEditingLoadedPendingQuotation && currentItems.length > 0) {
          return prev;
        }
        if (!currentKey && currentItems.length === 0 && !prev.civilScopeGroupLabel) {
          return prev;
        }
        return {
          ...prev,
          civilScopeGroupKey: '',
          civilScopeGroupLabel: '',
          civilScopeItems: [],
        };
      }

      if (isEditingLoadedPendingQuotation && currentItems.length > 0) {
        return prev;
      }

      if (currentKey === nextGroup.groupKey && currentItems.length > 0 && prev.civilScopeGroupLabel === nextGroup.groupLabel) {
        return prev;
      }

      return {
        ...prev,
        civilScopeGroupKey: nextGroup.groupKey,
        civilScopeGroupLabel: nextGroup.groupLabel,
        civilScopeItems: dedupeScopeItems(nextGroup.items.map((item) => item.ScopeDetailItem)),
      };
    });
  }, [civilScopeGroupOptions, createForm.civilScopeGroupKey, isEditingLoadedPendingQuotation]);

  const handleScopeGroupChange = (discipline: ScopeDisciplineKey, groupKey: string) => {
    const groups = discipline === 'structural' ? structuralScopeGroupOptions : civilScopeGroupOptions;
    const group = groups.find((item) => item.groupKey === groupKey) || null;

    if (discipline === 'structural') {
      setCreateForm((prev) => ({
        ...prev,
        structuralScopeGroupKey: group?.groupKey || '',
        structuralScopeGroupLabel: group?.groupLabel || '',
        structuralScopeItems: dedupeScopeItems(group?.items.map((item) => item.ScopeDetailItem) || []),
      }));
      return;
    }

    setCreateForm((prev) => ({
      ...prev,
      civilScopeGroupKey: group?.groupKey || '',
      civilScopeGroupLabel: group?.groupLabel || '',
      civilScopeItems: dedupeScopeItems(group?.items.map((item) => item.ScopeDetailItem) || []),
    }));
  };

  const handleScopeItemChange = (discipline: ScopeDisciplineKey, index: number, value: string) => {
    setCreateForm((prev) => {
      const key = discipline === 'structural' ? 'structuralScopeItems' : 'civilScopeItems';
      const nextItems = [...(prev[key] || [])];
      nextItems[index] = value;
      return {
        ...prev,
        [key]: nextItems,
      };
    });
  };

  const handleRemoveScopeItem = (discipline: ScopeDisciplineKey, index: number) => {
    setCreateForm((prev) => {
      const key = discipline === 'structural' ? 'structuralScopeItems' : 'civilScopeItems';
      return {
        ...prev,
        [key]: (prev[key] || []).filter((_: string, itemIndex: number) => itemIndex !== index),
      };
    });
  };

  const handleScopeItemDragStart = (
    discipline: ScopeDisciplineKey,
    index: number,
    event: React.DragEvent<HTMLButtonElement>,
  ) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `${discipline}:${index}`);
    setScopeItemDrag({ discipline, fromIndex: index, overIndex: index });
  };

  const handleScopeItemDragOver = (
    discipline: ScopeDisciplineKey,
    index: number,
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setScopeItemDrag((prev) => (
      prev && prev.discipline === discipline
        ? { ...prev, overIndex: index }
        : prev
    ));
  };

  const handleScopeItemDrop = (
    discipline: ScopeDisciplineKey,
    toIndex: number,
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    const rawTransfer = event.dataTransfer.getData('text/plain');
    const [transferDiscipline, transferIndex] = rawTransfer.split(':');
    const fromIndex = Number(transferIndex);
    const normalizedFromIndex = scopeItemDrag?.discipline === discipline
      ? scopeItemDrag.fromIndex
      : fromIndex;

    if (transferDiscipline !== discipline || !Number.isInteger(normalizedFromIndex)) {
      setScopeItemDrag(null);
      return;
    }

    setCreateForm((prev) => {
      const key = discipline === 'structural' ? 'structuralScopeItems' : 'civilScopeItems';
      const currentItems = prev[key] || [];
      return {
        ...prev,
        [key]: reorderArray(currentItems, normalizedFromIndex, toIndex),
      };
    });
    setScopeItemDrag(null);
  };

  const handleScopeItemDragEnd = () => {
    setScopeItemDrag(null);
  };

  const handleAppendBlankScopeItem = (discipline: ScopeDisciplineKey) => {
    setCreateForm((prev) => {
      const key = discipline === 'structural' ? 'structuralScopeItems' : 'civilScopeItems';
      return {
        ...prev,
        [key]: [...(prev[key] || []), ''],
      };
    });
  };

  const handleAddScopeLibraryRow = async (discipline: ScopeDisciplineKey) => {
    const extraRow = discipline === 'structural' ? structuralExtraScopeRow : civilExtraScopeRow;
    const saveForReuse = discipline === 'structural' ? saveStructuralExtraScopeRow : saveCivilExtraScopeRow;
    const normalizedRow = normalizeScopeDetailText(extraRow);
    if (!normalizedRow) {
      setScopeDetailMessage('Enter a detailed scope row before adding it.');
      return;
    }

    const groupKey = discipline === 'structural' ? createForm.structuralScopeGroupKey : createForm.civilScopeGroupKey;
    const groupLabel = discipline === 'structural' ? createForm.structuralScopeGroupLabel : createForm.civilScopeGroupLabel;
    if (!groupKey || !groupLabel) {
      setScopeDetailMessage('Select a detail group before adding reusable scope rows.');
      return;
    }

    const itemsKey = discipline === 'structural' ? 'structuralScopeItems' : 'civilScopeItems';
    setCreateForm((prev) => ({
      ...prev,
      [itemsKey]: dedupeScopeItems([...(prev[itemsKey] || []), normalizedRow]),
    }));

    if (discipline === 'structural') {
      setStructuralExtraScopeRow('');
    } else {
      setCivilExtraScopeRow('');
    }

    if (!saveForReuse) {
      setScopeDetailMessage('Added a one-off detailed scope row to this quotation.');
      return;
    }

    setScopeDetailActionBusy(discipline);
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.createQuotationScopeDetailItem({
        discipline,
        detailGroupKey: groupKey,
        detailGroupLabel: groupLabel,
        itemText: normalizedRow,
      });
      await loadScopeDetailLibrary();
      setScopeDetailMessage('Detailed scope row added and saved for future reuse.');
    } catch (err: any) {
      setScopeDetailMessage(formatUserFacingError(err, 'Detailed scope row could not be saved for reuse.'));
    } finally {
      setScopeDetailActionBusy(null);
    }
  };

  const getPaymentStageRefOptions = (componentKey: 'structural' | 'civil') => (
    paymentStageRefLibrary.disciplines.find(
      (discipline) => discipline.discipline === (componentKey === 'structural' ? 'Structural' : 'Civil')
    )?.options || []
  );

  const buildDisplayedPaymentComponentSchedule = (
    componentKey: 'structural' | 'civil',
    fee: number,
    quotationNumber: string,
    autoSchedule: PaymentComponentSchedule,
  ): PaymentComponentSchedule => {
    const drafts = componentKey === 'structural'
      ? createForm.structuralPaymentStages
      : createForm.civilPaymentStages;
    const paymentMode = componentKey === 'structural'
      ? createForm.structuralPaymentMode
      : createForm.civilPaymentMode;

    if (paymentMode === 'manual') {
      return buildManualPaymentComponentSchedule(componentKey, autoSchedule.label, fee, quotationNumber, drafts);
    }

    return mergeStageTriggers(autoSchedule, drafts);
  };

  const handlePaymentModeChange = (componentKey: 'structural' | 'civil', nextMode: 'auto' | 'manual') => {
    const sourceSchedule = autoPaymentComponentSchedules.find((schedule) => schedule.key === componentKey) || null;
    setCreateForm((prev) => {
      const modeKey = componentKey === 'structural' ? 'structuralPaymentMode' : 'civilPaymentMode';
      const stagesKey = componentKey === 'structural' ? 'structuralPaymentStages' : 'civilPaymentStages';
      const currentDrafts = prev[stagesKey];
      return {
        ...prev,
        [modeKey]: nextMode,
        [stagesKey]: currentDrafts.length > 0
          ? currentDrafts
          : sourceSchedule
            ? createStageDraftsFromSchedule(sourceSchedule, getPaymentStageReferenceOptionsForComponent(componentKey))
            : prev[stagesKey],
      };
    });
  };

  const handlePaymentStageDraftChange = (
    componentKey: 'structural' | 'civil',
    stageKey: PaymentStageKey,
    field: 'amount' | 'triggerOptionId' | 'triggerLabel',
    value: string | number | null,
  ) => {
    const sourceSchedule = paymentComponentSchedules.find((schedule) => schedule.key === componentKey)
      || autoPaymentComponentSchedules.find((schedule) => schedule.key === componentKey)
      || null;
    setCreateForm((prev) => {
      const stagesKey = componentKey === 'structural' ? 'structuralPaymentStages' : 'civilPaymentStages';
      const refOptions = getPaymentStageRefOptions(componentKey);
      const baseStages = (prev[stagesKey] || []).length > 0
        ? prev[stagesKey]
        : sourceSchedule
          ? createStageDraftsFromSchedule(sourceSchedule, refOptions)
          : [];
      const nextStages = baseStages.map((stage) => {
        if (stage.stageKey !== stageKey) return stage;
        if (field === 'triggerOptionId') {
          const selectedOption = refOptions.find((option) => (
            String(option.QuotationPaymentStageRefOptionId ?? `label:${option.OptionLabel}`) === String(value)
          ));
          return {
            ...stage,
            triggerOptionId: selectedOption?.QuotationPaymentStageRefOptionId ?? null,
            triggerLabel: selectedOption?.OptionLabel || '',
          };
        }
        if (field === 'triggerLabel') {
          return {
            ...stage,
            triggerOptionId: null,
            triggerLabel: String(value || ''),
          };
        }
        return {
          ...stage,
          [field]: value,
        };
      });
      const fee = componentKey === 'structural'
        ? toFeeNumber(prev.structuralFee)
        : toFeeNumber(prev.civilFee);
      return {
        ...prev,
        [stagesKey]: field === 'amount'
          ? normalizeManualPaymentStageAmounts(nextStages, fee)
          : nextStages,
      };
    });
  };

  const handleAddFourthStage = (componentKey: 'structural' | 'civil') => {
    const sourceSchedule = autoPaymentComponentSchedules.find((schedule) => schedule.key === componentKey) || null;
    setCreateForm((prev) => {
      const stagesKey = componentKey === 'structural' ? 'structuralPaymentStages' : 'civilPaymentStages';
      const refOptions = getPaymentStageReferenceOptionsForComponent(componentKey);
      const currentDrafts = prev[stagesKey].length > 0
        ? prev[stagesKey]
        : sourceSchedule
          ? createStageDraftsFromSchedule(sourceSchedule, refOptions)
          : [];
      if (currentDrafts.some((stage) => stage.stageKey === 'stage3')) {
        return prev;
      }
      const withoutFinal = currentDrafts.filter((stage) => stage.stageKey !== 'final');
      const finalStage = currentDrafts.find((stage) => stage.stageKey === 'final') || {
        stageKey: 'final' as const,
        label: getPaymentStageLabel('final'),
        amount: '',
        triggerOptionId: null,
        triggerLabel: '',
      };
      const stage3DefaultOption = getPaymentStageReferenceOptionForStage(refOptions, 'stage3', 2);
      const finalDefaultOption = getPaymentStageReferenceOptionForStage(refOptions, 'final', withoutFinal.length + 1);
      const legacyFinalDefaultOption = getDefaultPaymentStageReferenceOption(refOptions, withoutFinal.length);
      const shouldMoveFinalToLastRef = !finalStage.triggerLabel
        || stageDraftMatchesReferenceOption(finalStage, legacyFinalDefaultOption)
        || stageDraftMatchesReferenceOption(finalStage, stage3DefaultOption);
      const fee = componentKey === 'structural'
        ? toFeeNumber(prev.structuralFee)
        : toFeeNumber(prev.civilFee);
      const nextStages = normalizeManualPaymentStageAmounts([
        ...withoutFinal,
        {
          stageKey: 'stage3',
          label: getPaymentStageLabel('stage3'),
          amount: '',
          triggerOptionId: stage3DefaultOption?.QuotationPaymentStageRefOptionId ?? null,
          triggerLabel: stage3DefaultOption?.OptionLabel || '',
        },
        {
          ...finalStage,
          triggerOptionId: shouldMoveFinalToLastRef
            ? finalDefaultOption?.QuotationPaymentStageRefOptionId ?? null
            : finalStage.triggerOptionId ?? null,
          triggerLabel: shouldMoveFinalToLastRef
            ? finalDefaultOption?.OptionLabel || ''
            : finalStage.triggerLabel || '',
        },
      ], fee);
      return {
        ...prev,
        [stagesKey]: nextStages,
      };
    });
  };

  const handleRemoveFourthStage = (componentKey: 'structural' | 'civil') => {
    setCreateForm((prev) => {
      const stagesKey = componentKey === 'structural' ? 'structuralPaymentStages' : 'civilPaymentStages';
      const fee = componentKey === 'structural'
        ? toFeeNumber(prev.structuralFee)
        : toFeeNumber(prev.civilFee);
      return {
        ...prev,
        [stagesKey]: normalizeManualPaymentStageAmounts(
          (prev[stagesKey] || []).filter((stage) => stage.stageKey !== 'stage3'),
          fee,
        ),
      };
    });
  };

  const buildQuotationPreviewPayload = (signOnlineUrl?: string | null): QuotationPdfData => {
    const safeStructuralFee = toFeeNumber(createForm.structuralFee);
    const safeCivilFee = toFeeNumber(createForm.civilFee);
    const safeSoilReportFee = toFeeNumber(createForm.soilReportFee);
    const safeHourlyRate = toFeeNumber(createForm.hourlyRate) || DEFAULT_QUOTATION_HOURLY_RATE;
    const safeInspectionFee = toFeeNumber(createForm.inspectionFee) || DEFAULT_QUOTATION_INSPECTION_FEE;
    const sender = getCompanyPaymentProfile(createForm.entity);
    const previewPaymentSchedule = paymentComponentSchedules
      .flatMap((schedule) => schedule.stages.filter((stage) => Number(stage.amount) > 0));
    const selectedStructuralSection = buildScopeSectionFromSelection(
      'structural',
      createForm.structuralScopeGroupKey,
      createForm.structuralScopeGroupLabel,
      createForm.structuralScopeItems,
    );
    const selectedCivilSection = buildScopeSectionFromSelection(
      'civil',
      createForm.civilScopeGroupKey,
      createForm.civilScopeGroupLabel,
      createForm.civilScopeItems,
    );

    return {
      quotationNumber: createForm.quotationNumber || 'DRAFT',
      issueDate: createForm.quoteDate,
      drawingReceivedDate: createForm.quoteDate,
      entity: sender.entity,
      clientName: createForm.clientName || 'Client name',
      companyName: createForm.clientCompany || '',
      abn: createForm.clientAbn || '',
      clientAddress: createForm.clientAddress || '',
      clientEmail: createForm.clientEmail || '',
      clientMobile: normalizePhoneForStorage(createForm.clientMobile) || '',
      projectScope: createForm.projectScope || '',
      projectType: createForm.projectType || '',
      projectDescription: createForm.projectDescription || '',
      projectStreetAddress: createForm.projectAddress || '',
      projectSuburb: createForm.suburb || '',
      postCode: createForm.postCode || '',
      council: createForm.council || '',
      senderDisplayName: sender.senderDisplayName,
      senderAddress: sender.senderAddress,
      senderMobile: sender.senderMobile,
      senderEmail: sender.senderEmail,
      senderPayId: sender.senderPayId || '',
      senderBsb: sender.senderBsb,
      senderAccountNumber: sender.senderAccountNumber,
      senderAccountName: sender.senderAccountName,
      signOnlineUrl: toTrimmedString(signOnlineUrl) || undefined,
      signatoryEmail: createSignatoryProfile.email,
      signatoryName: createSignatoryProfile.displayName,
      signatoryCompany: createSignatoryProfile.company,
      signatoryPosition: createSignatoryProfile.position,
      scopeSections: [
        ...(createForm.includeStructuralScope
          ? (selectedStructuralSection ? [selectedStructuralSection] : groupedScopeSections.structural)
          : []),
        ...(createForm.includeCivilScope
          ? (selectedCivilSection ? [selectedCivilSection] : groupedScopeSections.civil)
          : []),
        ...(createForm.includeGeneralScope
          ? buildGeneralScopeSectionsFromDrafts(createForm.generalScopeItems, groupedScopeSections.general)
          : []),
      ].filter((section): section is QuotationScopeCatalogueSection => Boolean(section)),
      structuralScopeFootnote: createForm.includeStructuralScope ? createForm.structuralScopeFootnote.trim() : '',
      civilScopeFootnote: createForm.includeCivilScope ? createForm.civilScopeFootnote.trim() : '',
      termsConditions: quotationTemplateDefinition.termsConditions || [],
      exclusions: getSelectedExclusionsForOutput(),
      structuralFee: safeStructuralFee,
      civilFee: safeCivilFee,
      soilReportFee: safeSoilReportFee,
      hourlyRate: safeHourlyRate,
      inspectionFee: safeInspectionFee,
      quoteValidityDays: Number(createForm.quoteValidityDays),
      projectManagementFee: 0,
      quotedFeeTotal: safeStructuralFee + safeCivilFee,
      totalQuote: safeStructuralFee + safeCivilFee + safeSoilReportFee,
      paymentSchedule: previewPaymentSchedule,
    };
  };

  const buildPendingQuotationPreviewPayload = (quotation: PendingQuotationCandidate): QuotationPdfData => {
    const entityName = toTrimmedString(quotation.Entity) || createForm.entity || 'Linfinity Engineering Pty Ltd';
    const sender = getCompanyPaymentProfile(entityName);
    const signatoryProfile = resolveQuotationSignatoryProfile({
      entity: entityName,
      preferredEmail: toTrimmedString(quotation.CertifiedBy || quotation.BroughtBy),
      currentUserEmail,
      options: availableEngineerOptions,
    });
    const structuralFee = getQuotationStructuralFeeTotal(quotation);
    const civilFee = toFeeNumber(quotation.CivilFee);
    const soilReportFee = toFeeNumber(quotation.SoilReportFee);
    const hourlyRate = toFeeNumber(quotation.HourlyRate) || DEFAULT_QUOTATION_HOURLY_RATE;
    const inspectionFee = toFeeNumber(quotation.InspectionFee) || DEFAULT_QUOTATION_INSPECTION_FEE;
    const paymentSchedule = [
      ...parseQuotationPaymentStages(quotation.StructuralPaymentScheduleJson),
      ...parseQuotationPaymentStages(quotation.CivilPaymentScheduleJson),
    ];
    const inferredScopeSections = getQuotationScopeCatalogueSelectionFromTemplate(quotationTemplateDefinition, {
      projectScope: quotation.ProjectScope || '',
      projectType: quotation.ProjectType || '',
      projectDescription: quotation.ProjectDescription || '',
    });
    const fallbackScopeSections = partitionScopeSections(inferredScopeSections);
    const includeGeneralScope = isBooleanFlagEnabled(quotation.IncludeGeneralScope, false);
    const storedTotal = getQuotationDisplayAmount(quotation);

    return {
      quotationNumber: toTrimmedString(quotation.QuotationNumber || quotation.QuotationId) || 'DRAFT',
      issueDate: normalizeQuotationPdfDate(quotation.Date || quotation.IssueDate),
      drawingReceivedDate: normalizeQuotationPdfDate(quotation.Date || quotation.IssueDate),
      entity: sender.entity,
      clientName: toTrimmedString(quotation.Client || quotation.ClientName) || 'Client name',
      companyName: toTrimmedString(quotation.ClientCompany || quotation.CompanyName),
      abn: toTrimmedString(quotation.ABN),
      clientAddress: toTrimmedString(quotation.ClientAddress),
      clientEmail: toTrimmedString(quotation.ClientEmail),
      clientMobile: formatPhoneForDisplay(quotation.ClientMobile),
      projectScope: toTrimmedString(quotation.ProjectScope),
      projectType: toTrimmedString(quotation.ProjectType),
      projectDescription: toTrimmedString(quotation.ProjectDescription),
      projectStreetAddress: toTrimmedString(quotation.Address),
      projectSuburb: toTrimmedString(quotation.Suburb),
      postCode: normalizePostCode(quotation.PostCode),
      council: toTrimmedString(quotation.Council),
      senderDisplayName: toTrimmedString(quotation.SenderDisplayName) || sender.senderDisplayName,
      senderAddress: toTrimmedString(quotation.SenderAddress) || sender.senderAddress,
      senderMobile: toTrimmedString(quotation.SenderMobile) || sender.senderMobile,
      senderEmail: toTrimmedString(quotation.SenderEmail) || sender.senderEmail,
      senderPayId: toTrimmedString(quotation.SenderPayId) || sender.senderPayId || '',
      senderBsb: toTrimmedString(quotation.SenderBsb) || sender.senderBsb,
      senderAccountNumber: toTrimmedString(quotation.SenderAccountNumber) || sender.senderAccountNumber,
      senderAccountName: toTrimmedString(quotation.SenderAccountName) || sender.senderAccountName,
      signatoryEmail: signatoryProfile.email,
      signatoryName: signatoryProfile.displayName,
      signatoryCompany: signatoryProfile.company,
      signatoryPosition: signatoryProfile.position,
      scopeSections: buildStoredScopeSections(quotation, fallbackScopeSections, includeGeneralScope),
      structuralScopeFootnote: parseStringArrayValue(quotation.StructuralScopeItemsJson).length > 0 ? toTrimmedString(quotation.ScopeFootnoteS) : '',
      civilScopeFootnote: parseStringArrayValue(quotation.CivilScopeItemsJson).length > 0 ? toTrimmedString(quotation.ScopeFootnoteC) : '',
      termsConditions: quotationTemplateDefinition.termsConditions || [],
      exclusions: parseStringArrayValue(quotation.ExclusionsJson),
      structuralFee,
      civilFee,
      soilReportFee,
      hourlyRate,
      inspectionFee,
      quoteValidityDays: Number(quotation.QuoteValidityDays ?? 30),
      projectManagementFee: 0,
      quotedFeeTotal: structuralFee + civilFee,
      totalQuote: storedTotal > 0 ? storedTotal : structuralFee + civilFee + soilReportFee,
      paymentSchedule,
    };
  };

  const loadPendingQuotationIntoCreateDraft = (quotation: PendingQuotationCandidate) => {
    const structuralFee = getQuotationStructuralFeeTotal(quotation);
    const civilFee = toFeeNumber(quotation.CivilFee);
    const soilReportFee = toFeeNumber(quotation.SoilReportFee);
    const hourlyRate = toFeeNumber(quotation.HourlyRate) || DEFAULT_QUOTATION_HOURLY_RATE;
    const inspectionFee = toFeeNumber(quotation.InspectionFee) || DEFAULT_QUOTATION_INSPECTION_FEE;
    const entityName = toTrimmedString(quotation.Entity) || 'Linfinity Engineering Pty Ltd';
    const normalizedQuotationNumber = toTrimmedString(quotation.QuotationNumber || quotation.QuotationId);
    const inferredScopeSections = getQuotationScopeCatalogueSelectionFromTemplate(quotationTemplateDefinition, {
      projectScope: quotation.ProjectScope || '',
      projectType: quotation.ProjectType || '',
      projectDescription: quotation.ProjectDescription || '',
    });
    const fallbackScopeSections = partitionScopeSections(inferredScopeSections);
    const structuralScopeItems = dedupeScopeItems(parseStringArrayValue(quotation.StructuralScopeItemsJson));
    const civilScopeItems = dedupeScopeItems(parseStringArrayValue(quotation.CivilScopeItemsJson));
    const storedGeneralScopeItems = parseGeneralScopeItemDrafts(quotation.GeneralScopeItemsJson);
    const generalScopeItems = hydrateGeneralScopeDrafts(
      storedGeneralScopeItems,
      withQuotationAdditionalScope(fallbackScopeSections.general, quotationTemplateDefinition),
    );
    // Legacy quotations could enable inferred general scope before per-row JSON existed.
    // Preserve what that saved quotation already included, not newly available choices.
    if (!storedGeneralScopeItems.length && isBooleanFlagEnabled(quotation.IncludeGeneralScope, false)) {
      const legacyIncludedSections = new Set(fallbackScopeSections.general.map(section => section.key));
      generalScopeItems.forEach(item => { if (legacyIncludedSections.has(item.sectionKey)) item.included = true; });
    }
    const structuralStoredKey = toTrimmedString(quotation.StructuralScopeGroupKey);
    const structuralStoredLabel = toTrimmedString(quotation.StructuralScopeGroupLabel);
    const civilStoredKey = toTrimmedString(quotation.CivilScopeGroupKey);
    const civilStoredLabel = toTrimmedString(quotation.CivilScopeGroupLabel);
    const structuralFallbackSection = fallbackScopeSections.structural.find((section) => section.key === structuralStoredKey)
      || fallbackScopeSections.structural.find((section) => normalizeScopeGroupKey(section.title) === normalizeScopeGroupKey(structuralStoredLabel))
      || fallbackScopeSections.structural[0]
      || null;
    const civilFallbackSection = fallbackScopeSections.civil.find((section) => section.key === civilStoredKey)
      || fallbackScopeSections.civil.find((section) => normalizeScopeGroupKey(section.title) === normalizeScopeGroupKey(civilStoredLabel))
      || fallbackScopeSections.civil[0]
      || null;
    const hasStoredStructuralScope = Boolean(structuralStoredKey || structuralStoredLabel || structuralScopeItems.length > 0);
    const hasStoredCivilScope = Boolean(civilStoredKey || civilStoredLabel || civilScopeItems.length > 0);
    const includeGeneralScope = isBooleanFlagEnabled(quotation.IncludeGeneralScope, false);
    const signatoryProfile = resolveQuotationSignatoryProfile({
      entity: entityName,
      preferredEmail: toTrimmedString(quotation.CertifiedBy || quotation.BroughtBy),
      currentUserEmail,
      options: availableEngineerOptions,
    });
    const selectedExclusions = dedupeQuotationExclusions([
      ...parseStringArrayValue(quotation.ExclusionsJson),
      ...(
        (Array.isArray(quotation.ExclusionsJson) || toTrimmedString(quotation.ExclusionsJson).startsWith('['))
          ? []
          : (quotationExclusionOptions.length > 0
              ? quotationExclusionOptions.map((option) => option.Label)
              : DEFAULT_QUOTATION_EXCLUSION_OPTIONS)
      ),
    ]);
    setExclusionOrder(selectedExclusions);
    setQuotationExclusionOptions((prev) => buildQuotationExclusionOptions([
      ...prev,
      ...selectedExclusions.map((Label) => ({ Label, Source: 'local', IsSystem: false })),
    ]));
    const structuralPaymentMode = toTrimmedString(quotation.StructuralPaymentMode).toLowerCase() === 'manual' ? 'manual' : 'auto';
    const civilPaymentMode = toTrimmedString(quotation.CivilPaymentMode).toLowerCase() === 'manual' ? 'manual' : 'auto';
    const structuralPaymentStages = structuralPaymentMode === 'manual'
      ? buildPaymentStageDraftsFromStoredStages(parseQuotationPaymentStages(quotation.StructuralPaymentScheduleJson))
      : [];
    const civilPaymentStages = civilPaymentMode === 'manual'
      ? buildPaymentStageDraftsFromStoredStages(parseQuotationPaymentStages(quotation.CivilPaymentScheduleJson))
      : [];

    suppressClientLookupRef.current = true;
    exclusionSelectionInitializedRef.current = true;
    quotationNumberAutofillRef.current = true;
    setUseProjectAddressForClient(false);
    setLoadedPendingQuotationDraft({
      quotationId: toTrimmedString(quotation.QuotationId),
      quotationNumber: normalizedQuotationNumber,
    });
    setCreateForm((prev) => ({
      ...prev,
      quotationNumber: normalizedQuotationNumber || prev.quotationNumber,
      quoteDate: normalizeQuotationPdfDate(quotation.Date || quotation.IssueDate),
      entity: entityName,
      signatoryEmail: signatoryProfile.email,
      clientName: toTrimmedString(quotation.Client || quotation.ClientName),
      clientCompany: toTrimmedString(quotation.ClientCompany || quotation.CompanyName),
      clientAbn: toTrimmedString(quotation.ABN),
      clientAddress: toTrimmedString(quotation.ClientAddress),
      clientMobile: formatPhoneForDisplay(quotation.ClientMobile),
      clientEmail: toTrimmedString(quotation.ClientEmail),
      projectAddress: toTrimmedString(quotation.Address),
      suburb: toTrimmedString(quotation.Suburb),
      postCode: normalizePostCode(quotation.PostCode),
      council: toTrimmedString(quotation.Council),
      projectScope: toTrimmedString(quotation.ProjectScope),
      projectDescription: toTrimmedString(quotation.ProjectDescription),
      projectType: toTrimmedString(quotation.ProjectType),
      selectedExclusions,
      structuralFee: String(roundCurrency(structuralFee)),
      civilFee: String(roundCurrency(civilFee)),
      soilReportFee: String(roundCurrency(soilReportFee)),
      hourlyRate: String(roundCurrency(hourlyRate)),
      inspectionFee: String(roundCurrency(inspectionFee)),
      quoteValidityDays: String(quotation.QuoteValidityDays ?? 30),
      structuralPaymentMode,
      structuralPaymentStages,
      civilPaymentMode,
      civilPaymentStages,
      includeStructuralScope: hasStoredStructuralScope || structuralFee > 0,
      structuralScopeGroupKey: structuralStoredKey || structuralFallbackSection?.key || '',
      structuralScopeGroupLabel: structuralStoredLabel || structuralFallbackSection?.title || '',
      structuralScopeItems: structuralScopeItems.length > 0
        ? structuralScopeItems
        : dedupeScopeItems(structuralFallbackSection?.bullets || []),
      structuralScopeFootnote: toTrimmedString(quotation.ScopeFootnoteS),
      includeCivilScope: civilFee > 0,
      civilScopeGroupKey: civilStoredKey || civilFallbackSection?.key || '',
      civilScopeGroupLabel: civilStoredLabel || civilFallbackSection?.title || '',
      civilScopeItems: civilScopeItems.length > 0
        ? civilScopeItems
        : dedupeScopeItems(civilFallbackSection?.bullets || []),
      civilScopeFootnote: toTrimmedString(quotation.ScopeFootnoteC),
      includeGeneralScope,
      generalScopeItems,
    }));
    setAcceptForm((prev) => ({
      ...prev,
      quotationLookup: normalizedQuotationNumber || prev.quotationLookup,
      quotationId: toTrimmedString(quotation.QuotationId),
      quotationNumber: normalizedQuotationNumber,
    }));
    setSelectedPendingQuotation(quotation);
    resetQuotationOutputArtifacts();
    setQuotationLookupMessage(`Loaded quotation ${normalizedQuotationNumber || quotation.QuotationId || ''} into the draft editor for updates.`);
    setError(null);

    window.setTimeout(() => {
      createDraftPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  useEffect(() => {
    const state = (location.state || {}) as QuotationWorkflowPrefillState;
    const quotationId = toTrimmedString(state.amendQuotation?.quotationId);
    if (!quotationId) {
      return;
    }

    let active = true;
    setPrefillNotice(null);
    setError(null);

    const loadQuotationForAmendment = async () => {
      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const quotation = await azureSqlService.getQuotationById(quotationId, { useCache: false });
        if (!active) return;
        if (!quotation) {
          throw new Error(`Quotation ${state.amendQuotation?.quotationNumber || quotationId} was not found.`);
        }

        loadPendingQuotationIntoCreateDraft(quotation);
        setPrefillNotice(
          `Quotation ${toTrimmedString(quotation.QuotationNumber || state.amendQuotation?.quotationNumber || quotationId)} is loaded for amendment.`
        );
      } catch (err) {
        if (!active) return;
        setError(formatUserFacingError(err, 'Quotation could not be loaded for amendment.'));
      }
    };

    void loadQuotationForAmendment();

    return () => {
      active = false;
    };
  // Route state is intentionally consumed once per navigation entry.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const closeAcceptReview = () => {
    if (acceptReviewPdfUrl) {
      URL.revokeObjectURL(acceptReviewPdfUrl);
    }
    setAcceptReviewPdfUrl(null);
    setAcceptReviewQuotation(null);
    setAcceptReviewNotice(null);
    setAcceptReviewOpen(false);
  };

  const handleReturnReviewedQuotationToWorkflow = () => {
    if (acceptReviewQuotation) {
      loadPendingQuotationIntoCreateDraft(acceptReviewQuotation);
    }
    closeAcceptReview();
  };

  const handleEditSelectedPendingQuotation = () => {
    if (!selectedPendingQuotation) {
      setError('Select a pending quotation before editing the quotation row.');
      return;
    }

    loadPendingQuotationIntoCreateDraft(selectedPendingQuotation);
  };

  const resolvePendingQuotationForReview = async (): Promise<PendingQuotationCandidate> => {
    const lookup = acceptForm.quotationNumber.trim() || acceptForm.quotationLookup.trim() || acceptForm.quotationId.trim();
    if (!lookup) {
      throw new Error('Select a pending quotation before reviewing the PDF.');
    }

    const currentMatch = selectedPendingQuotation
      && (matchesPendingQuotationExactly(selectedPendingQuotation, lookup)
        || normalizeLookupValue(selectedPendingQuotation.QuotationId) === normalizeLookupValue(acceptForm.quotationId))
      ? selectedPendingQuotation
      : null;

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const matches = await azureSqlService.searchPendingQuotations(lookup, 8);
      const exactMatch = matches.find((row: PendingQuotationCandidate) => (
        matchesPendingQuotationExactly(row, lookup)
        || (
          acceptForm.quotationId
          && normalizeLookupValue(row.QuotationId) === normalizeLookupValue(acceptForm.quotationId)
        )
      )) ?? null;

      if (exactMatch) {
        return exactMatch;
      }
    } catch {
      // Fall back to the currently selected quotation so review can still proceed.
    }

    if (currentMatch) {
      return currentMatch;
    }

    throw new Error('The selected quotation could not be loaded for review. Try selecting it again.');
  };

  const handleOpenAcceptReview = async () => {
    setBusy('acceptReview');
    setError(null);
    setAcceptReviewNotice(null);

    try {
      const quotation = await resolvePendingQuotationForReview();
      const previewPayload = buildPendingQuotationPreviewPayload(quotation);
      const { pdfService } = await import('@/services/pdfService');
      const blob = await pdfService.generateQuotationPDFBlob(previewPayload);
      const nextUrl = URL.createObjectURL(blob);

      if (acceptReviewPdfUrl) {
        URL.revokeObjectURL(acceptReviewPdfUrl);
      }

      setAcceptReviewQuotation(quotation);
      setAcceptReviewPdfUrl(nextUrl);
      setAcceptReviewOpen(true);
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Quotation PDF review could not open.'));
    } finally {
      setBusy(null);
    }
  };

  const getMissingCreateFields = () => {
    const requiredFields = [
      { label: 'Quote Date', value: createForm.quoteDate },
      { label: 'Project Scope', value: createForm.projectScope },
      { label: 'Project Type', value: createForm.projectType },
      { label: 'Entity', value: createForm.entity },
      { label: 'Project Description', value: createForm.projectDescription },
      { label: 'Project Address', value: createForm.projectAddress },
      { label: 'Suburb', value: createForm.suburb },
      { label: 'Postcode', value: createForm.postCode },
      { label: 'Council', value: createForm.council },
      { label: 'Client Name', value: createForm.clientName },
      { label: 'Client Email', value: createForm.clientEmail },
      { label: 'Client Mobile', value: createForm.clientMobile },
      { label: 'Client Address', value: createForm.clientAddress },
      { label: 'Structural Fee', value: createForm.structuralFee },
      { label: 'Civil Fee', value: createForm.civilFee },
      { label: 'Soil Report Fee', value: createForm.soilReportFee },
    ];

    return requiredFields
      .filter((field) => String(field.value ?? '').trim() === '')
      .map((field) => field.label);
  };

  const handleCreateQuotation = async (e: React.FormEvent) => {
    e.preventDefault();
    const validityDays = Number(createForm.quoteValidityDays);
    if (!Number.isInteger(validityDays) || validityDays < 1 || validityDays > 2147483647) {
      setError('Quote validity period must be a positive whole number of days.');
      return;
    }

    const missingFields = getMissingCreateFields();
    if (missingFields.length > 0) {
      setError(`Complete all required fields: ${missingFields.join(', ')}.`);
      return;
    }
    if (hasBlockingQuotationNumberDuplicate) {
      const nextAvailable = quotationNumberStatus.nextAvailableQuotationNumber || quotationNumberStatus.recommendedQuotationNumber;
      setError(
        nextAvailable
          ? `Quotation number ${createForm.quotationNumber.trim()} is already used. Use ${nextAvailable} instead.`
          : `Quotation number ${createForm.quotationNumber.trim()} is already used.`
      );
      return;
    }

    setBusy('create');
    setError(null);
    setResult(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const sender = getCompanyPaymentProfile(createForm.entity);
      const quotationNumber = createForm.quotationNumber.trim();
      const payload = {
        ...createForm,
        reuseExistingQuotationFolder: isReusingExistingQuotationFolder,
        existingQuotationFolderPath: isReusingExistingQuotationFolder
          ? selectedExistingQuotationFolderPath
          : undefined,
        certifiedBy: createSignatoryProfile.email || undefined,
        selectedExclusions: getSelectedExclusionsForOutput(),
        structuralFee: createForm.structuralFee,
        civilFee: createForm.civilFee,
        soilReportFee: createForm.soilReportFee,
        hourlyRate: createForm.hourlyRate,
        inspectionFee: createForm.inspectionFee,
        quoteValidityDays: Number(createForm.quoteValidityDays),
        structuralDepositPercentage: createForm.structuralDepositPercentage,
        structuralStage2Percentage: createForm.structuralStage2Percentage,
        civilDepositPercentage: createForm.civilDepositPercentage,
        civilStage2Percentage: createForm.civilStage2Percentage,
        structuralPaymentMode: createForm.structuralPaymentMode,
        structuralPaymentStages: paymentComponentSchedules.find((schedule) => schedule.key === 'structural')?.stages || [],
        civilPaymentMode: createForm.civilPaymentMode,
        civilPaymentStages: paymentComponentSchedules.find((schedule) => schedule.key === 'civil')?.stages || [],
        structuralScopeGroupKey: createForm.includeStructuralScope ? createForm.structuralScopeGroupKey : '',
        structuralScopeGroupLabel: createForm.includeStructuralScope ? createForm.structuralScopeGroupLabel : '',
        structuralScopeItems: createForm.includeStructuralScope ? dedupeScopeItems(createForm.structuralScopeItems) : [],
        structuralScopeFootnote: createForm.includeStructuralScope ? createForm.structuralScopeFootnote.trim() : '',
        civilScopeGroupKey: createForm.includeCivilScope ? createForm.civilScopeGroupKey : '',
        civilScopeGroupLabel: createForm.includeCivilScope ? createForm.civilScopeGroupLabel : '',
        civilScopeItems: createForm.includeCivilScope ? dedupeScopeItems(createForm.civilScopeItems) : [],
        civilScopeFootnote: createForm.includeCivilScope ? createForm.civilScopeFootnote.trim() : '',
        includeGeneralScope: createForm.includeGeneralScope,
        generalScopeItems: createForm.generalScopeItems.map((item) => ({
          key: item.key,
          sectionKey: item.sectionKey,
          sectionTitle: item.sectionTitle,
          text: item.text,
          included: item.included,
        })),
      };
      const editingQuotationId = Number(loadedPendingQuotationDraft?.quotationId);

      if (Number.isFinite(editingQuotationId) && editingQuotationId > 0) {
        const structuralFee = toFeeNumber(createForm.structuralFee);
        const civilFee = toFeeNumber(createForm.civilFee);
        const soilReportFee = toFeeNumber(createForm.soilReportFee);
        const structuralScopeItems = createForm.includeStructuralScope ? dedupeScopeItems(createForm.structuralScopeItems) : [];
        const civilScopeItems = createForm.includeCivilScope ? dedupeScopeItems(createForm.civilScopeItems) : [];
        const generalScopeItems = createForm.generalScopeItems.map((item) => ({
          key: item.key,
          sectionKey: item.sectionKey,
          sectionTitle: item.sectionTitle,
          text: item.text,
          included: item.included,
        }));

        await azureSqlService.updateQuotation(Math.trunc(editingQuotationId), {
          acceptance: 'Pending',
          quotationNumber,
          date: createForm.quoteDate || null,
          broughtBy: currentUserEmail || null,
          certifiedBy: createSignatoryProfile.email || null,
          entity: createForm.entity.trim() || null,
          client: createForm.clientName.trim() || null,
          clientCompany: createForm.clientCompany.trim() || null,
          abn: createForm.clientAbn.trim() || null,
          clientAddress: createForm.clientAddress.trim() || null,
          clientMobile: normalizePhoneForStorage(createForm.clientMobile),
          clientEmail: createForm.clientEmail.trim() || null,
          address: createForm.projectAddress.trim() || null,
          suburb: createForm.suburb.trim() || null,
          postCode: createForm.postCode.trim() || null,
          council: createForm.council.trim() || null,
          projectScope: createForm.projectScope.trim() || null,
          projectDescription: createForm.projectDescription.trim() || null,
          projectType: createForm.projectType.trim() || null,
          exclusions: getSelectedExclusionsForOutput(),
          structuralFee,
          civilFee,
          soilReportFee,
          hourlyRate: toFeeNumber(createForm.hourlyRate),
          inspectionFee: toFeeNumber(createForm.inspectionFee),
          quoteValidityDays: Number(createForm.quoteValidityDays),
          structuralPaymentMode: createForm.structuralPaymentMode,
          structuralPaymentStages: payload.structuralPaymentStages,
          civilPaymentMode: createForm.civilPaymentMode,
          civilPaymentStages: payload.civilPaymentStages,
          structuralScopeGroupKey: createForm.includeStructuralScope ? createForm.structuralScopeGroupKey : null,
          structuralScopeGroupLabel: createForm.includeStructuralScope ? createForm.structuralScopeGroupLabel : null,
          structuralScopeItems,
          structuralScopeFootnote: createForm.includeStructuralScope ? createForm.structuralScopeFootnote.trim() : null,
          civilScopeGroupKey: createForm.includeCivilScope ? createForm.civilScopeGroupKey : null,
          civilScopeGroupLabel: createForm.includeCivilScope ? createForm.civilScopeGroupLabel : null,
          civilScopeItems,
          civilScopeFootnote: createForm.includeCivilScope ? createForm.civilScopeFootnote.trim() : null,
          includeGeneralScope: createForm.includeGeneralScope,
          generalScopeItems,
          subTotal: structuralFee + civilFee + soilReportFee,
          structural: structuralFee,
          senderDisplayName: sender.senderDisplayName,
          senderAddress: sender.senderAddress,
          senderMobile: sender.senderMobile,
          senderEmail: sender.senderEmail,
          senderPayId: sender.senderPayId || null,
          senderBsb: sender.senderBsb || null,
          senderAccountNumber: sender.senderAccountNumber || null,
          senderAccountName: sender.senderAccountName || null,
        });

        setLoadedPendingQuotationDraft({
          quotationId: String(Math.trunc(editingQuotationId)),
          quotationNumber,
        });
        resetQuotationOutputArtifacts();
        setAcceptForm((prev) => ({
          ...prev,
          quotationLookup: quotationNumber || prev.quotationLookup,
          quotationId: String(Math.trunc(editingQuotationId)),
          quotationNumber,
        }));
        setQuotationLookupMessage(`Quotation ${quotationNumber} updated and returned to Pending for client acceptance. Request a fresh approval/signing link before sending the amended PDF.`);
        setSelectedPendingQuotation((prev) => prev ? ({
          ...prev,
          Acceptance: 'Pending',
          Status: 'Pending',
          AcceptedDate: null,
          QuotationId: Math.trunc(editingQuotationId),
          QuotationNumber: quotationNumber,
          Date: createForm.quoteDate,
          Entity: createForm.entity,
          Client: createForm.clientName,
          ClientName: createForm.clientName,
          ClientCompany: createForm.clientCompany,
          ABN: createForm.clientAbn,
          ClientAddress: createForm.clientAddress,
          ClientMobile: normalizePhoneForStorage(createForm.clientMobile),
          ClientEmail: createForm.clientEmail,
          Address: createForm.projectAddress,
          Suburb: createForm.suburb,
          PostCode: createForm.postCode,
          Council: createForm.council,
          ProjectScope: createForm.projectScope,
          ProjectDescription: createForm.projectDescription,
          ProjectType: createForm.projectType,
          StructuralFee: structuralFee,
          CivilFee: civilFee,
          SoilReportFee: soilReportFee,
          HourlyRate: toFeeNumber(createForm.hourlyRate),
          InspectionFee: toFeeNumber(createForm.inspectionFee),
          QuoteValidityDays: Number(createForm.quoteValidityDays),
          QuotationAmount: structuralFee + civilFee + soilReportFee,
          ExclusionsJson: getSelectedExclusionsForOutput(),
          StructuralPaymentMode: createForm.structuralPaymentMode,
          StructuralPaymentScheduleJson: payload.structuralPaymentStages,
          CivilPaymentMode: createForm.civilPaymentMode,
          CivilPaymentScheduleJson: payload.civilPaymentStages,
          StructuralScopeGroupKey: createForm.includeStructuralScope ? createForm.structuralScopeGroupKey : null,
          StructuralScopeGroupLabel: createForm.includeStructuralScope ? createForm.structuralScopeGroupLabel : null,
          StructuralScopeItemsJson: structuralScopeItems,
          ScopeFootnoteS: createForm.includeStructuralScope ? createForm.structuralScopeFootnote : null,
          CivilScopeGroupKey: createForm.includeCivilScope ? createForm.civilScopeGroupKey : null,
          CivilScopeGroupLabel: createForm.includeCivilScope ? createForm.civilScopeGroupLabel : null,
          CivilScopeItemsJson: civilScopeItems,
          ScopeFootnoteC: createForm.includeCivilScope ? createForm.civilScopeFootnote : null,
          IncludeGeneralScope: createForm.includeGeneralScope,
          GeneralScopeItemsJson: generalScopeItems,
        }) : prev);
        setResult({
          success: true,
          data: {
            quotationId: Math.trunc(editingQuotationId),
            quotationNumber,
          },
          statusMessage: `Updated quotation ${quotationNumber}. Request a fresh approval/signing link before sending the amended PDF.`,
        });
      } else {
        const response = await azureSqlService.createWorkflowQuotation(payload);
        setResult(response);

        const createdIdentity = resolveWorkflowQuotationIdentity(response, payload.quotationNumber);
        const createdQuotationNumber = createdIdentity?.quotationNumber || payload.quotationNumber || '';
        if (createdQuotationNumber && createdQuotationNumber !== createForm.quotationNumber) {
          setCreate('quotationNumber', String(createdQuotationNumber));
        }
        rememberSavedQuotationIdentity(createdIdentity);
      }
    } catch (err: any) {
      setError(formatUserFacingError(
        err,
        loadedPendingQuotationDraft?.quotationId ? 'Quotation could not be updated.' : 'Quotation could not be created.'
      ));
    } finally {
      setBusy(null);
    }
  };

  const draftQuotationNumber = createForm.quotationNumber.trim() || 'DRAFT';
  const autoPaymentComponentSchedules = paymentSchedule?.componentSchedules?.length
    ? paymentSchedule.componentSchedules
    : [
        buildFallbackPaymentComponentSchedule(
          'structural',
          'Structural',
          toFeeNumber(createForm.structuralFee),
          draftQuotationNumber,
          createForm.structuralDepositPercentage,
          createForm.structuralStage2Percentage,
        ),
        buildFallbackPaymentComponentSchedule(
          'civil',
          'Civil',
          toFeeNumber(createForm.civilFee),
          draftQuotationNumber,
          createForm.civilDepositPercentage,
          createForm.civilStage2Percentage,
        ),
      ].filter((schedule) => schedule.fee > 0);
  const paymentComponentSchedules = autoPaymentComponentSchedules.map((schedule) => buildDisplayedPaymentComponentSchedule(
    schedule.key,
    schedule.fee,
    draftQuotationNumber,
    schedule,
  ));
  const orderedPaymentComponentSchedules = [
    ...paymentComponentSchedules.filter((schedule) => schedule.key === 'structural'),
    ...paymentComponentSchedules.filter((schedule) => schedule.key === 'civil'),
    ...paymentComponentSchedules.filter((schedule) => schedule.key !== 'structural' && schedule.key !== 'civil'),
  ];
  const scheduledTotal = paymentComponentSchedules.reduce((sum, schedule) => sum + schedule.fee, 0);
  const paymentSummaryText = `Total quoted fee: ${formatCurrency(toFeeNumber(createForm.structuralFee) + toFeeNumber(createForm.civilFee))}. Staged structural + civil fees: ${formatCurrency(scheduledTotal)}.`;
  const entityMatchedEngineerOptions = availableEngineerOptions.filter((option) => (
    normalizeCompanyName(option.company) === normalizeCompanyName(createForm.entity)
  ));
  const createSignatoryProfile = resolveQuotationSignatoryProfile({
    entity: createForm.entity,
    preferredEmail: createForm.signatoryEmail,
    currentUserEmail,
    options: availableEngineerOptions,
  });
  const savedWorkflowQuotationNumber = toTrimmedString(
    loadedPendingQuotationDraft?.quotationNumber
    || acceptForm.quotationNumber
    || result?.quotationNumber
    || result?.data?.quotationNumber
  );
  const currentDraftQuotationNumber = createForm.quotationNumber.trim();
  const matchesSavedQuotationNumber = !currentDraftQuotationNumber
    || !savedWorkflowQuotationNumber
    || currentDraftQuotationNumber === savedWorkflowQuotationNumber;
  const currentWorkflowQuotationId = matchesSavedQuotationNumber ? asPositiveInteger(
    loadedPendingQuotationDraft?.quotationId
    || acceptForm.quotationId
    || result?.quotationId
    || result?.data?.quotationId
  ) : null;
  const canSendQuotationEmail = Boolean(currentWorkflowQuotationId && createForm.clientEmail.trim());
  const activeOutputSigningLink = isActiveSigningLink(outputSigningLink, currentWorkflowQuotationId)
    ? outputSigningLink
    : null;
  const shouldAutoCcWd = quotationInvolvesWd([
    createForm.signatoryEmail,
    createSignatoryProfile.email,
    createSignatoryProfile.displayName,
  ]);
  const quotationEmailCcEmails = buildQuotationCcEmails(
    quotationEmailCcText,
    createForm.clientEmail,
    shouldAutoCcWd ? [WD_QUOTATION_CC_EMAIL] : [],
  );
  const ensureOutputSigningLink = async (quotationId = currentWorkflowQuotationId): Promise<QuotationSigningLinkResult | null> => {
    if (!quotationId) {
      return null;
    }
    if (isActiveSigningLink(outputSigningLink, quotationId)) {
      return outputSigningLink;
    }

    const { azureSqlService } = await import('@/services/azureSqlService.js');
    const issued = await azureSqlService.issueQuotationSigningLink({
      quotationId,
    });
    setOutputSigningLink(issued);
    return issued;
  };

  const handlePreviewQuotation = async () => {
    setBusy('preview');
    setError(null);

    try {
      const signUrl = (await ensureOutputSigningLink())?.signUrl || null;
      const { pdfService } = await import('@/services/pdfService');
      await pdfService.previewQuotationPDF(buildQuotationPreviewPayload(signUrl));
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Quotation preview could not open.'));
    } finally {
      setBusy(null);
    }
  };

  const handleRequestQuotationFileApproval = async () => {
    const quotationNumber = createForm.quotationNumber.trim();
    if (!quotationNumber) {
      setError('Create the quotation row first so PMApp can request signing approval.');
      return;
    }
    if (!canRequestQuotationOtpApproval(quotationNumber, currentWorkflowQuotationId)) {
      setError('Save or update the quotation row first so PMApp can request OTP approval for the saved quotation.');
      return;
    }

    setBusy('fileApproval');
    setError(null);
    setResult(null);

    try {
      const signUrl = (await ensureOutputSigningLink())?.signUrl || null;
      const previewPayload = buildQuotationPreviewPayload(signUrl);
      const { pdfService } = await import('@/services/pdfService');
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const pdfBlob = await pdfService.generateQuotationPDFBlob(previewPayload);
      const pdfBase64 = await blobToBase64(pdfBlob);
      const pdfFileName = buildFiledQuotationPdfFileName({
        quotationNumber,
        address: createForm.projectAddress,
        suburb: createForm.suburb,
        fallbackQuotationId: currentWorkflowQuotationId,
      });
      const approval = await azureSqlService.requestDocumentApproval({
        documentType: 'quotation',
        documentId: currentWorkflowQuotationId || undefined,
        documentNumber: quotationNumber,
        pdfBase64,
        approverEmail: createSignatoryProfile.email || undefined,
      });
      setQuotationFileApproval({
        approvalId: String(approval?.approvalId || ''),
        status: String(approval?.status || 'Pending'),
        approverEmail: approval?.approverEmail || null,
        expiresAtLabel: approval?.expiresAtLabel || null,
        approvedAt: approval?.approvedAt || null,
        pdfBase64,
        pdfFileName,
      });
      setQuotationFileApprovalCode('');
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Digital signing approval could not be requested.'));
    } finally {
      setBusy(null);
    }
  };

  const handleVerifyQuotationFileApproval = async () => {
    if (!quotationFileApproval?.approvalId) {
      setError('Request digital signing approval before entering the code.');
      return;
    }
    if (!quotationFileApprovalCode.trim()) {
      setError('Enter the approval code from the email.');
      return;
    }

    setBusy('fileApprovalVerify');
    setError(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const verified = await azureSqlService.verifyDocumentApproval({
        approvalId: quotationFileApproval.approvalId,
        otpCode: quotationFileApprovalCode.trim(),
      });
      setQuotationFileApproval((prev) => prev ? {
        ...prev,
        status: String(verified?.status || 'Approved'),
        approvedAt: verified?.approvedAt || new Date().toISOString(),
        approverEmail: verified?.approverEmail || prev.approverEmail,
        expiresAtLabel: verified?.expiresAtLabel || prev.expiresAtLabel,
      } : prev);
      setQuotationFileApprovalCode('');
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Approval code could not be verified.'));
    } finally {
      setBusy(null);
    }
  };

  const handleFileQuotationPdf = async () => {
    const quotationNumber = createForm.quotationNumber.trim();
    if (!quotationNumber) {
      setError('Create the quotation row first so the PDF can be filed to the quotation folder.');
      return;
    }
    if (!quotationFileApproval?.approvalId || quotationFileApproval.status !== 'Approved' || !quotationFileApproval.pdfBase64) {
      setError('Request OTP approval and verify the code before filing the digitally signed quotation.');
      return;
    }

    setBusy('filePdf');
    setError(null);
    setResult(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const pdfFileName = buildFiledQuotationPdfFileName({
        quotationNumber,
        address: createForm.projectAddress,
        suburb: createForm.suburb,
        fallbackQuotationId: currentWorkflowQuotationId,
      });
      const response = await azureSqlService.fileWorkflowQuotationPdf({
        quotationId: currentWorkflowQuotationId || undefined,
        quotationNumber,
        reuseExistingQuotationFolder: isReusingExistingQuotationFolder,
        existingQuotationFolderPath: isReusingExistingQuotationFolder
          ? selectedExistingQuotationFolderPath
          : undefined,
        address: createForm.projectAddress || undefined,
        suburb: createForm.suburb || undefined,
        projectType: createForm.projectType || undefined,
        noUnits: createForm.noUnits || undefined,
        title: createForm.projectDescription || undefined,
        pdfBase64: quotationFileApproval.pdfBase64,
        pdfContentType: 'application/pdf',
        pdfFileName,
        approvalId: quotationFileApproval.approvalId,
        signedByEmail: quotationFileApproval.approverEmail || createSignatoryProfile.email || undefined,
      });
      setResult(response);
      const draftBlob = response?.draftBlob || response?.data?.draftBlob || null;
      const digitalSignature = response?.digitalSignature || response?.data?.digitalSignature || null;
      if (digitalSignature && draftBlob?.fileId) {
        setFiledSignedQuotationPdf({
          fileId: String(draftBlob.fileId),
          fileName: draftBlob.fileName || pdfFileName,
          contentType: draftBlob.contentType || 'application/pdf',
          contentPath: draftBlob.contentPath || null,
        });
      }
      setQuotationFileApproval(null);
      setQuotationFileApprovalCode('');
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Quotation PDF could not be filed.'));
    } finally {
      setBusy(null);
    }
  };

  const handlePreviewQuotationEmail = async () => {
    if (!currentWorkflowQuotationId) {
      setError('Create or update the quotation row first so PMApp can preview the quotation email and signing link.');
      return;
    }
    if (!createForm.clientEmail.trim()) {
      setError('Add the client email before previewing the quotation email.');
      return;
    }

    setBusy('emailPreview');
    setError(null);
    setQuotationEmailSentConfirmation(null);

    try {
      const issuedLink = await ensureOutputSigningLink();
      const signUrl = issuedLink?.signUrl || null;
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const pdfFileName = buildFiledQuotationPdfFileName({
        quotationNumber: createForm.quotationNumber.trim(),
        address: createForm.projectAddress,
        suburb: createForm.suburb,
        fallbackQuotationId: currentWorkflowQuotationId,
      });
      const response = await azureSqlService.previewWorkflowQuotationEmail({
        quotationId: currentWorkflowQuotationId,
        recipientEmail: createForm.clientEmail.trim(),
        ccEmails: quotationEmailCcEmails,
        signUrl: signUrl || undefined,
        pdfFileName,
      });
      rememberSavedQuotationIdentity(resolveWorkflowQuotationIdentity(response, createForm.quotationNumber.trim()));
      setQuotationEmailPreview(response || null);
      setFiledSignedQuotationPdf(response?.filedSignedPdf?.fileId ? response.filedSignedPdf : null);
      setQuotationEmailComposerDraft((currentDraft) => (
        response
          ? currentDraft ?? {
              subject: String(response.subject || ''),
              html: String(response.html || ''),
            }
          : null
      ));
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Quotation email preview could not load.'));
    } finally {
      setBusy(null);
    }
  };

  const resetQuotationEmailComposerDraft = () => {
    setQuotationEmailComposerDraft(quotationEmailPreview ? {
      subject: String(quotationEmailPreview.subject || ''),
      html: String(quotationEmailPreview.html || ''),
    } : null);
  };

  const handleSendQuotationEmail = async () => {
    if (!quotationEmailPreview || !quotationEmailComposerDraft) {
      setError('Preview the quotation email before sending it.');
      return;
    }
    const emailWorkflowQuotationId = currentWorkflowQuotationId
      || asPositiveInteger(resolveWorkflowQuotationIdentity(quotationEmailPreview, createForm.quotationNumber.trim())?.quotationId);
    if (!emailWorkflowQuotationId) {
      setError('Create or update the quotation row first so PMApp can email the quotation and signing link.');
      return;
    }
    if (!createForm.clientEmail.trim()) {
      setError('Add the client email before sending the quotation email.');
      return;
    }
    const filedPdfForEmail = filedSignedQuotationPdf?.fileId
      ? filedSignedQuotationPdf
      : quotationEmailPreview.filedSignedPdf?.fileId
        ? quotationEmailPreview.filedSignedPdf
        : null;
    if (!filedPdfForEmail?.fileId) {
      setError('Sign and file the quotation PDF before emailing it. Email uses the stored signed PDF and does not request a second OTP.');
      return;
    }

    setBusy('emailPdf');
    setError(null);
    setResult(null);
    setQuotationEmailSentConfirmation(null);

    try {
      const issuedLink = await ensureOutputSigningLink(emailWorkflowQuotationId);
      const signUrl = issuedLink?.signUrl || null;
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.sendWorkflowQuotationEmail({
        quotationId: emailWorkflowQuotationId,
        recipientEmail: createForm.clientEmail.trim(),
        ccEmails: quotationEmailCcEmails,
        signUrl: signUrl || undefined,
        pdfFileName: filedPdfForEmail.fileName || quotationEmailPreview?.pdfFileName || undefined,
        signedPdfDraftBlobFileId: filedPdfForEmail.fileId,
        customEmailSubject: quotationEmailComposerDraft.subject,
        customEmailHtml: quotationEmailComposerDraft.html,
      });
      const sentRecipients = Array.isArray(response?.recipientEmails || response?.data?.recipientEmails)
        ? (response?.recipientEmails || response?.data?.recipientEmails).map((value: unknown) => toTrimmedString(value)).filter(Boolean)
        : parseEmailListInput(createForm.clientEmail);
      const sentFileName = toTrimmedString(
        response?.email?.fileName
        || response?.data?.email?.fileName
        || filedPdfForEmail.fileName
        || quotationEmailPreview?.pdfFileName
      );
      const confirmation = [
        `Quotation email sent to ${sentRecipients.length > 0 ? sentRecipients.join(', ') : createForm.clientEmail.trim() || 'the client'}.`,
        sentFileName ? `Attached ${sentFileName}.` : '',
      ].filter(Boolean).join(' ');
      setQuotationEmailSentConfirmation(confirmation);
      setResult({
        ...(response || {}),
        statusMessage: response?.statusMessage || response?.data?.statusMessage || confirmation,
      });
      if (response?.filedSignedPdf?.fileId || response?.data?.filedSignedPdf?.fileId) {
        setFiledSignedQuotationPdf(response?.filedSignedPdf || response?.data?.filedSignedPdf);
      }
    } catch (err: any) {
      setError(formatUserFacingError(err, 'Quotation email could not be sent.'));
    } finally {
      setBusy(null);
    }
  };

  const handleReviewSignedQuotationPdf = async () => {
    const filedPdfForReview = filedSignedQuotationPdf?.fileId
      ? filedSignedQuotationPdf
      : quotationEmailPreview?.filedSignedPdf?.fileId
        ? quotationEmailPreview.filedSignedPdf
        : null;

    if (!filedPdfForReview?.fileId) {
      setError('Sign and file the quotation PDF before reviewing the signed quotation email attachment.');
      return;
    }

    const previewWindow = openQuotationReviewWindow('Opening signed quotation...');
    setBusy('reviewSignedPdf');
    setError(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const download = await azureSqlService.downloadDraftFile(filedPdfForReview.fileId);
      openQuotationPdfBlob(download.blob, previewWindow, 'Signed Quotation Review');
    } catch (err: any) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      setError(formatUserFacingError(err, 'Signed quotation PDF could not open.'));
    } finally {
      setBusy(null);
    }
  };

  const renderQuotationFileApprovalPanel = () => {
    const hasFiledSignedPdf = Boolean(filedSignedQuotationPdf?.fileId);
    const hasApprovedOtp = quotationFileApproval?.status === 'Approved';
    const statusIsReady = hasFiledSignedPdf || hasApprovedOtp;

    return (
      <div style={{ width: '100%', borderRadius: 12, border: '1px solid #d8e1f2', background: '#fff', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid #d8e1f2' }}>
          <span style={{ width: 4, height: 24, borderRadius: 999, background: '#155e75', flexShrink: 0 }} />
          <div style={{ fontWeight: 900, color: '#061833', fontSize: 15 }}>File Digital Signing Approval</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, padding: '12px 14px', alignItems: 'center' }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <div style={{
              borderRadius: 999,
              border: statusIsReady ? '1px solid #b7d8c9' : '1px solid #cbd5e1',
              background: statusIsReady ? '#e8f5ee' : '#f8fafc',
              color: statusIsReady ? '#064e3b' : '#475569',
              padding: '5px 12px',
              textAlign: 'center',
              fontWeight: 900,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}>
              {hasFiledSignedPdf ? 'Signed and filed with OTP' : hasApprovedOtp ? 'Approved' : quotationFileApproval?.approvalId ? 'OTP sent' : 'Approval required'}
            </div>
            <div style={{ color: '#53627a', fontSize: 12, lineHeight: 1.5 }}>
              {hasFiledSignedPdf
                ? `Using stored signed quotation PDF${filedSignedQuotationPdf?.fileName ? `: ${filedSignedQuotationPdf.fileName}` : ''}. Email will use this PDF without another OTP.`
                : hasApprovedOtp
                  ? `Approved${quotationFileApproval?.approverEmail ? ` by ${quotationFileApproval.approverEmail}` : ''}. Sign and file the PDF to store the approved signed copy.`
                  : quotationFileApproval?.approvalId
                    ? `OTP sent${quotationFileApproval.approverEmail ? ` to ${quotationFileApproval.approverEmail}` : ''}${quotationFileApproval.expiresAtLabel ? `, expires ${quotationFileApproval.expiresAtLabel}` : ''}.`
                    : 'Request OTP approval before filing the digitally signed PDF.'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {hasFiledSignedPdf ? (
              <button
                type="button"
                disabled
                style={{
                  border: '1px solid #b7d8c9',
                  background: '#f0fdf4',
                  color: '#166534',
                  borderRadius: 10,
                  padding: '10px 14px',
                  cursor: 'default',
                  fontWeight: 800,
                }}
              >
                OTP already applied
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => { void handleRequestQuotationFileApproval(); }}
                  disabled={busy !== null || !canRequestQuotationOtpApproval(createForm.quotationNumber, currentWorkflowQuotationId)}
                  style={{
                    border: '1px solid rgba(21,94,117,0.28)',
                    background: '#fff',
                    color: '#155e75',
                    borderRadius: 10,
                    padding: '10px 14px',
                    cursor: busy || !canRequestQuotationOtpApproval(createForm.quotationNumber, currentWorkflowQuotationId) ? 'not-allowed' : 'pointer',
                    opacity: busy || !canRequestQuotationOtpApproval(createForm.quotationNumber, currentWorkflowQuotationId) ? 0.7 : 1,
                    fontWeight: 800,
                  }}
                  title={!canRequestQuotationOtpApproval(createForm.quotationNumber, currentWorkflowQuotationId) ? 'Save or update the quotation row before requesting OTP approval.' : undefined}
                >
                  {busy === 'fileApproval' ? 'Sending OTP...' : quotationFileApproval?.approvalId ? 'Request new OTP' : 'Request OTP'}
                </button>
                {quotationFileApproval?.approvalId && quotationFileApproval.status !== 'Approved' ? (
                  <>
                    <input
                      style={{ ...inputStyle, width: 150, letterSpacing: '0.18em', fontWeight: 800, textAlign: 'center' }}
                      value={quotationFileApprovalCode}
                      onChange={(event) => setQuotationFileApprovalCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      inputMode="numeric"
                    />
                    <button
                      type="button"
                      onClick={() => { void handleVerifyQuotationFileApproval(); }}
                      disabled={busy !== null || quotationFileApprovalCode.trim().length < 6}
                      style={{
                        border: 'none',
                        background: '#155e75',
                        color: '#fff',
                        borderRadius: 10,
                        padding: '10px 14px',
                        cursor: busy || quotationFileApprovalCode.trim().length < 6 ? 'not-allowed' : 'pointer',
                        opacity: busy || quotationFileApprovalCode.trim().length < 6 ? 0.7 : 1,
                        fontWeight: 800,
                      }}
                    >
                      {busy === 'fileApprovalVerify' ? 'Verifying...' : 'Verify OTP'}
                    </button>
                  </>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderQuotationEmailPreviewComposer = () => {
    const currentToEmails = parseEmailListInput(createForm.clientEmail);
    const previewToEmails = currentToEmails.length > 0
      ? currentToEmails
      : quotationEmailPreview?.recipientEmails || [];
    const previewCcEmails = quotationEmailCcEmails;
    const filedPdfForEmail = filedSignedQuotationPdf?.fileId
      ? filedSignedQuotationPdf
      : quotationEmailPreview?.filedSignedPdf?.fileId
        ? quotationEmailPreview.filedSignedPdf
        : null;
    const emailWorkflowQuotationId = currentWorkflowQuotationId
      || asPositiveInteger(resolveWorkflowQuotationIdentity(quotationEmailPreview, createForm.quotationNumber.trim())?.quotationId);
    const emailCanSend = Boolean(emailWorkflowQuotationId && quotationEmailComposerDraft && filedPdfForEmail?.fileId);
    const compactApprovalBadgeStyle = (ready: boolean): React.CSSProperties => ({
      borderRadius: 999,
      border: ready ? '1px solid #b7d8c9' : '1px solid #cbd5e1',
      background: ready ? '#e8f5ee' : '#f8fafc',
      color: ready ? '#064e3b' : '#475569',
      padding: '3px 10px',
      textAlign: 'center',
      fontWeight: 900,
      fontSize: 12,
      lineHeight: 1.25,
      letterSpacing: '0.03em',
      textTransform: 'uppercase',
    });
    const compactApprovalRowStyle = (withBorder = false): React.CSSProperties => ({
      display: 'grid',
      gridTemplateColumns: 'minmax(120px, 0.32fr) minmax(0, 1fr)',
      gap: 10,
      padding: '8px 12px',
      borderBottom: withBorder ? '1px solid #e1e7f2' : undefined,
      alignItems: 'center',
    });
    const compactApprovalDetailStyle: React.CSSProperties = {
      color: '#53627a',
      fontSize: 12,
      lineHeight: 1.35,
    };

    return (
      <div style={{ border: '1px solid #bfdbfe', borderRadius: 14, background: '#eff6ff', padding: 14, display: 'grid', gap: 10, fontSize: 13, lineHeight: 1.6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 800, color: '#10243f' }}>Quotation email preview</div>
            <div style={{ color: '#34579e', fontSize: 12 }}>
              Preview the email, edit the subject or body, then send the edited version.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={resetQuotationEmailComposerDraft}
              disabled={!quotationEmailPreview || busy !== null}
              style={{
                border: '1px solid rgba(47,111,212,0.24)',
                background: '#fff',
                color: '#234ea8',
                borderRadius: 10,
                padding: '8px 12px',
                cursor: !quotationEmailPreview || busy ? 'not-allowed' : 'pointer',
                opacity: !quotationEmailPreview || busy ? 0.7 : 1,
                fontWeight: 700,
              }}
            >
              Reset edits
            </button>
            <button
              type="button"
              onClick={() => { void handlePreviewQuotationEmail(); }}
              disabled={busy !== null || !canSendQuotationEmail}
              style={{
                border: '1px solid rgba(47,111,212,0.42)',
                background: '#fff',
                color: '#234ea8',
                borderRadius: 10,
                padding: '8px 12px',
                cursor: busy || !canSendQuotationEmail ? 'not-allowed' : 'pointer',
                opacity: busy || !canSendQuotationEmail ? 0.7 : 1,
                fontWeight: 700,
              }}
            >
              {busy === 'emailPreview' ? 'Loading preview...' : 'Refresh preview'}
            </button>
          </div>
        </div>
        <div><strong>To:</strong> {previewToEmails.length > 0 ? previewToEmails.join(', ') : 'Not set yet'}</div>
        <div><strong>CC:</strong> {previewCcEmails.length > 0 ? previewCcEmails.join(', ') : 'None'}</div>
        <div><strong>From:</strong> {quotationEmailPreview?.senderEmail || currentUserEmail || 'Not available yet'}</div>
        <div style={{ borderRadius: 10, border: '1px solid rgba(144,163,195,0.24)', background: '#fff', padding: 12, color: '#1f2937' }}>
          {quotationEmailPreview ? (
            <div style={{ display: 'grid', gap: 10 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Subject</span>
                <input
                  style={inputStyle}
                  value={quotationEmailComposerDraft?.subject ?? ''}
                  onChange={(event) => setQuotationEmailComposerDraft((prev) => ({
                    subject: event.target.value,
                    html: prev?.html ?? String(quotationEmailPreview.html || ''),
                  }))}
                  placeholder="Email subject"
                />
              </label>
              <div style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: '#34579e', textTransform: 'uppercase' }}>Email body</span>
                <div style={{ borderRadius: 10, border: '1px solid rgba(144,163,195,0.24)', background: '#fff', padding: 12, maxHeight: 320, overflowY: 'auto' }}>
                  <EditableEmailHtml
                    value={quotationEmailComposerDraft?.html ?? ''}
                    onChange={(nextHtml) => setQuotationEmailComposerDraft((prev) => ({
                      subject: prev?.subject ?? String(quotationEmailPreview.subject || ''),
                      html: nextHtml,
                    }))}
                    minHeight={180}
                  />
                </div>
              </div>
              <div style={{ borderRadius: 10, border: '1px solid #d8e1f2', background: '#fff', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '1px solid #d8e1f2' }}>
                  <span style={{ width: 4, height: 20, borderRadius: 999, background: '#3156c7', flexShrink: 0 }} />
                  <div style={{ fontWeight: 900, color: '#061833', fontSize: 14 }}>Digital Signing Approval</div>
                </div>
                <div style={{ display: 'grid' }}>
                  <div style={compactApprovalRowStyle(true)}>
                    <div style={{ fontWeight: 800, color: '#0b2545', fontSize: 13 }}>Status</div>
                    <div style={{ display: 'grid', gap: 4 }}>
                      <div style={compactApprovalBadgeStyle(Boolean(filedPdfForEmail))}>
                        {filedPdfForEmail ? 'Signed and filed with OTP' : 'Sign and file required'}
                      </div>
                      <div style={compactApprovalDetailStyle}>
                        {filedPdfForEmail
                          ? `Using stored signed quotation PDF${filedPdfForEmail.fileName ? `: ${filedPdfForEmail.fileName}` : ''}. No second OTP is required for email.`
                          : 'Use Sign and File Quotation PDF first. Email sends the stored signed PDF only.'}
                      </div>
                    </div>
                  </div>
                  <div style={compactApprovalRowStyle()}>
                    <div style={{ fontWeight: 800, color: '#0b2545', fontSize: 13 }}>Signing OTP</div>
                    <div style={{ display: 'grid', gap: 4 }}>
                      <div style={compactApprovalBadgeStyle(Boolean(filedPdfForEmail))}>
                        {filedPdfForEmail ? 'Already applied' : 'Handled during sign and file'}
                      </div>
                      <div style={compactApprovalDetailStyle}>
                        {filedPdfForEmail
                          ? 'The PDF was already approved, signed, and filed with OTP. Emailing does not request another OTP.'
                          : 'Request and verify OTP in the file approval panel, then sign and file before emailing.'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => { void handleSendQuotationEmail(); }}
                  disabled={busy !== null || !emailCanSend}
                  style={{
                    border: 'none',
                    background: '#166534',
                    color: '#fff',
                    borderRadius: 10,
                    padding: '10px 14px',
                    cursor: busy || !emailCanSend ? 'not-allowed' : 'pointer',
                    opacity: busy || !emailCanSend ? 0.7 : 1,
                    fontWeight: 700,
                  }}
                >
                  {busy === 'emailPdf' ? 'Sending quotation...' : 'Send filed quotation email'}
                </button>
                <button
                  type="button"
                  onClick={() => { void handleReviewSignedQuotationPdf(); }}
                  disabled={busy !== null || !filedPdfForEmail?.fileId}
                  style={{
                    border: '1px solid rgba(47,111,212,0.42)',
                    background: '#fff',
                    color: '#234ea8',
                    borderRadius: 10,
                    padding: '10px 14px',
                    cursor: busy || !filedPdfForEmail?.fileId ? 'not-allowed' : 'pointer',
                    opacity: busy || !filedPdfForEmail?.fileId ? 0.7 : 1,
                    fontWeight: 700,
                  }}
                  title={!filedPdfForEmail?.fileId ? 'Sign and file the quotation PDF before reviewing the signed copy.' : undefined}
                >
                  {busy === 'reviewSignedPdf' ? 'Opening signed quotation...' : 'Review Signed Quotation'}
                </button>
              </div>
              {quotationEmailSentConfirmation ? (
                <div style={{
                  justifySelf: 'start',
                  borderRadius: 10,
                  border: '1px solid #b7e4c7',
                  background: '#f0fdf4',
                  color: '#166534',
                  padding: '8px 12px',
                  fontSize: 13,
                  lineHeight: 1.4,
                  fontWeight: 700,
                }}>
                  {quotationEmailSentConfirmation}
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ color: '#64748b' }}>
              No email preview loaded yet. Use <strong>Email Filed Quotation PDF</strong> to load the current email before sending.
            </div>
          )}
        </div>
      </div>
    );
  };

  const submitAcceptedQuotation = async () => {
    setBusy('accept');
    setError(null);
    setResult(null);
    setAcceptedQuotationForActions(null);
    setAcceptReviewNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.acceptWorkflowQuotation({
        quotationId: acceptForm.quotationId,
        quotationNumber: acceptForm.quotationNumber || acceptForm.quotationLookup || undefined,
        projectNumber: acceptForm.projectNumber || undefined,
        projectTitle: acceptForm.projectTitle || undefined,
        assignedEngineerEmails: acceptForm.assignedEngineerEmails,
        createSharePointFolders: acceptForm.createSharePointFolders,
      });

      setResult(response);
      setAcceptedQuotationForActions(acceptReviewQuotation || selectedPendingQuotation || null);
      closeAcceptReview();
    } catch (err: any) {
      const notice = formatWorkflowUiNotice(err, 'Quotation could not be accepted.');
      setAcceptReviewNotice(notice);
      setError(notice.message);
    } finally {
      setBusy(null);
    }
  };

  const handleAcceptQuotation = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleOpenAcceptReview();
  };

  const resultSharePointPlan = result?.sharePointPlan || result?.data?.sharePointPlan || null;
  const resultQuotationNumber = toTrimmedString(result?.quotationNumber || result?.data?.quotationNumber);
  const resultStatusMessage = toTrimmedString(result?.statusMessage || result?.data?.statusMessage);
  const resultDraftBlob = result?.draftBlob || result?.data?.draftBlob || null;
  const resultSharePointUpload = result?.sharePointUpload || result?.data?.sharePointUpload || null;
  const resultQuotationFolder = result?.quotationFolder || result?.data?.quotationFolder || null;
  const resultEmail = result?.email || result?.data?.email || null;
  const resultEmailRecipients = Array.isArray(result?.recipientEmails || result?.data?.recipientEmails)
    ? (result?.recipientEmails || result?.data?.recipientEmails).map((value: unknown) => toTrimmedString(value)).filter(Boolean)
    : [];
  const resultEmailCcRecipients = Array.isArray(result?.ccEmails || result?.data?.ccEmails)
    ? (result?.ccEmails || result?.data?.ccEmails).map((value: unknown) => toTrimmedString(value)).filter(Boolean)
    : [];
  const resultEmailSignUrl = toTrimmedString(result?.signUrl || result?.data?.signUrl || resultEmail?.signUrl);
  const isQuotationEmailResult = Boolean(resultEmail || resultEmailRecipients.length > 0);
  const isQuotationPdfFiledResult = !isQuotationEmailResult && Boolean(
    resultDraftBlob
    || resultSharePointUpload
    || resultQuotationFolder
    || (resultStatusMessage && !result?.projectId && !result?.data?.projectId && !result?.projectNumber && !result?.data?.projectNumber)
  );
  const resultProjectId = toTrimmedString(result?.projectId || result?.data?.projectId);
  const resultProjectNumber = toTrimmedString(result?.projectNumber || result?.data?.projectNumber);
  const resultProjectTitle = toTrimmedString(result?.projectTitle || result?.data?.projectTitle);
  const resultCreatedTaskIds = Array.isArray(result?.createdTaskIds || result?.data?.createdTaskIds)
    ? (result?.createdTaskIds || result?.data?.createdTaskIds).map((value: unknown) => toTrimmedString(value)).filter(Boolean)
    : [];
  const resultSharePointErrors = Array.isArray(resultSharePointPlan?.errors)
    ? resultSharePointPlan.errors.map((item: unknown) => toTrimmedString(item)).filter(Boolean)
    : [];
  const sharePointUnauthorizedClientError = resultSharePointErrors.find((item) => (
    item.toLowerCase().includes('unauthorized_client') || item.includes('AADSTS700016')
  )) || '';
  const structuralInvoicePrefill = buildStructuralInvoicePrefillState(
    acceptedQuotationForActions,
    resultProjectId,
    resultProjectNumber,
  );
  const handleCreateStructuralInvoice = () => {
    if (!structuralInvoicePrefill) return;
    navigate('/invoices/workflow', { state: structuralInvoicePrefill });
  };

  const toggleAssignedEngineer = (email: string) => {
    setAcceptForm((prev) => {
      const normalized = String(email || '').trim().toLowerCase();
      if (!normalized) return prev;
      const exists = prev.assignedEngineerEmails.includes(normalized);
      return {
        ...prev,
        assignedEngineerEmails: exists
          ? prev.assignedEngineerEmails.filter((value) => value !== normalized)
          : [...prev.assignedEngineerEmails, normalized],
      };
    });
  };

  return (
    <div className="wdgf-page wdgf-page--quotation-create">
      <div className="wdgf-page__hero">
        <div className="wdgf-page__hero-inner" style={{ maxWidth: 1100 }}>
          <div className="wdgf-page__hero-title">
            <button
              type="button"
              onClick={() => navigate('/quotations')}
              className="wdgf-page__back-button"
            >
              Back
            </button>
            <div>
              <div className="wdgf-page__hero-kicker">Quotation Workflow</div>
              <h1>Quotation Intake / Acceptance</h1>
            </div>
          </div>
          <div className="wdgf-page__hero-copy">
            Create quotation rows with the Linfinity scope and type fields, preview the template, and accept approved quotations into projects without switching context.
          </div>
        </div>
      </div>

      <div className="wdgf-page__content" style={{ maxWidth: 1100 }}>
        {error && (
          <div className="wdgf-notice wdgf-notice--danger" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}
        {prefillNotice ? (
          <div className="wdgf-notice wdgf-notice--success" style={{ marginBottom: 16 }}>
            {prefillNotice}
          </div>
        ) : null}
        {isOutlookDraft && !isEditingLoadedPendingQuotation && routeState.outlookQuotationDraft && <section className="wdgf-notice" aria-label="Outlook quotation source" style={{ marginBottom: 16, border: '1px solid #d8e0ee', padding: 16, borderRadius: 12, background: '#f3f7fb', overflowWrap: 'anywhere' }}>
          <strong>Source email — review only</strong>
          <p style={{ margin: '8px 0' }}>{routeState.outlookQuotationDraft.source.subject || 'No subject'}</p>
          <p style={{ margin: '8px 0' }}>{[routeState.outlookQuotationDraft.source.fromName, routeState.outlookQuotationDraft.source.fromEmail].filter(Boolean).join(' · ') || 'Unknown sender'}</p>
          <p style={{ margin: '8px 0' }}>Attachment names only. Files have not been read or uploaded.</p>
          {routeState.outlookQuotationDraft.source.attachmentNames.length > 0 && <ul style={{ paddingLeft: 20, marginBottom: 0 }}>{routeState.outlookQuotationDraft.source.attachmentNames.slice(0, 10).map((name, index) => <li key={index}>{name}</li>)}</ul>}
          {routeState.outlookQuotationDraft.source.attachmentNames.length > 10 && <p>{routeState.outlookQuotationDraft.source.attachmentNames.length - 10} more attachments in the original email.</p>}
          <p style={{ margin: '8px 0 0' }}>Requested Structural/Civil services are checked below. Select and review detailed scope rows yourself; fees remain blank until you enter them.</p>
        </section>}

        <nav className="wdgf-mobile-jumpnav" aria-label="Quotation workflow sections">
          <a href="#quotation-create-core">Basics</a>
          <a href="#quotation-create-scope">Scope</a>
          <a href="#quotation-create-issue">Send</a>
          <a href="#quotation-accept">Accept</a>
        </nav>

        <div id="quotation-create-core" ref={createDraftPanelRef} className="quotation-create-panel" style={panelStyle}>
          <h2 className="quotation-create-title" style={{ marginTop: 0, marginBottom: 12, fontSize: 18 }}>1. Create Quotation</h2>
          <form onSubmit={handleCreateQuotation}>
            <div className="quotation-create-form-stack" style={{ display: 'grid', gap: 16 }}>
              <div className="quotation-create-section" style={sectionStyle}>
                <h3 className="quotation-create-section-title" style={sectionTitleStyle}>Quotation Setup</h3>
                <div className="quotation-create-section-caption" style={sectionCaptionStyle}>
                  Keep the quotation number, quote date, project scope, project type, and short project description together.
                </div>
                <div className="quotation-create-grid quotation-create-grid--setup" style={gridStyle}>
                  <div>
                    <label style={labelStyle}>Quotation Number *</label>
                    <input
                      style={hasBlockingQuotationNumberDuplicate
                        ? { ...inputStyle, border: '1px solid #d9822b', background: '#fff8ee' }
                        : inputStyle}
                      value={createForm.quotationNumber}
                      onChange={(e) => setCreate('quotationNumber', e.target.value)}
                      placeholder={quotationNumberStatus?.nextAvailableQuotationNumber
                        ? `Next available: ${quotationNumberStatus.nextAvailableQuotationNumber}`
                        : 'e.g. 26010'}
                    />
                    {quotationNumberHelpMessage ? (
                      <div style={{
                        marginTop: 6,
                        fontSize: 12,
                        color: hasBlockingQuotationNumberDuplicate ? '#b26a00' : '#5f6b7a',
                      }}>
                        {quotationNumberHelpMessage}
                      </div>
                    ) : null}
                    {hasBlockingQuotationNumberDuplicate && quotationNumberStatus?.existingQuotationFolder ? (
                      <button
                        type="button"
                        onClick={() => setSelectedExistingQuotationFolderPath(
                          quotationNumberStatus.existingQuotationFolder?.folderPath || ''
                        )}
                        style={{
                          marginTop: 8,
                          marginRight: 8,
                          padding: '6px 10px',
                          borderRadius: 999,
                          border: '1px solid #3156a3',
                          background: '#eef4ff',
                          color: '#234585',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Use {quotationNumberStatus.existingQuotationFolder.folderName}
                      </button>
                    ) : null}
                    {hasBlockingQuotationNumberDuplicate && quotationNumberStatus?.nextAvailableQuotationNumber ? (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedExistingQuotationFolderPath('');
                          setCreate('quotationNumber', quotationNumberStatus.nextAvailableQuotationNumber);
                        }}
                        style={{
                          marginTop: 8,
                          padding: '6px 10px',
                          borderRadius: 999,
                          border: '1px solid #d9822b',
                          background: '#fff8ee',
                          color: '#8d4f00',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Use {quotationNumberStatus.nextAvailableQuotationNumber}
                      </button>
                    ) : null}
                  </div>
                  <div>
                    <label style={labelStyle}>Quote Date *</label>
                    <input required type="date" style={inputStyle} value={createForm.quoteDate} onChange={(e) => setCreate('quoteDate', e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="quote-validity-days" style={labelStyle}>Quote validity period (days) *</label>
                    <input id="quote-validity-days" required type="number" min="1" max="2147483647" step="1" style={inputStyle}
                      value={createForm.quoteValidityDays} onChange={(e) => setCreate('quoteValidityDays', e.target.value)} />
                  </div>
                  <div className="quotation-create-field--full-mobile">
                    <label style={labelStyle}>Project Scope *</label>
                    <select required style={inputStyle} value={createForm.projectScope} onChange={(e) => setCreate('projectScope', e.target.value)}>
                      <option value="">Select project scope</option>
                      {isWorkflowDraft && createForm.projectScope && !quotationTemplateDefinition.projectScopeOptions.includes(createForm.projectScope) && <option value={createForm.projectScope}>{createForm.projectScope}</option>}
                      {quotationTemplateDefinition.projectScopeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div className="quotation-create-field--full-mobile">
                    <label style={labelStyle}>Project Type *</label>
                    <input
                      required
                      list="quotation-project-type-options"
                      style={inputStyle}
                      value={createForm.projectType}
                      onChange={(e) => setCreate('projectType', e.target.value)}
                      placeholder="Select or type project type"
                    />
                    <datalist id="quotation-project-type-options">
                      {projectTypeOptions.map((option) => (
                        <option key={option} value={option} />
                      ))}
                    </datalist>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div className="quotation-create-triple-grid" style={{ ...responsiveWorkflowTripleFieldGridStyle, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                      <div>
                        <label style={labelStyle}>Quotation Signatory *</label>
                        <select
                          required
                          style={inputStyle}
                          value={createForm.signatoryEmail}
                          onChange={(e) => setCreate('signatoryEmail', e.target.value)}
                        >
                          {(entityMatchedEngineerOptions.length > 0 ? entityMatchedEngineerOptions : availableEngineerOptions).length > 0 ? (
                            (entityMatchedEngineerOptions.length > 0 ? entityMatchedEngineerOptions : availableEngineerOptions).map((engineer) => (
                              <option key={engineer.email} value={engineer.email}>
                                {[engineer.displayName || engineer.email, engineer.jobTitle || engineer.role || null].filter(Boolean).join(' - ')}
                              </option>
                            ))
                          ) : (
                            <option value={createSignatoryProfile.email || ''}>
                              {createSignatoryProfile.displayName || 'No engineer profile available'}
                            </option>
                          )}
                        </select>
                        <div style={{ marginTop: 6, fontSize: 12, color: '#5f6b7a' }}>
                          {createSignatoryProfile.company
                            ? `${createSignatoryProfile.company}${createSignatoryProfile.position ? ` | ${createSignatoryProfile.position}` : ''}`
                            : 'Signatory details load from Users/UserList.'}
                        </div>
                      </div>
                      <div className="quotation-create-field--full-mobile">
                        <label style={labelStyle}>Entity *</label>
                        <select required style={inputStyle} value={createForm.entity} onChange={(e) => setCreate('entity', e.target.value)}>
                          <option value="Linfinity Engineering Pty Ltd">Linfinity Engineering Pty Ltd</option>
                          <option value="Equilibrium Engineering Pty Ltd">Equilibrium Engineering Pty Ltd</option>
                        </select>
                      </div>
                      <div style={currencyFieldWrapStyle}>
                        <label style={labelStyle}>Hourly Rate *</label>
                        <div style={currencyInputShellStyle}>
                          <span style={currencyPrefixStyle}>$</span>
                          <input
                            style={currencyInputStyle}
                            type="number"
                            min="0"
                            step="0.01"
                            required
                            value={createForm.hourlyRate}
                            onChange={(e) => setCreate('hourlyRate', e.target.value)}
                          />
                        </div>
                      </div>
                      <div style={currencyFieldWrapStyle}>
                        <label style={labelStyle}>Inspection Fee *</label>
                        <div style={currencyInputShellStyle}>
                          <span style={currencyPrefixStyle}>$</span>
                          <input
                            style={currencyInputStyle}
                            type="number"
                            min="0"
                            step="0.01"
                            required
                            value={createForm.inspectionFee}
                            onChange={(e) => setCreate('inspectionFee', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Project Description *</label>
                    <input
                      required
                      style={inputStyle}
                      value={createForm.projectDescription}
                      onChange={(e) => setCreate('projectDescription', e.target.value)}
                      placeholder="One-line project description"
                    />
                  </div>
                </div>
              </div>

              <div className="quotation-create-section" style={sectionStyle}>
                <h3 className="quotation-create-section-title" style={sectionTitleStyle}>Project Address</h3>
                <div className="quotation-create-section-caption" style={sectionCaptionStyle}>
                  Confirm the site address first, then use the suburb lookup to fill postcode and council where possible.
                </div>
                <div className="quotation-create-grid quotation-create-grid--address" style={gridStyle}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Project Address *</label>
                    <GoogleAddressInput
                      style={inputStyle}
                      required
                      value={createForm.projectAddress}
                      onChange={(nextValue) => setCreate('projectAddress', nextValue)}
                      onPlaceSelected={(_, place) => { void handleProjectAddressSelected(place); }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Suburb *</label>
                    <input
                      list="quotation-suburb-options"
                      style={inputStyle}
                      required
                      value={createForm.suburb}
                      onChange={(e) => {
                        setCreate('suburb', e.target.value);
                        setSuburbLookupMessage('');
                      }}
                      onBlur={(e) => { void hydrateSuburbLookup(e.target.value); }}
                    />
                    <datalist id="quotation-suburb-options">
                      {suburbOptions.map((option) => (
                        <option key={`${option.Suburb}-${option.PostCode}`} value={option.Suburb}>
                          {option.PostCode}{option.Council ? ` - ${option.Council}` : ''}
                        </option>
                      ))}
                    </datalist>
                    {suburbLookupMessage && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#5f6b7a' }}>{suburbLookupMessage}</div>
                    )}
                  </div>
                  <div>
                    <label style={labelStyle}>Postcode *</label>
                    <input
                      style={inputStyle}
                      required
                      value={createForm.postCode}
                      onChange={(e) => {
                        setCreate('postCode', e.target.value);
                        setSuburbLookupMessage('');
                      }}
                      onBlur={(e) => { void hydratePostCodeLookup(e.target.value); }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Council *</label>
                    <input required style={inputStyle} value={createForm.council} onChange={(e) => setCreate('council', e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="quotation-create-section" style={sectionStyle}>
                <h3 className="quotation-create-section-title" style={sectionTitleStyle}>Client Details</h3>
                <div className="quotation-create-section-caption" style={sectionCaptionStyle}>
                  Keep the client contact and address information together for the quotation and acceptance handoff.
                </div>
                <div className="quotation-create-grid quotation-create-grid--client" style={clientDetailsGridStyle}>
                  <div style={{ position: 'relative' }}>
                    <label style={labelStyle}>Client Name *</label>
                    <input
                      style={inputStyle}
                      required
                      value={createForm.clientName}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape' && (clientMatches.length > 0 || clientLookupMessage)) {
                          e.preventDefault();
                          dismissClientMatches();
                        }
                      }}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        const nextLookupKey = normalizeLookupValue(nextValue);
                        setCreate('clientName', nextValue);
                        if (nextLookupKey !== dismissedClientLookupQueryRef.current) {
                          dismissedClientLookupQueryRef.current = '';
                          setDismissedClientLookupQuery('');
                        }
                        setClientLookupMessage('');
                      }}
                    />
                    {(clientLookupBusy || clientLookupMessage) ? (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#5f6b7a' }}>
                        {clientLookupBusy ? 'Checking client list...' : clientLookupMessage}
                      </div>
                    ) : null}
                    {clientMatches.length > 0 ? (
                      <div
                        className="quotation-client-suggestions"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          zIndex: 20,
                          marginTop: 8,
                          width: 'min(760px, calc(100vw - 80px))',
                          maxWidth: '100%',
                          background: '#fff',
                          border: '1px solid #d9e2ec',
                          borderRadius: 12,
                          boxShadow: '0 18px 40px rgba(15, 23, 42, 0.12)',
                          overflow: 'hidden',
                        }}
                      >
                        <div className="quotation-client-suggestions__header">
                          <span>{clientMatches.length} matching client{clientMatches.length === 1 ? '' : 's'}</span>
                          <button
                            type="button"
                            className="quotation-client-suggestions__dismiss"
                            aria-label="Dismiss client suggestions"
                            title="Dismiss suggestions"
                            onClick={(event) => {
                              event.stopPropagation();
                              dismissClientMatches();
                            }}
                          >
                            <X size={14} aria-hidden="true" />
                          </button>
                        </div>
                        {clientMatches.map((match) => (
                          <button
                            key={`${match.ClientId ?? match.Name}-${match.Email ?? match.Phone ?? ''}`}
                            type="button"
                            onClick={() => applyClientMatch(match)}
                            style={{
                              width: '100%',
                              border: 'none',
                              background: '#fff',
                              padding: '12px 14px',
                              textAlign: 'left',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eef2f7',
                            }}
                          >
                            <div style={{ display: 'grid', gap: 4 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#102a43' }}>{match.Name || 'Unnamed client'}</div>
                                <div style={{ fontSize: 12, color: '#486581' }}>{match.Company || 'No company'}</div>
                              </div>
                              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: '#52606d' }}>
                                <span>{formatPhoneForDisplay(match.Phone) || 'No phone'}</span>
                                <span>{match.Email || 'No email'}</span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label style={labelStyle}>Client Company</label>
                    <input style={inputStyle} value={createForm.clientCompany} onChange={(e) => setCreate('clientCompany', e.target.value)} />
                  </div>
                  <div style={{ maxWidth: 160 }}>
                    <label style={labelStyle}>Client ABN</label>
                    <input
                      style={inputStyle}
                      value={createForm.clientAbn}
                      onChange={(e) => setCreate('clientAbn', e.target.value)}
                      placeholder="12 345 678 901"
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div className="quotation-create-wide-grid" style={responsiveWorkflowWideFieldGridStyle}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                          <label style={{ ...labelStyle, marginBottom: 0 }}>Client Address *</label>
                          <label style={{
                            ...labelStyle,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            marginBottom: 0,
                            color: '#486581',
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                          }}>
                            <input
                              type="checkbox"
                              checked={useProjectAddressForClient}
                              onChange={(e) => handleUseProjectAddressForClientChange(e.target.checked)}
                            />
                            Use Project Address
                          </label>
                        </div>
                        <input
                          required
                          style={inputStyle}
                          value={createForm.clientAddress}
                          onChange={(e) => {
                            setUseProjectAddressForClient(false);
                            setCreate('clientAddress', e.target.value);
                          }}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Client Email *</label>
                        <ClientEmailAutocompleteInput
                          required
                          style={inputStyle}
                          type="email"
                          value={createForm.clientEmail}
                          onChange={(value) => setCreate('clientEmail', value)}
                          placeholder="Search client name or email"
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Client Mobile *</label>
                        <input required style={inputStyle} type="tel" value={createForm.clientMobile} onChange={(e) => setCreate('clientMobile', e.target.value)} onBlur={() => formatCreatePhoneField('clientMobile')} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="quotation-create-section" style={sectionStyle}>
                <h3 className="quotation-create-section-title" style={sectionTitleStyle}>Fees and Payment Split</h3>
                <div className="quotation-create-section-caption" style={sectionCaptionStyle}>
                  Structural and civil staged payments are calculated independently through the API. Structural defaults stay configurable at 10% deposit and 70% stage 2, while civil follows the fee-threshold policy.
                </div>
                <div className="quotation-create-grid quotation-create-grid--fees" style={gridStyle}>
                  <div style={currencyFieldWrapStyle}>
                    <label style={labelStyle}>Structural Fee *</label>
                    <div style={currencyInputShellStyle}>
                      <span style={currencyPrefixStyle}>$</span>
                      <input
                        style={currencyInputStyle}
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        value={createForm.structuralFee}
                        onChange={(e) => setCreate('structuralFee', e.target.value)}
                      />
                    </div>
                  </div>
                  <div style={currencyFieldWrapStyle}>
                    <label style={labelStyle}>Civil Fee *</label>
                    <div style={currencyInputShellStyle}>
                      <span style={currencyPrefixStyle}>$</span>
                      <input
                        style={currencyInputStyle}
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        value={createForm.civilFee}
                        onChange={(e) => setCreate('civilFee', e.target.value)}
                      />
                    </div>
                  </div>
                  <div style={currencyFieldWrapStyle}>
                    <label style={labelStyle}>Soil Report Fee *</label>
                    <div style={currencyInputShellStyle}>
                      <span style={currencyPrefixStyle}>$</span>
                      <input
                        style={currencyInputStyle}
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        value={createForm.soilReportFee}
                        onChange={(e) => setCreate('soilReportFee', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
                  {orderedPaymentComponentSchedules.map((schedule) => {
                    const depositFieldKey = schedule.key === 'structural'
                      ? 'structuralDepositPercentage'
                      : 'civilDepositPercentage';
                    const stage2FieldKey = schedule.key === 'structural'
                      ? 'structuralStage2Percentage'
                      : 'civilStage2Percentage';
                    const paymentModeKey = schedule.key === 'structural'
                      ? 'structuralPaymentMode'
                      : 'civilPaymentMode';
                    const stageDraftsKey = schedule.key === 'structural'
                      ? 'structuralPaymentStages'
                      : 'civilPaymentStages';
                    const isStructuralSchedule = schedule.key === 'structural';
                    const isManualSchedule = createForm[paymentModeKey] === 'manual';
                    const stageDrafts = createForm[stageDraftsKey] || [];
                    const hasFourthStage = stageDrafts.some((stage) => stage.stageKey === 'stage3');
                    const refOptions = getPaymentStageRefOptions(schedule.key);

                    return (
                      <div key={schedule.key} style={paymentDisciplinePanelStyle}>
                        <div style={paymentDisciplineHeaderStyle}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#102a43' }}>
                              {schedule.label} Payment Schedule
                            </div>
                            <div style={{ marginTop: 4, fontSize: 12, color: '#52606d' }}>
                              {isStructuralSchedule
                                ? `${formatCurrency(schedule.fee)} structural fee split independently.`
                                : `${formatCurrency(schedule.fee)} civil fee split with automatic threshold rules.`}
                            </div>
                          </div>
                          <div style={{ display: 'grid', gap: 10, width: '100%', maxWidth: 420 }}>
                            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
                              <input
                                type="checkbox"
                                checked={isManualSchedule}
                                onChange={(e) => handlePaymentModeChange(schedule.key, e.target.checked ? 'manual' : 'auto')}
                              />
                              Manual override split
                            </label>
                            {isManualSchedule ? (
                              <div style={{ fontSize: 12, color: '#52606d', lineHeight: 1.6 }}>
                                Enter the non-final stage amounts directly. Final payment automatically uses the remaining balance, and you can add a 4th stage if needed.
                              </div>
                            ) : isStructuralSchedule ? (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(120px, 1fr))', gap: 10 }}>
                                <div>
                                  <label style={labelStyle}>Deposit %</label>
                                  <input
                                    style={inputStyle}
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={createForm[depositFieldKey]}
                                    onChange={(e) => setCreate(depositFieldKey, e.target.value)}
                                  />
                                </div>
                                <div>
                                  <label style={labelStyle}>Stage 2 %</label>
                                  <input
                                    style={inputStyle}
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={createForm[stage2FieldKey]}
                                    onChange={(e) => setCreate(stage2FieldKey, e.target.value)}
                                  />
                                </div>
                              </div>
                            ) : (
                              <div style={{ fontSize: 12, color: '#52606d', lineHeight: 1.6 }}>
                                Deposit: $0 up to $3,000, then $500 to $5,000, then 20% plus GST above $5,000.
                                Stage 2: balance up to $5,000, 60% from $5,001 to $10,000, then balance again above $10,000.
                              </div>
                            )}
                            {isManualSchedule ? (
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {!hasFourthStage ? (
                                  <button
                                    type="button"
                                    onClick={() => handleAddFourthStage(schedule.key)}
                                    style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                                  >
                                    Add 4th stage
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveFourthStage(schedule.key)}
                                    style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                                  >
                                    Remove 4th stage
                                  </button>
                                )}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div style={paymentStageGridStyle}>
                          {schedule.stages.map((stage) => {
                            const stageDraft = stageDrafts.find((draft) => draft.stageKey === stage.stageKey) || null;
                            const triggerText = stageDraft?.triggerLabel ?? stage.triggerLabel ?? stage.reference ?? '';
                            const matchingRefOption = refOptions.find((option) => option.OptionLabel === triggerText) || null;
                            const selectedRefValue = stageDraft?.triggerOptionId !== null && stageDraft?.triggerOptionId !== undefined
                              ? stageDraft.triggerOptionId
                              : stage.triggerOptionId !== null && stage.triggerOptionId !== undefined
                                ? stage.triggerOptionId
                                : matchingRefOption
                                  ? matchingRefOption.QuotationPaymentStageRefOptionId ?? `label:${matchingRefOption.OptionLabel}`
                                  : '';
                            return (
                            <div key={stage.key} style={stageCardStyle}>
                              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#486581', textTransform: 'uppercase' }}>
                                {stage.label}
                              </div>
                              {isManualSchedule && stage.stageKey !== 'final' ? (
                                <div style={{ marginTop: 8 }}>
                                  <label style={labelStyle}>Amount</label>
                                  <div style={currencyInputShellStyle}>
                                    <span style={currencyPrefixStyle}>$</span>
                                    <input
                                      style={currencyInputStyle}
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={stageDraft?.amount || ''}
                                      onChange={(e) => handlePaymentStageDraftChange(schedule.key, stage.stageKey as PaymentStageKey, 'amount', e.target.value)}
                                    />
                                  </div>
                                </div>
                              ) : null}
                              <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: '#102a43' }}>
                                {formatCurrency(stage.amount)}
                              </div>
                              <div style={{ marginTop: 4, fontSize: 12, color: '#52606d' }}>
                                Target: {formatPercentage(stage.requestedPercentage ?? stage.percentage)}
                              </div>
                              <div style={{ marginTop: 2, fontSize: 12, color: '#52606d' }}>
                                {stage.stageKey === 'final'
                                  ? 'Remaining balance of the discipline fee'
                                  : isManualSchedule
                                    ? `${formatPercentage(stage.percentage)} of ${schedule.label.toLowerCase()} fee from manual override`
                                    : `${formatPercentage(stage.percentage)} of ${schedule.label.toLowerCase()} fee after rounding`}
                              </div>
                              <div style={{ marginTop: 8 }}>
                                <label style={labelStyle}>Use saved payment ref</label>
                                <select
                                  style={inputStyle}
                                  value={selectedRefValue}
                                  onChange={(e) => handlePaymentStageDraftChange(
                                    schedule.key,
                                    stage.stageKey as PaymentStageKey,
                                    'triggerOptionId',
                                    e.target.value || null,
                                  )}
                                >
                                  <option value="">Select stage payment ref</option>
                                  {refOptions.map((option) => (
                                    <option
                                      key={`${schedule.key}-${option.QuotationPaymentStageRefOptionId ?? option.OptionLabel}`}
                                      value={option.QuotationPaymentStageRefOptionId ?? `label:${option.OptionLabel}`}
                                    >
                                      {option.OptionLabel}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div style={{ marginTop: 8 }}>
                                <label style={labelStyle}>Stage Payment Ref</label>
                                <textarea
                                  style={paymentStageRefTextareaStyle}
                                  value={triggerText}
                                  onChange={(e) => handlePaymentStageDraftChange(
                                    schedule.key,
                                    stage.stageKey as PaymentStageKey,
                                    'triggerLabel',
                                    e.target.value,
                                  )}
                                  placeholder="Type the payment timing shown on the quotation"
                                />
                              </div>
                              <div style={paymentStageRefTextStyle}>
                                Ref: {triggerText || 'Not set'}
                              </div>
                              {stage.description ? (
                                <div style={{ marginTop: 6, fontSize: 12, color: '#5f6b7a', lineHeight: 1.5 }}>
                                  {stage.description}
                                </div>
                              ) : null}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: 10, fontSize: 12, color: '#5f6b7a' }}>
                  {paymentScheduleBusy ? 'Calculating staged payments from the quotation payment API...' : paymentSummaryText}
                  {paymentScheduleMessage ? ` ${paymentScheduleMessage}` : ''}
                  {paymentStageRefMessage ? ` ${paymentStageRefMessage}` : ''}
                  {' Soil report fee remains outside the structural and civil staged split.'}
                </div>
              </div>

              <details id="quotation-create-scope" className="quotation-mobile-disclosure" open={isPhone ? undefined : true}>
                <summary className="quotation-mobile-disclosure__summary">
                  Scope rows and reusable wording
                </summary>
                <div className="quotation-create-section" style={sectionStyle}>
                <h3 className="quotation-create-section-title" style={sectionTitleStyle}>Detailed Scope Selection</h3>
                <div className="quotation-create-section-caption" style={sectionCaptionStyle}>
                  Pick the structural and civil detail groups below. Each group loads reusable DB-backed scope rows automatically, and you can add extra rows for this quote or save them for future reuse.
                </div>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div style={scopePanelStyle}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 700, color: '#102a43' }}>
                      <input
                        type="checkbox"
                        checked={createForm.includeStructuralScope}
                        onChange={(e) => setCreate('includeStructuralScope', e.target.checked)}
                      />
                      Detailed Structural Scope
                    </label>
                    {!createForm.includeStructuralScope ? (
                      <div style={{ marginTop: 10, fontSize: 13, color: '#5f6b7a', lineHeight: 1.6 }}>
                        Structural detailed scope is turned off and will not be included in this quotation.
                      </div>
                    ) : structuralScopeGroupOptions.length > 0 ? (
                      <>
                        <div style={{ marginTop: 10 }}>
                          <label style={labelStyle}>Structural Detail Group</label>
                          <select
                            style={inputStyle}
                            value={createForm.structuralScopeGroupKey}
                            onChange={(e) => handleScopeGroupChange('structural', e.target.value)}
                          >
                            {isImportedDraft && !isEditingLoadedPendingQuotation && <option value="">Select a structural scope group</option>}
                            {structuralScopeGroupOptions.map((group) => (
                              <option key={group.groupKey} value={group.groupKey}>
                                {group.groupLabel}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ marginTop: 10 }}>
                          <label style={labelStyle}>Structural Special Footnote</label>
                          <textarea
                            style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
                            value={createForm.structuralScopeFootnote}
                            onChange={(e) => setCreate('structuralScopeFootnote', e.target.value)}
                            placeholder="Optional structural footnote for this quotation"
                          />
                        </div>

                        <div style={scopeEditorListStyle}>
                          {createForm.structuralScopeItems.map((item, index) => {
                            const isDragSource = scopeItemDrag?.discipline === 'structural' && scopeItemDrag.fromIndex === index;
                            const isDropTarget = scopeItemDrag?.discipline === 'structural' && scopeItemDrag.overIndex === index && !isDragSource;

                            return (
                              <div
                                key={`structural-scope-item-${index}`}
                                className="quotation-create-scope-row"
                                onDragOver={(event) => handleScopeItemDragOver('structural', index, event)}
                                onDrop={(event) => handleScopeItemDrop('structural', index, event)}
                                style={{
                                  ...scopeEditorRowStyle,
                                  opacity: isDragSource ? 0.58 : 1,
                                  borderRadius: 10,
                                  outline: isDropTarget ? '2px solid #34579e' : '2px solid transparent',
                                  outlineOffset: 2,
                                }}
                              >
                                <button
                                  type="button"
                                  draggable
                                  aria-label={`Drag structural scope row ${index + 1}`}
                                  title="Drag row"
                                  onDragStart={(event) => handleScopeItemDragStart('structural', index, event)}
                                  onDragEnd={handleScopeItemDragEnd}
                                  style={{
                                    ...scopeDragHandleStyle,
                                    cursor: isDragSource ? 'grabbing' : 'grab',
                                  }}
                                >
                                  <GripVertical size={15} aria-hidden="true" />
                                </button>
                                <QuotationScopeItemInput
                                  style={inputStyle}
                                  value={item}
                                  onChange={(value) => handleScopeItemChange('structural', index, value)}
                                  options={structuralScopeItemOptions}
                                  label={`Structural scope row ${index + 1}`}
                                  placeholder="Detailed structural scope row"
                                />
                                <button
                                  type="button"
                                  aria-label={`Remove structural scope row ${index + 1}`}
                                  title="Remove row"
                                  onClick={() => handleRemoveScopeItem('structural', index)}
                                  style={scopeDeleteButtonStyle}
                                >
                                  <Trash2 size={14} aria-hidden="true" />
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                          <button
                            type="button"
                            onClick={() => handleAppendBlankScopeItem('structural')}
                            style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, width: 'fit-content' }}
                          >
                            Add blank row
                          </button>
                          <QuotationScopeItemInput
                            multiline
                            style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
                            value={structuralExtraScopeRow}
                            onChange={setStructuralExtraScopeRow}
                            options={structuralScopeItemOptions}
                            label="Extra structural scope row"
                            placeholder="Add an extra structural scope row"
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155' }}>
                              <input
                                type="checkbox"
                                checked={saveStructuralExtraScopeRow}
                                onChange={(e) => setSaveStructuralExtraScopeRow(e.target.checked)}
                              />
                              Save for future reuse
                            </label>
                            <button
                              type="button"
                              disabled={scopeDetailActionBusy === 'structural'}
                              onClick={() => { void handleAddScopeLibraryRow('structural'); }}
                              style={{ border: 'none', background: '#0f4c81', color: '#fff', borderRadius: 8, padding: '8px 12px', cursor: scopeDetailActionBusy === 'structural' ? 'not-allowed' : 'pointer', opacity: scopeDetailActionBusy === 'structural' ? 0.7 : 1, fontWeight: 600 }}
                            >
                              {scopeDetailActionBusy === 'structural' ? 'Saving...' : 'Add structural row'}
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div style={{ marginTop: 10, fontSize: 13, color: '#5f6b7a', lineHeight: 1.6 }}>
                        No structural detail group is currently available for the selected project scope and type.
                      </div>
                    )}
                  </div>

                  <div style={scopePanelStyle}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 700, color: '#102a43' }}>
                      <input
                        type="checkbox"
                        checked={createForm.includeCivilScope}
                        disabled={!isImportedDraft && toFeeNumber(createForm.civilFee) <= 0}
                        onChange={(e) => setCreate('includeCivilScope', e.target.checked && (isImportedDraft || toFeeNumber(createForm.civilFee) > 0))}
                      />
                      Detailed Civil Scope
                    </label>
                    {!createForm.includeCivilScope ? (
                      <div style={{ marginTop: 10, fontSize: 13, color: '#5f6b7a', lineHeight: 1.6 }}>
                        Civil detailed scope is turned off and will not be included in this quotation.
                      </div>
                    ) : civilScopeGroupOptions.length > 0 ? (
                      <>
                        <div style={{ marginTop: 10 }}>
                          <label style={labelStyle}>Civil Detail Group</label>
                          <select
                            style={inputStyle}
                            value={createForm.civilScopeGroupKey}
                            onChange={(e) => handleScopeGroupChange('civil', e.target.value)}
                          >
                            {isImportedDraft && !isEditingLoadedPendingQuotation && <option value="">Select a civil scope group</option>}
                            {civilScopeGroupOptions.map((group) => (
                              <option key={group.groupKey} value={group.groupKey}>
                                {group.groupLabel}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ marginTop: 10 }}>
                          <label style={labelStyle}>Civil Special Footnote</label>
                          <textarea
                            style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
                            value={createForm.civilScopeFootnote}
                            onChange={(e) => setCreate('civilScopeFootnote', e.target.value)}
                            placeholder="Optional civil footnote for this quotation"
                          />
                        </div>

                        <div style={scopeEditorListStyle}>
                          {createForm.civilScopeItems.map((item, index) => {
                            const isDragSource = scopeItemDrag?.discipline === 'civil' && scopeItemDrag.fromIndex === index;
                            const isDropTarget = scopeItemDrag?.discipline === 'civil' && scopeItemDrag.overIndex === index && !isDragSource;

                            return (
                              <div
                                key={`civil-scope-item-${index}`}
                                className="quotation-create-scope-row"
                                onDragOver={(event) => handleScopeItemDragOver('civil', index, event)}
                                onDrop={(event) => handleScopeItemDrop('civil', index, event)}
                                style={{
                                  ...scopeEditorRowStyle,
                                  opacity: isDragSource ? 0.58 : 1,
                                  borderRadius: 10,
                                  outline: isDropTarget ? '2px solid #34579e' : '2px solid transparent',
                                  outlineOffset: 2,
                                }}
                              >
                                <button
                                  type="button"
                                  draggable
                                  aria-label={`Drag civil scope row ${index + 1}`}
                                  title="Drag row"
                                  onDragStart={(event) => handleScopeItemDragStart('civil', index, event)}
                                  onDragEnd={handleScopeItemDragEnd}
                                  style={{
                                    ...scopeDragHandleStyle,
                                    cursor: isDragSource ? 'grabbing' : 'grab',
                                  }}
                                >
                                  <GripVertical size={15} aria-hidden="true" />
                                </button>
                                <QuotationScopeItemInput
                                  style={inputStyle}
                                  value={item}
                                  onChange={(value) => handleScopeItemChange('civil', index, value)}
                                  options={civilScopeItemOptions}
                                  label={`Civil scope row ${index + 1}`}
                                  placeholder="Detailed civil scope row"
                                />
                                <button
                                  type="button"
                                  aria-label={`Remove civil scope row ${index + 1}`}
                                  title="Remove row"
                                  onClick={() => handleRemoveScopeItem('civil', index)}
                                  style={scopeDeleteButtonStyle}
                                >
                                  <Trash2 size={14} aria-hidden="true" />
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                          <button
                            type="button"
                            onClick={() => handleAppendBlankScopeItem('civil')}
                            style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, width: 'fit-content' }}
                          >
                            Add blank row
                          </button>
                          <QuotationScopeItemInput
                            multiline
                            style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
                            value={civilExtraScopeRow}
                            onChange={setCivilExtraScopeRow}
                            options={civilScopeItemOptions}
                            label="Extra civil scope row"
                            placeholder="Add an extra civil scope row"
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155' }}>
                              <input
                                type="checkbox"
                                checked={saveCivilExtraScopeRow}
                                onChange={(e) => setSaveCivilExtraScopeRow(e.target.checked)}
                              />
                              Save for future reuse
                            </label>
                            <button
                              type="button"
                              disabled={scopeDetailActionBusy === 'civil'}
                              onClick={() => { void handleAddScopeLibraryRow('civil'); }}
                              style={{ border: 'none', background: '#0f4c81', color: '#fff', borderRadius: 8, padding: '8px 12px', cursor: scopeDetailActionBusy === 'civil' ? 'not-allowed' : 'pointer', opacity: scopeDetailActionBusy === 'civil' ? 0.7 : 1, fontWeight: 600 }}
                            >
                              {scopeDetailActionBusy === 'civil' ? 'Saving...' : 'Add civil row'}
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div style={{ marginTop: 10, fontSize: 13, color: '#5f6b7a', lineHeight: 1.6 }}>
                        No civil detail group is currently available for the selected project scope and type.
                      </div>
                    )}
                  </div>

                  {(groupedScopeSections.general.length > 0 || createForm.generalScopeItems.length > 0) ? (
                    <div style={scopePanelStyle}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 700, color: '#102a43' }}>
                        <input
                          type="checkbox"
                          checked={createForm.includeGeneralScope}
                          onChange={(e) => {
                            const included = e.target.checked;
                            setCreateForm(prev => ({
                              ...prev,
                              includeGeneralScope: included,
                              generalScopeItems: included && isOutlookDraft && !isEditingLoadedPendingQuotation
                                ? hydrateGeneralScopeDrafts(prev.generalScopeItems, groupedScopeSections.general)
                                : prev.generalScopeItems,
                            }));
                          }}
                        />
                        Additional Scope
                      </label>
                      {!createForm.includeGeneralScope ? (
                        <div style={{ marginTop: 10, fontSize: 13, color: '#5f6b7a', lineHeight: 1.6 }}>
                          Optional. Select the required items to include them. Fees are unchanged.
                        </div>
                      ) : null}
                        <>
                          <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700, color: '#34579e' }}>
                            No additional fee is applied automatically.
                          </div>
                          {(
                            createForm.generalScopeItems.length > 0
                              ? Array.from(new Map(
                                  createForm.generalScopeItems.map((item) => [
                                    item.sectionKey,
                                    {
                                      key: item.sectionKey,
                                      title: item.sectionTitle,
                                    },
                                  ])
                                ).values())
                              : groupedScopeSections.general.map((section) => ({
                                  key: section.key,
                                  title: section.title,
                                }))
                          ).map((section) => (
                            <div key={section.key} style={{ marginTop: 10 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#243b53' }}>{section.title}</div>
                              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                                {createForm.generalScopeItems
                                  .filter((item) => item.sectionKey === section.key)
                                  .map((item) => (
                                    <label
                                      key={item.key}
                                      style={{
                                        display: 'grid',
                                        gridTemplateColumns: '24px minmax(0, 1fr)',
                                        gap: 10,
                                        alignItems: 'center',
                                        minHeight: 44,
                                        fontSize: 13,
                                        color: '#243b53',
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        aria-label={`Include ${item.text}`}
                                        checked={createForm.includeGeneralScope && item.included}
                                        onChange={(e) => {
                                          const nextIncluded = e.target.checked;
                                          setCreateForm((prev) => ({
                                            ...prev,
                                            includeGeneralScope: nextIncluded || prev.includeGeneralScope,
                                            generalScopeItems: prev.generalScopeItems.map((draft) => (
                                              draft.key === item.key
                                                ? { ...draft, included: nextIncluded }
                                                : draft
                                            )),
                                          }));
                                        }}
                                      />
                                      <input
                                        style={inputStyle}
                                        aria-label={`Additional scope wording: ${item.text}`}
                                        value={item.text}
                                        onChange={(e) => {
                                          const nextValue = e.target.value;
                                          setCreateForm((prev) => ({
                                            ...prev,
                                            generalScopeItems: prev.generalScopeItems.map((draft) => (
                                              draft.key === item.key
                                                ? { ...draft, text: nextValue }
                                                : draft
                                            )),
                                          }));
                                        }}
                                      />
                                    </label>
                                  ))}
                              </div>
                            </div>
                          ))}
                        </>
                    </div>
                  ) : null}
                </div>
                {scopeDetailMessage ? (
                  <div style={{ marginTop: 10, fontSize: 12, color: '#5f6b7a' }}>{scopeDetailMessage}</div>
                ) : null}
                </div>
              </details>

              <details className="quotation-mobile-disclosure" open={isPhone ? undefined : true}>
                <summary className="quotation-mobile-disclosure__summary">
                  Exclusions and PDF notes
                </summary>
                <div className="quotation-create-section" style={{ ...sectionStyle, background: '#fafbfd' }}>
                <h3 className="quotation-create-section-title" style={sectionTitleStyle}>Quotation Exclusions</h3>
                <div className="quotation-create-section-caption" style={sectionCaptionStyle}>
                  Checked exclusions are included in the quotation PDF.
                </div>
                <div style={{ border: '1px solid #d6d6d6', borderRadius: 10, padding: 12, background: '#ffffff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                    <div style={{ fontSize: 12, color: '#5f6b7a' }}>
                      Use these defaults and add one-off exclusions when the project needs extra wording.
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={selectAllExclusions}
                        style={{ border: '1px solid #cfd8dc', background: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={clearAllExclusions}
                        style={{ border: '1px solid #cfd8dc', background: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        Clear all
                      </button>
                    </div>
                  </div>
                  <QuotationExclusionEditor
                    options={quotationExclusionOptions}
                    selected={createForm.selectedExclusions}
                    order={exclusionOrder}
                    onOrder={setExclusionOrder}
                    onToggle={toggleExclusionSelection}
                    canDelete={canDeleteQuotationExclusionOption}
                    onDelete={(option) => { void handleDeleteQuotationExclusionOption(option); }}
                    deletingLabel={exclusionDeleteBusyLabel}
                  />

                  <div style={{ marginTop: 12, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                    <label style={{ ...labelStyle, marginBottom: 8 }}>Add ad-hoc exclusion</label>
                    <textarea
                      style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
                      value={adHocExclusion}
                      onChange={(e) => setAdHocExclusion(e.target.value)}
                      placeholder="Type a one-off exclusion line here"
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155' }}>
                        <input
                          type="checkbox"
                          checked={saveAdHocExclusion}
                          onChange={(e) => setSaveAdHocExclusion(e.target.checked)}
                        />
                        Save for future reuse
                      </label>
                      <button
                        type="button"
                        disabled={exclusionActionBusy}
                        onClick={() => { void handleAddAdHocExclusion(); }}
                        style={{
                          border: 'none',
                          background: '#0f4c81',
                          color: '#fff',
                          borderRadius: 8,
                          padding: '8px 12px',
                          cursor: exclusionActionBusy ? 'not-allowed' : 'pointer',
                          opacity: exclusionActionBusy ? 0.7 : 1,
                          fontWeight: 600,
                        }}
                      >
                        {exclusionActionBusy ? 'Saving...' : 'Add exclusion'}
                      </button>
                    </div>
                    {quotationExclusionMessage ? (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#5f6b7a' }}>{quotationExclusionMessage}</div>
                    ) : null}
                  </div>
                </div>
              </div>
              </details>
            </div>

            <div id="quotation-create-issue" className="quotation-create-actions" style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {loadedPendingQuotationDraft?.quotationId ? (
                <div style={{
                  width: '100%',
                  fontSize: 12,
                  color: '#5f6b7a',
                  lineHeight: 1.5,
                }}>
                  Quotation {loadedPendingQuotationDraft.quotationNumber || loadedPendingQuotationDraft.quotationId} is loaded in edit mode. Saving here updates that existing row instead of creating a new quotation number.
                </div>
              ) : null}
              <button
                type="submit"
                disabled={busy !== null}
                style={{
                  border: 'none',
                  background: '#0f4c81',
                  color: '#fff',
                  borderRadius: 10,
                  padding: '10px 14px',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.7 : 1,
                  fontWeight: 600
                }}
              >
                {busy === 'create'
                  ? (loadedPendingQuotationDraft?.quotationId ? 'Updating...' : 'Creating...')
                  : (loadedPendingQuotationDraft?.quotationId ? 'Update Quotation' : 'Create Quotation Row')}
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => { void handlePreviewQuotation(); }}
                style={{
                  border: '1px solid #cfd8dc',
                  background: '#fff',
                  color: '#1f2937',
                  borderRadius: 10,
                  padding: '10px 14px',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.7 : 1,
                  fontWeight: 600
                }}
              >
                {busy === 'preview' ? 'Opening preview...' : 'Preview Quotation PDF'}
              </button>
              <button
                type="button"
                disabled={busy !== null || quotationFileApproval?.status !== 'Approved'}
                onClick={() => { void handleFileQuotationPdf(); }}
                style={{
                  border: '1px solid #9ac2d6',
                  background: '#eef8fb',
                  color: '#155e75',
                  borderRadius: 10,
                  padding: '10px 14px',
                  cursor: busy || quotationFileApproval?.status !== 'Approved' ? 'not-allowed' : 'pointer',
                  opacity: busy || quotationFileApproval?.status !== 'Approved' ? 0.7 : 1,
                  fontWeight: 600
                }}
              >
                {busy === 'filePdf' ? 'Signing and filing quotation PDF...' : 'Sign and File Quotation PDF'}
              </button>
              <button
                type="button"
                disabled={busy !== null || !canSendQuotationEmail}
                onClick={() => { void handlePreviewQuotationEmail(); }}
                style={{
                  border: '1px solid #b9d7c0',
                  background: '#f1fbf4',
                  color: '#166534',
                  borderRadius: 10,
                  padding: '10px 14px',
                  cursor: busy || !canSendQuotationEmail ? 'not-allowed' : 'pointer',
                  opacity: busy || !canSendQuotationEmail ? 0.7 : 1,
                  fontWeight: 600
                }}
                title={!matchesSavedQuotationNumber
                  ? 'Save or update the quotation row first so the email and public signing page stay aligned.'
                  : !currentWorkflowQuotationId
                  ? 'Create or update the quotation row before emailing it.'
                  : !createForm.clientEmail.trim()
                    ? 'Add the client email before sending the quotation.'
                    : 'Preview the quotation email and send the stored signed PDF after filing.'}
              >
                {busy === 'emailPreview' ? 'Loading email preview...' : 'Email Filed Quotation PDF'}
              </button>
              {renderQuotationFileApprovalPanel()}
              {activeOutputSigningLink ? (
                <div style={{ width: '100%', fontSize: 12, color: '#5f6b7a', lineHeight: 1.5 }}>
                  Reusing the current public signing link for preview, PDF output, and the quotation email until it expires on{' '}
                  {new Date(activeOutputSigningLink.expiresAt).toLocaleString('en-AU')}.
                </div>
              ) : null}
              <QuotationCcInput
                value={quotationEmailCcText}
                onChange={setQuotationEmailCcText}
                inputStyle={inputStyle}
                labelStyle={labelStyle}
              />
              <div style={{ width: '100%' }}>
                {renderQuotationEmailPreviewComposer()}
              </div>
            </div>
          </form>
        </div>

        <div id="quotation-accept" style={panelStyle}>
          <h2 style={{ marginTop: 0, marginBottom: 12, fontSize: 18 }}>2. Accept Quotation -&gt; Create Project + Tasks</h2>
          <form onSubmit={handleAcceptQuotation}>
            <div style={responsiveAcceptWorkflowGridStyle}>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={gridStyle}>
                  <div>
                    <label style={labelStyle}>Quotation ID / Number *</label>
                    <input
                      list="pending-quotation-options"
                      style={inputStyle}
                      required
                      inputMode="numeric"
                      value={acceptForm.quotationLookup}
                      onChange={(e) => handleQuotationLookupChange(e.target.value)}
                      placeholder="Type pending quotation number, e.g. 26009"
                    />
                    <datalist id="pending-quotation-options">
                      {pendingQuotationSuggestions.map((row) => {
                        const optionValue = String(row.QuotationNumber || row.QuotationId || '').trim();
                        if (!optionValue) return null;
                        return (
                          <option key={`${row.QuotationId ?? optionValue}-${optionValue}`} value={optionValue}>
                            {buildPendingQuotationOptionLabel(row)}
                          </option>
                        );
                      })}
                    </datalist>
                    {(quotationLookupBusy || quotationLookupMessage || acceptForm.quotationId) ? (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#5f6b7a' }}>
                        {quotationLookupBusy
                          ? 'Searching pending quotations...'
                          : acceptForm.quotationId
                            ? `${quotationLookupMessage}${quotationLookupMessage ? ' ' : ''}Matched row ID ${acceptForm.quotationId}.`
                            : quotationLookupMessage}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label style={labelStyle}>Project Number (optional)</label>
                    <input style={inputStyle} value={acceptForm.projectNumber} onChange={(e) => setAccept('projectNumber', e.target.value)} placeholder="Auto-derived if empty" />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Project Title (optional)</label>
                  <input style={inputStyle} value={acceptForm.projectTitle} onChange={(e) => setAccept('projectTitle', e.target.value)} placeholder="Auto-generated from quotation if empty" />
                </div>
                <div>
                  <label style={labelStyle}>Project Engineers</label>
                  <div style={{ marginBottom: 8, fontSize: 12, color: '#5f6b7a', lineHeight: 1.5 }}>
                    Pick the engineers who should receive the structural and civil kickoff tasks created during quotation acceptance.
                  </div>
                  <div style={{ marginBottom: 10, fontSize: 12, color: '#486581', lineHeight: 1.55 }}>
                    Acceptance creates the project row immediately and can provision the project folders right away.
                    Selected project engineers get the structural and civil kickoff tasks when those quoted fees are above zero.
                    If multiple engineers are selected, the kickoff tasks are assigned in order across the selected list.
                  </div>
                  <div style={{
                    border: '1px solid #d9e2ec',
                    borderRadius: 10,
                    background: '#fff',
                    padding: 12,
                  }}>
                    {engineerOptionsBusy ? (
                      <div style={{ fontSize: 13, color: '#52606d' }}>Loading available company engineers...</div>
                    ) : availableEngineerOptions.length > 0 ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10 }}>
                        {availableEngineerOptions.map((engineer) => {
                          const checked = acceptForm.assignedEngineerEmails.includes(engineer.email);
                          const shortLabel = getWorkflowEngineerShortLabel(engineer);
                          return (
                            <label
                              key={engineer.email}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                border: '1px solid #e2e8f0',
                                borderRadius: 10,
                                padding: '10px 12px',
                                background: checked ? '#f0f9ff' : '#fff',
                                cursor: 'pointer',
                                minHeight: 52,
                              }}
                              title={engineer.displayName || engineer.email}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleAssignedEngineer(engineer.email)}
                                style={{ marginTop: 0 }}
                              />
                              <div style={{ minWidth: 0, fontSize: 16, fontWeight: 800, color: '#102a43', letterSpacing: 0.4 }}>
                                {shortLabel}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: '#52606d' }}>
                        {engineerOptionsMessage || 'No available company engineers found.'}
                      </div>
                    )}
                  </div>
                  {acceptForm.assignedEngineerEmails.length > 0 ? (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#5f6b7a' }}>
                      Selected: {acceptForm.assignedEngineerEmails.map((email) => (
                        getWorkflowEngineerShortLabel(
                          availableEngineerOptions.find((option) => option.email === email) || { email, displayName: email }
                        )
                      )).join(', ')}
                      <div>DBYD account and daily allowance: {acceptForm.assignedEngineerEmails[0]}</div>
                    </div>
                  ) : null}
                </div>
                <div>
                  <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={acceptForm.createSharePointFolders}
                      onChange={(e) => setAccept('createSharePointFolders', e.target.checked)}
                    />
                    Create SharePoint folders after acceptance
                  </label>
                  <div style={{ marginTop: 6, fontSize: 12, color: '#5f6b7a', lineHeight: 1.5 }}>
                    Leave this on to provision the project folders after the quotation is accepted. If the Graph app or tenant settings are not configured, the quotation can still be accepted but the folder step will return a warning.
                  </div>
                </div>
              </div>

              <div style={{
                border: '1px solid #d9e2ec',
                borderRadius: 14,
                background: '#f8fbfd',
                padding: 14,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#486581', textTransform: 'uppercase' }}>
                  Pending Quotation Check
                </div>
                <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#102a43' }}>
                  {selectedPendingQuotation
                    ? `Quotation ${selectedPendingQuotation.QuotationNumber || selectedPendingQuotation.QuotationId}`
                    : 'Select a pending quotation'}
                </div>
                <div style={{ marginTop: 6, fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                  {selectedPendingQuotation
                    ? `${selectedPendingQuotation.Client || 'Client not set'}${selectedPendingQuotation.ClientCompany ? ` (${selectedPendingQuotation.ClientCompany})` : ''}`
                    : 'As you type a pending quotation number, the workflow will suggest matches and load the quotation summary here.'}
                </div>
                {selectedPendingQuotation ? (
                  <button
                    type="button"
                    onClick={handleEditSelectedPendingQuotation}
                    disabled={busy !== null}
                    style={{
                      marginTop: 12,
                      border: '1px solid #9ac2d6',
                      background: '#eef8fb',
                      color: '#155e75',
                      borderRadius: 10,
                      padding: '9px 12px',
                      cursor: busy !== null ? 'not-allowed' : 'pointer',
                      opacity: busy !== null ? 0.7 : 1,
                      fontWeight: 700,
                    }}
                  >
                    Edit Quotation Row
                  </button>
                ) : null}

                <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 130px) 1fr', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581' }}>Street</div>
                    <div style={{ fontSize: 13, color: '#102a43' }}>{selectedPendingQuotation?.Address || 'Pending quotation selection'}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 130px) 1fr', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581' }}>Suburb</div>
                    <div style={{ fontSize: 13, color: '#102a43' }}>{selectedPendingQuotation?.Suburb || '-'}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 130px) 1fr', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581' }}>Postcode</div>
                    <div style={{ fontSize: 13, color: '#102a43' }}>{normalizePostCode(selectedPendingQuotation?.PostCode) || '-'}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 130px) 1fr', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581' }}>Structural Fee</div>
                    <div style={{ fontSize: 13, color: '#102a43' }}>{formatCurrencyOrDash(selectedPendingQuotation ? getQuotationStructuralFeeTotal(selectedPendingQuotation) : null)}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 130px) 1fr', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581' }}>Civil Fee</div>
                    <div style={{ fontSize: 13, color: '#102a43' }}>{formatCurrencyOrDash(selectedPendingQuotation?.CivilFee)}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 130px) 1fr', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581' }}>Soil Report Fee</div>
                    <div style={{ fontSize: 13, color: '#102a43' }}>{formatCurrencyOrDash(selectedPendingQuotation?.SoilReportFee)}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 130px) 1fr', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581' }}>Project Type</div>
                    <div style={{ fontSize: 13, color: '#102a43' }}>{selectedPendingQuotation?.ProjectType || '-'}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 130px) 1fr', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581' }}>Project Scope</div>
                    <div style={{ fontSize: 13, color: '#102a43', whiteSpace: 'pre-wrap' }}>{selectedPendingQuotation?.ProjectScope || '-'}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 130px) 1fr', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581' }}>Project Description</div>
                    <div style={{ fontSize: 13, color: '#102a43', whiteSpace: 'pre-wrap' }}>{selectedPendingQuotation?.ProjectDescription || '-'}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 130px) 1fr', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581' }}>Confirm Address</div>
                    <div style={{ fontSize: 13, color: '#102a43' }}>{buildPendingQuotationAddress(selectedPendingQuotation) || '-'}</div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="submit"
                disabled={busy !== null || !acceptForm.quotationId}
                style={{
                  border: 'none',
                  background: '#0f4c81',
                  color: '#fff',
                  borderRadius: 10,
                  padding: '10px 14px',
                  cursor: busy !== null || !acceptForm.quotationId ? 'not-allowed' : 'pointer',
                  opacity: busy !== null || !acceptForm.quotationId ? 0.7 : 1,
                  fontWeight: 600
                }}
              >
                {busy === 'acceptReview' ? 'Opening review...' : 'Review PDF Before Accepting'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/projects')}
                style={{
                  border: '1px solid #cfd8dc',
                  background: '#fff',
                  color: '#1f2937',
                  borderRadius: 10,
                  padding: '10px 14px',
                  cursor: 'pointer'
                }}
              >
                Go to Projects
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: '#5f6b7a', lineHeight: 1.5 }}>
              Acceptance is completed from the PDF review window so the quotation can be checked before the project and task records are created.
            </div>
          </form>
        </div>

        {acceptReviewOpen && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.56)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 1000,
          }}>
            <div style={{
              width: 'min(1160px, 100%)',
              height: 'min(97vh, 1120px)',
              background: '#ffffff',
              borderRadius: 18,
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.28)',
              display: 'grid',
              gridTemplateRows: '1fr auto',
              overflow: 'hidden',
            }}>
              <div style={{ background: '#f8fafc', padding: 12, minHeight: 0 }}>
                {busy === 'acceptReview' || !acceptReviewPdfUrl ? (
                  <div style={{
                    height: '100%',
                    borderRadius: 14,
                    border: '1px dashed #cbd5e1',
                    background: '#ffffff',
                    display: 'grid',
                    placeItems: 'center',
                    padding: 24,
                    textAlign: 'center',
                    color: '#52606d',
                    lineHeight: 1.6,
                  }}>
                    Preparing the quotation PDF review...
                  </div>
                ) : (
                  <iframe
                    title="Quotation PDF review"
                    src={acceptReviewPdfUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: '1px solid #d9e2ec',
                      borderRadius: 14,
                      background: '#ffffff',
                    }}
                  />
                )}
              </div>

              <div style={{
                padding: '16px 18px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: 12,
                flexWrap: 'wrap',
              }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.35, color: '#486581', textTransform: 'uppercase' }}>
                    Required Review
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#102a43' }}>
                    Quotation PDF Review
                  </div>
                  <div style={{ fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                    Review quotation {toTrimmedString(acceptReviewQuotation?.QuotationNumber || acceptReviewQuotation?.QuotationId) || acceptForm.quotationLookup || '-'} here, then accept it from this footer when ready.
                  </div>
                  <div style={{ fontSize: 12, color: '#5f6b7a', lineHeight: 1.5 }}>
                    If the row needs changes, edit it before accepting so the saved quotation row and reviewed PDF stay aligned.
                  </div>
                  {busy === 'accept' ? (
                    <div style={{
                      marginTop: 6,
                      borderRadius: 10,
                      border: '1px solid #bfdbfe',
                      background: '#eff6ff',
                      padding: '10px 12px',
                      color: '#1d4ed8',
                      fontSize: 12,
                      lineHeight: 1.55,
                    }}>
                      Submitting quotation acceptance now. This can take a moment while the project and folder actions complete.
                    </div>
                  ) : null}
                  {acceptReviewNotice ? (
                    <div style={{
                      marginTop: 6,
                      borderRadius: 10,
                      border: '1px solid #fecaca',
                      background: '#fef2f2',
                      padding: '10px 12px',
                      color: '#991b1b',
                      fontSize: 12,
                      lineHeight: 1.6,
                    }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>
                        Acceptance failed
                      </div>
                      <div>{acceptReviewNotice.message}</div>
                      {acceptReviewNotice.code ? (
                        <div style={{ marginTop: 4 }}>
                          Error code: <code>{acceptReviewNotice.code}</code>
                        </div>
                      ) : null}
                      {acceptReviewNotice.correlationId ? (
                        <div style={{ marginTop: 4 }}>
                          Correlation ID: <code>{acceptReviewNotice.correlationId}</code>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {acceptReviewPdfUrl ? (
                    <a
                      href={acceptReviewPdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        border: '1px solid #cbd5e1',
                        background: '#fff',
                        color: '#1f2937',
                        borderRadius: 10,
                        padding: '10px 12px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                    >
                      Open In New Tab
                    </a>
                  ) : null}
                  <button
                    type="button"
                    onClick={closeAcceptReview}
                    disabled={busy === 'accept'}
                    style={{
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      color: '#1f2937',
                      borderRadius: 10,
                      padding: '10px 12px',
                      cursor: busy === 'accept' ? 'not-allowed' : 'pointer',
                      opacity: busy === 'accept' ? 0.7 : 1,
                      fontWeight: 600,
                    }}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handleReturnReviewedQuotationToWorkflow}
                    disabled={busy === 'accept'}
                    style={{
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      color: '#1f2937',
                      borderRadius: 10,
                      padding: '10px 12px',
                      cursor: busy === 'accept' ? 'not-allowed' : 'pointer',
                      opacity: busy === 'accept' ? 0.7 : 1,
                      fontWeight: 600,
                    }}
                  >
                    Edit Quotation Row
                  </button>
                  <button
                    type="button"
                    disabled={busy === 'accept' || busy === 'acceptReview' || !acceptForm.quotationId}
                    onClick={() => { void submitAcceptedQuotation(); }}
                    style={{
                      border: 'none',
                      background: '#1f7a4d',
                      color: '#fff',
                      borderRadius: 10,
                      padding: '10px 14px',
                      cursor: busy === 'accept' || busy === 'acceptReview' || !acceptForm.quotationId ? 'not-allowed' : 'pointer',
                      opacity: busy === 'accept' || busy === 'acceptReview' || !acceptForm.quotationId ? 0.7 : 1,
                      fontWeight: 700,
                    }}
                  >
                    {busy === 'accept' ? 'Accepting...' : 'Accept Quotation'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div style={{ ...panelStyle, background: '#f7fbff', border: '1px solid #d6e8f7' }}>
            <h3 style={{ marginTop: 0 }}>Workflow Result</h3>
            {isQuotationEmailResult ? (
              <>
                {resultStatusMessage ? (
                  <div style={{
                    marginBottom: 12,
                    borderRadius: 10,
                    border: '1px solid #b7e4c7',
                    background: '#f3fbf6',
                    padding: 12,
                    color: '#166534',
                    lineHeight: 1.55,
                    fontWeight: 700,
                  }}>
                    {resultStatusMessage}
                  </div>
                ) : null}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 12,
                }}>
                  <div style={{ border: '1px solid #d9e2ec', borderRadius: 10, background: '#fff', padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581', textTransform: 'uppercase', letterSpacing: 0.35 }}>
                      Quotation
                    </div>
                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#102a43' }}>
                      {resultQuotationNumber || createForm.quotationNumber || 'Quotation'}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                      {toTrimmedString(resultEmail?.fileName) || 'Quotation PDF attached to the email.'}
                    </div>
                  </div>
                  <div style={{ border: '1px solid #d9e2ec', borderRadius: 10, background: '#fff', padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581', textTransform: 'uppercase', letterSpacing: 0.35 }}>
                      Recipients
                    </div>
                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#102a43' }}>
                      {resultEmailRecipients.length || 0}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                      {resultEmailRecipients.length > 0 ? resultEmailRecipients.join(', ') : 'No recipients were returned.'}
                    </div>
                  </div>
                  <div style={{ border: '1px solid #d9e2ec', borderRadius: 10, background: '#fff', padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581', textTransform: 'uppercase', letterSpacing: 0.35 }}>
                      Signing Link
                    </div>
                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#102a43' }}>
                      {resultEmailSignUrl ? 'Included' : 'Not included'}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                      {resultEmailSignUrl || 'The email was sent without an online signing link.'}
                    </div>
                  </div>
                </div>
                {resultEmailCcRecipients.length > 0 ? (
                  <div style={{ marginTop: 12, fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                    <strong>CC:</strong> {resultEmailCcRecipients.join(', ')}
                  </div>
                ) : null}
              </>
            ) : isQuotationPdfFiledResult ? (
              <>
                {resultStatusMessage ? (
                  <div style={{
                    marginBottom: 12,
                    borderRadius: 10,
                    border: '1px solid #b7e4c7',
                    background: '#f3fbf6',
                    padding: 12,
                    color: '#166534',
                    lineHeight: 1.55,
                    fontWeight: 700,
                  }}>
                    {resultStatusMessage}
                  </div>
                ) : null}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 12,
                }}>
                  <div style={{ border: '1px solid #d9e2ec', borderRadius: 10, background: '#fff', padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581', textTransform: 'uppercase', letterSpacing: 0.35 }}>
                      Quotation
                    </div>
                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#102a43' }}>
                      {resultQuotationNumber || createForm.quotationNumber || 'Quotation'}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                      {toTrimmedString(resultSharePointUpload?.fileName || resultDraftBlob?.fileName) || 'Quotation PDF filed.'}
                    </div>
                  </div>
                  <div style={{ border: '1px solid #d9e2ec', borderRadius: 10, background: '#fff', padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581', textTransform: 'uppercase', letterSpacing: 0.35 }}>
                      Draft Blob
                    </div>
                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#102a43' }}>
                      {resultDraftBlob?.fileId ? 'Stored' : 'Pending'}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                      {resultDraftBlob?.fileName || resultDraftBlob?.contentPath || 'No blob-backed quotation PDF returned.'}
                    </div>
                  </div>
                  <div style={{ border: '1px solid #d9e2ec', borderRadius: 10, background: '#fff', padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581', textTransform: 'uppercase', letterSpacing: 0.35 }}>
                      Quotation Folder
                    </div>
                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#102a43' }}>
                      {resultSharePointUpload?.integrationStatus === 'configured' ? 'Filed' : 'Needs attention'}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                      {toTrimmedString(resultSharePointUpload?.plannedPath) || toTrimmedString(resultQuotationFolder?.quotationProjectRoot) || 'Quotation folder action completed.'}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {resultSharePointPlan?.integrationStatus === 'error' ? (
                  <div style={{
                    marginBottom: 12,
                    borderRadius: 10,
                    border: '1px solid #f5c26b',
                    background: '#fff8eb',
                    padding: 12,
                    color: '#7c4a03',
                    lineHeight: 1.55,
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>
                      Quotation accepted, but SharePoint folder provisioning failed.
                    </div>
                    <div>
                      The project record was created, but the folder step returned an error.
                      {resultSharePointPlan?.message ? ` ${resultSharePointPlan.message}` : ''}
                    </div>
                    {sharePointUnauthorizedClientError ? (
                      <div style={{ marginTop: 6 }}>
                        Azure AD returned `AADSTS700016 unauthorized_client`, which usually means the configured Graph application ID is not registered in the `Linfinity Engineering Pty Ltd` tenant, has not been consented there, or the workflow is pointing at the wrong tenant.
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 12,
                  marginBottom: 14,
                }}>
                  <div style={{ border: '1px solid #d9e2ec', borderRadius: 10, background: '#fff', padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581', textTransform: 'uppercase', letterSpacing: 0.35 }}>
                      Project
                    </div>
                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#102a43' }}>
                      {resultProjectNumber || 'Created'}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                      {resultProjectTitle || 'Project row created from the accepted quotation.'}
                    </div>
                  </div>
                  <div style={{ border: '1px solid #d9e2ec', borderRadius: 10, background: '#fff', padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581', textTransform: 'uppercase', letterSpacing: 0.35 }}>
                      SharePoint Folder
                    </div>
                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#102a43' }}>
                      {resultSharePointPlan?.skipped
                        ? 'Skipped'
                        : resultSharePointPlan?.integrationStatus === 'queued'
                          ? 'In progress'
                        : resultSharePointPlan?.integrationStatus === 'error'
                          ? 'Needs attention'
                          : resultSharePointPlan?.projectFolder
                            ? 'Ready'
                            : 'Planned'}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                      {resultSharePointPlan?.message || 'Project folder action completed.'}
                    </div>
                  </div>
                  <div style={{ border: '1px solid #d9e2ec', borderRadius: 10, background: '#fff', padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#486581', textTransform: 'uppercase', letterSpacing: 0.35 }}>
                      Kickoff Tasks
                    </div>
                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#102a43' }}>
                      {resultCreatedTaskIds.length}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: '#52606d', lineHeight: 1.5 }}>
                      {resultCreatedTaskIds.length > 0
                        ? 'Only structural and civil kickoff tasks were created.'
                        : 'No kickoff tasks were needed for this quotation.'}
                    </div>
                  </div>
                </div>
              </>
            )}
            {!isQuotationPdfFiledResult && structuralInvoicePrefill ? (
              <div style={{
                marginBottom: 14,
                borderRadius: 10,
                border: '1px solid #c7eed8',
                background: '#f3fbf6',
                padding: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>
                    Structural invoice action ready
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, color: '#365314', lineHeight: 1.5 }}>
                    Open the invoice workflow with invoice number {structuralInvoicePrefill.prefill.invoiceNumber} and the first structural stage prefilled from this quotation.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCreateStructuralInvoice}
                  style={{
                    border: 'none',
                    background: '#166534',
                    color: '#fff',
                    borderRadius: 10,
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  Create Structure Invoice 1
                </button>
              </div>
            ) : null}
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#102a43' }}>
                Raw workflow response
              </summary>
              <pre style={{
                margin: '12px 0 0 0',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 12,
                background: '#fff',
                border: '1px solid #e6edf3',
                borderRadius: 8,
                padding: 12,
                overflowX: 'auto'
              }}>
                {JSON.stringify(result, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

function QuotationWorkflowEntry() {
  const { user } = useAuth();
  const location = useLocation();
  const state = (location.state || {}) as QuotationWorkflowPrefillState;
  const ownerIdentity = outlookQuotationOwnerIdentity(user);
  if (state.outlookQuotationDraft && !state.amendQuotation && state.outlookQuotationDraft.ownerIdentity !== ownerIdentity) {
    return <div className="wdgf-notice wdgf-notice--danger" role="alert" style={{ margin: 24 }}>This Outlook draft belongs to a different sign-in session. Reopen it from Outlook using the original account. No quotation was created.</div>;
  }
  if (state.workflowQuotationDraft && !state.amendQuotation && state.workflowQuotationDraft.ownerIdentity !== ownerIdentity) {
    return <div className="wdgf-notice wdgf-notice--danger" role="alert" style={{ margin: 24 }}>This quotation draft belongs to a different sign-in session. Open the saved draft using its original account. No quotation was created.</div>;
  }
  return <QuotationWorkflowPage key={state.outlookQuotationDraft || state.workflowQuotationDraft ? `${ownerIdentity}:${location.key}` : 'quotation-workflow'} />;
}

export default QuotationWorkflowEntry;
